// ── RECEIPTS TAB ──────────────────────────────────────────────────────────────
// Split-pane layout:
//   Left:  reader (always-visible drop zone + kind pre-classify + primer)
//   Right: inbox (flat list with status filter chips and per-row status pill)
//
// Drag-and-drop works across the whole tab — drop overlay covers the screen
// when files are dragged. Multi-file uploads process sequentially.
//
// Branding: AI calls are surfaced as "Gilda" in the UI; the underlying API
// is still Claude (api/extract-receipt.js).

import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useAppContext } from './AppContext';
// BillToClientModal removed 2026-05-18 — receipts now attach to billing
// summary drafts directly via the picker on Client Invoices → Billing
// Summary. The intermediate reimbursable-invoice layer was deprecated.
import VendorPicker from './VendorPicker';
import Fuse from 'fuse.js';
import { supabase } from '../lib/supabase';
import { useDetailJump } from '../lib/jump';
import { Lbl, IS, CurrencyInput } from './ui';
import { eligibleCardsForReceipt, linkablePosForReceipt, fmtMoney, hasAdminAccess, displayNameFor, resolveVendorByName, poSubtotalLive, computePoPaidBefore, detectKindFromContext, kindIsDestructive, computeFeeSumForCategory, distributeFeeDeltaAcrossItems, buildPoAdjustmentEntry, receiptDeletionBlocked, findPotentialDuplicateVendorPayment, computeFeeSharePatchesForReceipt, FEE_CATEGORIES_ALL, FEE_CATEGORIES_PHASE_5C, VENDOR_RECEIPT_KINDS, receiptAttachmentsFor, RECEIPT_ATTACHMENT_ROLES, allocateVendorPaymentToItems, receiptMatchesSearch, STATUSES, canonicalItemStatus, applyReimbursableMarkup, receiptMarkupPct } from '../utils';
import { dualWriteVendorRow } from '../lib/vendorDualWrite';
import * as copy from '../lib/receiptCopy';
import {
  INK_1, INK_2, INK_3, INK_4,
  SURFACE, GROUND_OFF,
  LINE_ROW, LINE_CONTAINER, LINE_SHELL,
  ZONE_YELLOW, ZONE_FUCHSIA, ZONE_GREEN, ZONE_GREY,
  ALERT_RED_TEXT, ALERT_RED_BORDER,
  ALERT_AMBER_TEXT, ALERT_AMBER_BORDER,
  ALERT_INFO_BG,
  HAIRLINE, HAIRLINE_ROW, HAIRLINE_SHELL,
  HAIRLINE_FUCHSIA, HAIRLINE_RED, HAIRLINE_AMBER,
  HAIRLINE_1PX, HAIRLINE_1PX_SHELL,
  HAIRLINE_THICK_INK, HAIRLINE_THICK_FUCHSIA,
  DASHED_DROP, ACCENT_LEFT,
  SHADOW_MODAL, SCRIM, DRAG_OVERLAY_BG,
  TABULAR,
  TYPE_EYEBROW,
} from '../lib/designTokens';

// ── CONSTANTS ─────────────────────────────────────────────────────────────────

// Plain-language pass 2026-08-23 (Cimon: "be really really clear about
// which receipt types they are"). Row eyebrows say what the money DID
// in normal words; DB enum values never change.
var KIND_LABEL = {
  vendor_deposit:       'Vendor — deposit paid',
  vendor_final:         'Vendor — final payment',
  vendor_full_payment:  'Vendor — paid in full',
  vendor_fee:           'Vendor — shipping / fee bill',
  project_reimbursable: 'Client expense (billed +15%)',
  personal:             'Office — firm cost',  // DB enum value is 'personal'
  merchandise_direct:   'Direct purchase — bills via FIN',
  sales_tax:            'Sales tax — paid to the state',   // mirrored from a filing (2026-10-16)
  unknown:              'Needs a type',
};

// Pre-classify segmented buttons. `isProject: true` → fuchsia active state
// (project-related: links to a PO or to the project for billing).
// Auto = no hint sent. Office covers firm-internal / non-billable spend
// (office supplies, software, generic overhead). DB enum value is still
// 'personal' for backward compat; we render it as 'Office' in the UI.

// Status mapped to the firm's three-zone palette (yellow / fuchsia / green / grey)
// All values are token references — see src/lib/designTokens.js. Needs_link
// + push_failed text colors deliberately use ALERT_RED_TEXT for the
// destructive-state cue; the fuchsia dot signals the zone.
var STATUS_STYLE = {
  pending:      { dot: ZONE_YELLOW,  text: INK_2,           label: 'Pending' },
  needs_link:   { dot: ZONE_FUCHSIA, text: ALERT_RED_TEXT,  label: 'Needs Link' },
  approved:     { dot: ZONE_GREEN,   text: INK_2,           label: 'Approved' },
  pushed:       { dot: ZONE_GREEN,   text: INK_2,           label: 'Approved' },   // legacy value from the retired QB sync
  push_failed:  { dot: ZONE_GREEN,   text: INK_2,           label: 'Approved' },   // legacy value from the retired QB sync
  rejected:     { dot: ZONE_GREY,    text: INK_3,           label: 'Rejected' },
};

// Filter-chip groups for the inbox status filter. Each filter maps to ONE
// underlying status. Legacy 'pushed' / 'push_failed' rows (from the
// retired QuickBooks sync) read as approved.
var STATUS_FILTERS = [
  { value: 'all',         label: 'All',         match: function(r) { return true; } },
  { value: 'pending',     label: 'Pending',     match: function(r) { return r.status === 'pending'; } },
  { value: 'needs_link',  label: 'Needs Link',  match: function(r) { return r.status === 'needs_link'; } },
  { value: 'approved',    label: 'Approved',    match: function(r) { return r.status === 'approved' || r.status === 'pushed' || r.status === 'push_failed'; } },
  { value: 'rejected',    label: 'Rejected',    match: function(r) { return r.status === 'rejected'; } },
];

// Type-filter chips. Same shape as STATUS_FILTERS so the render loop
// can stay uniform, but the state is a Set (multi-select). Each chip
// toggles independently; zero active = all types shown, one or more
// active = union of selected. Type filter intersects with status
// filter — "Approved" + "Reimbursable" = approved reimbursable rows
// only. Labels match the row 2 type vocabulary ("Vendor", not
// "Procurement").
export var TYPE_FILTERS = [
  { value: 'vendor',       label: 'Vendor',       match: function(r) { return kindCategory(r.kind) === 'vendor'; } },
  { value: 'reimbursable', label: 'Reimbursable', match: function(r) { return kindCategory(r.kind) === 'reimbursable'; } },
  { value: 'merchandise',  label: 'Direct buys',  match: function(r) { return kindCategory(r.kind) === 'merchandise'; } },
  { value: 'office',       label: 'Office',       match: function(r) { return kindCategory(r.kind) === 'office'; } },
  { value: 'tax',          label: 'Sales tax',    match: function(r) { return kindCategory(r.kind) === 'tax'; } },
];

// Staff-facing kind text (2026-10-05, Cimon): the +15% expense markup is
// not for regular employees to see — only the merchandise markup on items
// is. Approvers (owner + admin) keep the full label. Display only; the
// kind value, the billing math and the picker flow are unchanged.
var KIND_LABEL_STAFF = Object.assign({}, KIND_LABEL, { project_reimbursable: 'Client expense' });
export function kindLabelFor(kind, canSeeMarkup) {
  var map = canSeeMarkup ? KIND_LABEL : KIND_LABEL_STAFF;
  return map[kind] || kind;
}
function kindOptionLabel(o, canSeeMarkup) {
  if (!canSeeMarkup && o.value === 'project_reimbursable') return 'Project expense — bill the client';
  return o.label;
}

var KIND_OPTIONS_FULL = [
  { value: 'vendor_deposit',       label: 'Paid a vendor — deposit on an order' },
  { value: 'vendor_final',         label: 'Paid a vendor — final balance (ready to ship)' },
  { value: 'vendor_full_payment',  label: 'Paid a vendor — in full, one payment (in-stock)' },
  { value: 'vendor_fee',           label: 'Paid a shipping / freight / customs bill' },
  { value: 'project_reimbursable', label: 'Project expense — bill the client (+15%)' },
  { value: 'merchandise_direct',   label: 'Bought merchandise directly — no PO, bills via final invoice' },
  { value: 'personal',             label: 'Office expense — firm cost, never billed' },
];

// Phase 2 (2026-06-27) — Kind cards replace the small dropdown. Cimon's
// observation from the receipt-tab copy audit: people "cruise by" the
// dropdown because it doesn't feel important, and the wrong choice has
// real downstream consequences (Kind drives the vendor_payments
// payment_type tagging, isPoFullyPaid math, the "ready to invoice"
// signal, and QB reconciliation). Cards make the choice feel deliberate.
//
// Card copy lives in src/lib/receiptCopy.js (copy.kindCards). The
// 4 vendor sub-types render with a fuchsia accent (project-related);
// Reimbursable + Office are neutral.
var KIND_CARDS = copy.kindCards;

// ── Phase 3 wizard scaffold (2026-06-27) ────────────────────────────────
// The receipt detail modal is a step-by-step wizard. Step count + labels
// vary by Kind. Until the user picks a Kind (Step 1), totalSteps = 1.
//
// Date paid is its own dedicated Step 2 across all kinds (2026-06-28
// per Cimon). The date is load-bearing for downstream order-tracking +
// bookkeeper card-statement reconciliation, and burying it on a busy
// Details step encouraged users to accept the default-today silently.
// As its own step it forces a deliberate "look at the receipt, confirm
// the date" pause.
//
// Vendor wizard (6 steps):
//   1. Receipt type → 2. Date paid → 3. Link the PO → 4. Which items?
//     → 5. Fee details → 6. Confirm + approve
//
// Reimbursable / Office wizard (4 steps):
//   1. Receipt type → 2. Date paid → 3. Details → 4. Confirm + approve

var WIZARD_STEPS_BY_KIND = {
  vendor_deposit:       ['Receipt type', 'Receipt date', 'Link the PO', 'Which items?', 'Fee details', 'Confirm'],
  vendor_final:         ['Receipt type', 'Receipt date', 'Link the PO', 'Which items?', 'Fee details', 'Confirm'],
  vendor_full_payment:  ['Receipt type', 'Receipt date', 'Link the PO', 'Which items?', 'Fee details', 'Confirm'],
  vendor_fee:           ['Receipt type', 'Receipt date', 'Link the PO', 'Which items?', 'Fee details', 'Confirm'],
  project_reimbursable: ['Receipt type', 'Receipt date', 'Details', 'Confirm'],
  personal:             ['Receipt type', 'Receipt date', 'Details', 'Confirm'],
  // Direct merchandise purchase (2026-08-23): no PO, no fee cascade —
  // the item link replaces the PO link. Approval is a plain status flip.
  merchandise_direct:   ['Receipt type', 'Receipt date', 'Item & details', 'Confirm'],
};

function wizardStepsForKind(kind) {
  return WIZARD_STEPS_BY_KIND[kind] || ['Receipt type'];
}

// Receipt date + lead weeks → 'Sep 22' (the wizard's live ETA preview).
// Pure; blank on missing/unparseable inputs — never guessed.
export function receiptEtaFor(receiptDate, leadWeeks) {
  var wks = parseInt(leadWeeks, 10);
  if (isNaN(wks) || wks <= 0) return '';
  var m = String(receiptDate || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return '';
  try {
    var d = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10) + wks * 7);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch (e) { return ''; }
}

// ── FORMATTING ────────────────────────────────────────────────────────────────

function fmtDateOnly(s) {
  if (!s) return '—';
  try {
    var d = new Date(s + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch(e) { return s; }
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    var s = String(iso);
    var m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    var d = m ? new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3])) : new Date(s);
    if (isNaN(d.getTime())) return s.slice(0, 10);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch(e) { return String(iso).slice(0, 10); }
}

// Procurement kinds carry a vendor reference that pushes into QB as a
// Bill against a real Vendor record. Canonical vendor names are
// load-bearing here — drift between "Casa Design" / "Casa Design, LLC"
// splits QB Bills and vendor_payments rows across phantom vendors, then
// 1099 reporting and the per-vendor outstanding balance break. So
// procurement receipts get the strict VendorPicker (typeahead + dup-check
// modal). Project_reimbursable and personal (Office) kinds carry the
// vendor as a free-form label only — Lyft, Starbucks, Amazon never want
// to live in the trade-vendor directory, and the downstream QB target
// is an Expense (project Class or firm overhead), not a Bill. Those
// kinds use a plain text input. Single helper so the call sites can't
// drift.
export function isProcurementKind(kind) {
  return kind === 'vendor_deposit'
      || kind === 'vendor_final'
      || kind === 'vendor_full_payment'
      || kind === 'vendor_fee';
}

// Categorize a receipt kind into the three top-level buckets the
// bookkeeper-facing UI uses for filtering + color-coding. The four
// vendor_* kinds collapse to 'vendor' (they share a row-border accent
// and a single "Vendor" filter chip); project_reimbursable maps to
// 'reimbursable'; personal (DB enum) maps to 'office' (UI label). The
// 'unknown' enum stays distinct so it doesn't accidentally get
// categorized — Approve is gated against it upstream.
export function kindCategory(kind) {
  if (isProcurementKind(kind)) return 'vendor';
  if (kind === 'project_reimbursable') return 'reimbursable';
  if (kind === 'merchandise_direct') return 'merchandise';
  if (kind === 'personal') return 'office';
  if (kind === 'sales_tax') return 'tax';   // 2026-10-16: filing mirrors
  return 'unknown';
}

// Kinds whose vendor input is the strict <VendorPicker> (directory
// match + dup-check modal). The four procurement kinds because the
// vendor string drives QB Bills + vendor_payments; merchandise_direct
// because it's a real trade vendor selling real merchandise — the
// canonical name matters for statement matching and the item record —
// even though NO cascade or QB push ever fires from it.
export function vendorInputUsesDirectory(kind) {
  return isProcurementKind(kind) || kind === 'merchandise_direct';
}

// TYPE_ACCENT removed 2026-06-28. The 3px colored left bar on receipt
// rows (amber for vendor, sage for reimbursable, grey for office) was
// duplicating information that the row text + Inbox filter chips +
// section grouping already convey. Per Bauhaus discipline (color used
// sparingly), repeating receipt-type via chromatic accent on every
// row was noise, not signal. Row category is still computed via
// kindCategory() for the filter chips + the row's data-receipt-category
// attribute (test selector). The visual bar is gone.

// ── FILE / API HELPERS ────────────────────────────────────────────────────────

function fileToBase64(file) {
  return new Promise(function(resolve, reject) {
    var reader = new FileReader();
    reader.onload  = function() {
      var result = String(reader.result || '');
      var idx = result.indexOf(',');
      resolve(idx >= 0 ? result.substring(idx + 1) : result);
    };
    reader.onerror = function() { reject(new Error('Could not read file')); };
    reader.readAsDataURL(file);
  });
}

// Downscale big photos before extraction (2026-08-31 — IMG_0387 HTTP 413).
// Vercel caps request bodies (~4.5MB) and a full-res phone photo + base64's
// ~33% inflation blows past it. OCR needs nowhere near full resolution, so
// images over EXTRACT_MAX_BYTES are redrawn onto a canvas capped at
// EXTRACT_MAX_EDGE px and re-encoded as JPEG. ONLY the extraction payload
// shrinks — the storage archive keeps the original file (receipts mirror
// the paper; the stored PDF/photo is the audit copy). PDFs and small
// images pass through untouched; any decode failure (e.g. HEIC in a
// browser that can't rasterize it) falls back to the original bytes so
// behavior is never worse than before.
var EXTRACT_MAX_BYTES = 2.5 * 1024 * 1024;
var EXTRACT_MAX_EDGE  = 2200;

// HEIC/HEIF → JPEG at intake (2026-08-31, Cimon: "make it so gilda reads
// HEIC or converts"). iPhones shoot HEIC by default, but neither the
// Claude vision API nor non-Safari browsers can decode it — extraction
// failed AND the stored copy wouldn't preview. Converting ONCE here means
// the same JPEG feeds extraction, storage, and the wizard preview; the
// JPEG (quality 0.9, full resolution) is the faithful mirror of the
// paper. heic2any (WASM libheif) is dynamically imported so its ~1MB
// never loads unless a HEIC actually arrives. Conversion failure throws
// a plain-language error rather than letting the opaque 413/extraction
// failure surface downstream.
function isHeicFile(file) {
  if (!file) return false;
  var t = String(file.type || '').toLowerCase();
  if (t === 'image/heic' || t === 'image/heif') return true;
  // Safari sometimes hands HEIC files with an empty type — go by name.
  return !t && /\.(heic|heif)$/i.test(String(file.name || ''));
}

async function convertHeicToJpeg(file) {
  try {
    var mod = await import('heic2any');
    var heic2any = mod.default || mod;
    var out = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
    var blob = Array.isArray(out) ? out[0] : out;
    var jpegName = String(file.name || 'receipt').replace(/\.(heic|heif)$/i, '') + '.jpg';
    console.log('[Receipts] HEIC converted:', file.name, '→', jpegName,
      Math.round(file.size / 1024) + 'KB → ' + Math.round(blob.size / 1024) + 'KB');
    return new File([blob], jpegName, { type: 'image/jpeg' });
  } catch (e) {
    console.error('[Receipts] HEIC conversion failed:', e);
    throw new Error('Could not convert ' + (file.name || 'the photo') + ' from HEIC. Re-take as JPEG (Settings → Camera → Formats → Most Compatible) or screenshot the photo and upload that.');
  }
}

async function fileForExtraction(file) {
  var passthrough = async function() {
    return { fileBase64: await fileToBase64(file), mimeType: file.type || 'application/octet-stream' };
  };
  if (!file || !/^image\//.test(file.type || '') || file.size <= EXTRACT_MAX_BYTES) {
    return passthrough();
  }
  try {
    var url = URL.createObjectURL(file);
    var img = await new Promise(function(resolve, reject) {
      var el = new Image();
      el.onload = function() { resolve(el); };
      el.onerror = function() { reject(new Error('decode failed')); };
      el.src = url;
    });
    var scale = Math.min(1, EXTRACT_MAX_EDGE / Math.max(img.naturalWidth, img.naturalHeight));
    var canvas = document.createElement('canvas');
    canvas.width  = Math.max(1, Math.round(img.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(url);
    var dataUrl = canvas.toDataURL('image/jpeg', 0.82);
    var idx = dataUrl.indexOf(',');
    console.log('[Receipts] downscaled for extraction:', file.name,
      Math.round(file.size / 1024) + 'KB → ~' + Math.round((dataUrl.length - idx) * 0.75 / 1024) + 'KB');
    return { fileBase64: dataUrl.substring(idx + 1), mimeType: 'image/jpeg' };
  } catch (e) {
    console.warn('[Receipts] downscale failed, sending original:', e && e.message);
    return passthrough();
  }
}

async function callExtractionAPI(payload) {
  console.log('[Receipts] POST /api/extract-receipt', { mimeType: payload.mimeType, hasFile: !!payload.fileBase64, kindHint: payload.kindHint, openPOs: (payload.availablePOs || []).length });
  var r = await fetch('/api/extract-receipt', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    var err = await r.json().catch(function() { return {}; });
    var msg = err.error || ('Extraction failed (HTTP ' + r.status + ')');
    console.error('[Receipts] extraction failed:', msg, err);
    throw new Error(msg);
  }
  var data = await r.json();
  console.log('[Receipts] extraction ok:', data);
  return data;
}

async function uploadReceiptFile(file, kind, receiptId) {
  var ext = (file.name.split('.').pop() || 'bin').toLowerCase().replace(/[^a-z0-9]/g, '');
  var month = new Date().toISOString().slice(0, 7);
  var path  = (kind || 'unknown') + '/' + month + '/' + receiptId + '.' + ext;
  console.log('[Receipts] storage upload:', path);
  var r = await supabase.storage.from('receipts').upload(path, file, {
    contentType: file.type || 'application/octet-stream',
    upsert: false,
  });
  if (r.error) {
    console.error('[Receipts] storage upload failed:', r.error);
    throw new Error('Storage upload failed: ' + (r.error.message || r.error));
  }
  return path;
}

// ── STYLES ────────────────────────────────────────────────────────────────────

var S = {
  // fontFeatureSettings: TABULAR applies tnum + lnum to every numeral
  // rendered anywhere inside the tab. Per CLAUDE.md design discipline:
  // "Tabular numerals on every numeric display." Inheriting at the wrap
  // level catches every $ amount, percentage, date, and ID without
  // having to dot 15+ fmtMoney() sites with the same style.
  wrap:        { fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: INK_1, background: SURFACE, minHeight: '100vh', position: 'relative', display: 'flex', flexDirection: 'column', fontFeatureSettings: TABULAR },
  toolbar:     { display: 'flex', alignItems: 'center', height: 44, padding: '0 14px', gap: 8, borderBottom: HAIRLINE_SHELL, background: SURFACE, flexShrink: 0 },
  pageTitle:   { fontSize: 13, fontWeight: 500, color: INK_1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  tbtnGhost:   { height: 28, padding: '0 10px', borderRadius: 5, border: HAIRLINE_SHELL, background: SURFACE, cursor: 'pointer', fontSize: 11, fontWeight: 500, color: INK_1, fontFamily: 'inherit', whiteSpace: 'nowrap' },
  tbtnPrimary: { height: 28, padding: '0 11px', borderRadius: 5, border: '0.5px solid ' + INK_1, background: INK_1, cursor: 'pointer', fontSize: 11, fontWeight: 500, color: SURFACE, fontFamily: 'inherit', whiteSpace: 'nowrap' },
  tbtnDanger:  { height: 28, padding: '0 10px', borderRadius: 5, border: HAIRLINE_RED, background: SURFACE, cursor: 'pointer', fontSize: 11, fontWeight: 500, color: ALERT_RED_TEXT, fontFamily: 'inherit', whiteSpace: 'nowrap' },
  tbtnSuccess: { height: 28, padding: '0 11px', borderRadius: 5, border: '0.5px solid ' + ZONE_GREEN, background: ZONE_GREEN, cursor: 'pointer', fontSize: 11, fontWeight: 500, color: SURFACE, fontFamily: 'inherit', whiteSpace: 'nowrap' },
  vsep:        { width: '0.5px', height: 18, background: LINE_SHELL, margin: '0 2px', flexShrink: 0 },
  splitWrap:   { display: 'flex', flex: 1, minHeight: 0 },
  readerPane:  { width: '42%', minWidth: 360, borderRight: HAIRLINE_SHELL, background: GROUND_OFF, display: 'flex', flexDirection: 'column', padding: '20px 22px', overflowY: 'auto' },
  inboxPane:   { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', background: SURFACE },
  inboxHead:   { display: 'flex', alignItems: 'center', gap: 8, padding: '14px 18px 10px', borderBottom: HAIRLINE_ROW, flexShrink: 0, flexWrap: 'wrap' },
  inboxList:   { flex: 1, overflowY: 'auto', minHeight: 0 },
  sectionHead: Object.assign({}, TYPE_EYEBROW),
};

// ── STATUS DOT (matches ui.jsx pattern) ──────────────────────────────────────

function StatusPill(p) {
  var s = STATUS_STYLE[p.status] || STATUS_STYLE.pending;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0, whiteSpace: 'nowrap' }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.dot, flexShrink: 0 }} />
      <span style={{ fontSize: 11, color: s.text, fontWeight: 400 }}>{s.label}</span>
    </span>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────

// ── Implicit fee-choice synthesis (2026-06-26 default, HARDENED 2026-07-02) ──
// At approval, fee categories the bookkeeper didn't explicitly decide get an
// implicit "bill as fee" default so real actual-vs-estimate deltas flow to
// client billing without an extra click. The 2026-07-02 hardening closes a
// silent margin leak found in audit: the original default fired on ANY
// non-zero delta — including when NO actual was ever entered (empty
// fee_breakdown → actual 0 → delta = −estimate), which quietly ZEROED the
// items' fee estimates at approval (e.g., approving a deposit receipt, whose
// invoice naturally carries no fee lines, erased the PO items' freight
// estimate — the eventual FIN then billed $0 freight the firm actually paid).
// The rule now: a choice is synthesized ONLY when a positive actual was
// entered for that category. Zeroing or reducing fees with no entered actual
// requires an explicit human choice in the fee panel. Explicit choices in
// rawChoices are always preserved untouched.
// Pure + module-scope, exported for tests.
export function synthesizeImplicitFeeChoices(feeBreakdown, poItems, rawChoices) {
  var choices = Object.assign({}, rawChoices || {});
  var bd = (feeBreakdown && typeof feeBreakdown === 'object') ? feeBreakdown : {};
  var cats = ['freight', 'crating', 'installation', 'tariff', 'custom'];
  cats.forEach(function(k) {
    if (choices[k]) return;                      // explicit choice wins
    var actual = parseFloat(bd[k]) || 0;
    if (actual <= 0) return;                     // never synthesize from an empty actual
    var estSum = computeFeeSumForCategory(poItems, k);
    var delta = Math.round((actual - estSum) * 100) / 100;
    if (Math.abs(delta) >= 0.01) choices[k] = 'bill_as_fee';
  });
  return choices;
}

// ── Fee confirmation (2026-07-02, Cimon's directive) ────────────────────
// "I would like all estimated fees included in the wizard, and then have
// people confirm those fees or edit them." Structural completion of the
// f314206 hardening: the fee step PRE-FILLS the PO's estimated fees and a
// human must confirm-or-edit them before Approve unlocks, so the "empty
// fee panel" state ceases to exist. Persisting the fully-populated
// receipts.fee_breakdown IS the confirmation marker — no new column.
//
// Three pure, module-scope helpers (exported for unit tests):
//   prefillFeeBreakdown  — what the fee inputs initialize to
//   feeStepRequired      — which receipts owe a confirmation at all
//   feeConfirmationGate  — the Approve-blocking predicate
//
// A saved fee_breakdown value counts as "present" when the key exists
// with a finite numeric value — INCLUDING an explicit 0 (the bookkeeper
// zeroing a fee is a decision; it must not silently re-inflate to the
// estimate on the next open).

// Human-facing labels for the five fee categories — used by the gate
// messages ("Still to confirm: Freight, Tariff").
var FEE_CATEGORY_LABELS = {
  freight:      'Freight',
  crating:      'Crating',
  installation: 'Installation',
  tariff:       'Tariff',
  custom:       'Custom fees',
};
export function feeCategoryLabel(key) {
  return FEE_CATEGORY_LABELS[key] || key;
}

// True when the value stored for a category is a real saved number
// (explicit 0 included). Missing key / null / '' / non-numeric → false.
function feeValueIsPresent(v) {
  if (v === undefined || v === null || v === '') return false;
  var n = parseFloat(v);
  return isFinite(n);
}

// prefillFeeBreakdown — the value each fee category's "actual" input
// initializes to, and the exact object "Confirm fees" persists.
// Precedence per category: the receipt's saved fee_breakdown value if
// present → else the PO estimate (computeFeeSumForCategory). Always
// returns ALL five categories, rounded to cents.
export function prefillFeeBreakdown(feeBreakdown, poItems) {
  // PER-RECEIPT SEMANTICS (2026-08-10, supersedes the 2026-07-02
  // prefill-the-estimates design): a receipt records ONLY what is on
  // that receipt. Untouched categories stay EMPTY — empty means "this
  // receipt says nothing about that fee" and never touches items or
  // prompts a choice. The PO estimate stays visible as a reference
  // column but is never injected as a value. (Multi-shipment POs made
  // the injected estimates read as this receipt's fees — the Casa
  // 26MOS-PO-003 confusion.)
  var bd = (feeBreakdown && typeof feeBreakdown === 'object') ? feeBreakdown : {};
  var out = {};
  FEE_CATEGORIES_ALL.forEach(function(k) {
    out[k] = feeValueIsPresent(bd[k]) ? Math.round(parseFloat(bd[k]) * 100) / 100 : '';
  });
  return out;
}

// feeStepRequired — does this receipt owe a fee confirmation?
// True for vendor-kind receipts linked to a PO whose items carry ANY
// nonzero fee estimate in any category, OR whose receipt already has
// any fee actuals saved. Non-vendor kinds and vendor receipts without
// a PO are exempt.
export function feeStepRequired(receipt, poItems) {
  if (!receipt) return false;
  if (!isProcurementKind(receipt.kind)) return false;
  if (!receipt.poId) return false;
  var hasEstimate = FEE_CATEGORIES_ALL.some(function(k) {
    return computeFeeSumForCategory(poItems, k) >= 0.01;
  });
  if (hasEstimate) return true;
  var bd = (receipt.feeBreakdown && typeof receipt.feeBreakdown === 'object') ? receipt.feeBreakdown : {};
  return FEE_CATEGORIES_ALL.some(function(k) {
    return feeValueIsPresent(bd[k]) && Math.abs(parseFloat(bd[k])) >= 0.01;
  });
}

// feeConfirmationGate — the Approve-blocking predicate. Blocks until
// fee_breakdown contains a saved value for EVERY category that has a
// nonzero PO estimate (confirmed-at-estimate counts — that's the one
// click). Categories with zero estimate and no actual are ignored.
// No owner/admin bypass. Returns { blocked, missing } where missing is
// the list of category keys still awaiting a confirmed value.
export function feeConfirmationGate(receipt, poItems) {
  // 2026-08-10: never blocks. Under per-receipt semantics an empty
  // category is a real answer ("not on this receipt"), so there is
  // nothing to force-confirm. The function survives for its callers
  // and tests; the silent-zero hazard the old gate guarded against is
  // structurally gone (empty no longer writes anything).
  return { blocked: false, missing: [] };
}

export default function ReceiptsTab() {
  var ctx = useAppContext();
  var receipts = ctx.receipts || [];
  var purchaseOrders = ctx.purchaseOrders || [];
  var projects = ctx.projects || [];
  var vendors = ctx.vendors || [];
  var items = ctx.items || [];
  var vendorPayments = ctx.vendorPayments || [];
  var vendorPaymentItems = ctx.vendorPaymentItems || [];
  var setVendorPaymentItems = ctx.setVendorPaymentItems;
  var addVendorInline = ctx.addVendorInline;
  var saveReceipt = ctx.saveReceipt;
  var deleteReceipt = ctx.deleteReceipt;
  var applyVendorReceiptSideEffects = ctx.applyVendorReceiptSideEffects;
  var replayReceiptCascade = ctx.replayReceiptCascade;
  var updateItem = ctx.updateItem;
  var savePOToDb = ctx.savePOToDb;
  var setPurchaseOrders = ctx.setPurchaseOrders;
  var userEmail = (ctx.userEmail || '').toLowerCase();
  var isOwner = ctx.isOwner;
  var firmSettings = ctx.firmSettings || {};
  var canManage = isOwner || hasAdminAccess(userEmail, firmSettings);

  var fileInputRef = useRef(null);
  var dragCounterRef = useRef(0);
  var us = useState(false);    var uploading = us[0];      var setUploading = us[1];
  var ues = useState('');      var uploadErr = ues[0];     var setUploadErr = ues[1];
  var ums = useState('');      var uploadMsg = ums[0];     var setUploadMsg = ums[1];
  var uss = useState('');      var uploadSuccess = uss[0]; var setUploadSuccess = uss[1];
  var khs = useState('');      var kindHint  = khs[0];     var setKindHint = khs[1];
  var ds = useState(null);     var detailId  = ds[0];      var setDetailId = ds[1];

  // Honor detail jumps from other tabs — opens the receipt detail modal.
  useDetailJump('receipts', function(id) { setDetailId(id); });
  // BillToClientModal state removed 2026-05-18 (modal deleted).
  var fs = useState('all');    var lensFilter = fs[0];     var setLensFilter = fs[1];
  var sfs = useState('all');   var statusFilter = sfs[0];  var setStatusFilter = sfs[1];
  // Expanded-section state (2026-06-28). Approved + Rejected sections
  // collapse to the first 6 rows by default with a "Show all N →"
  // expander; Inbox always renders fully. Sections live in a Set so
  // multiple can be expanded simultaneously.
  var ess = useState(function() { return new Set(); });
  var expandedSections = ess[0];
  var setExpandedSections = ess[1];
  // Type filter is multi-select via a Set. Zero active = no type
  // restriction (all kinds visible); one or more active = union of
  // selected categories. Intersects with statusFilter via the
  // visibleReceipts memo below.
  var tfs = useState(function() { return new Set(); });
  var typeFilter = tfs[0]; var setTypeFilter = tfs[1];
  // Filter bar — Project / Search / Date range (2026-06-28 per Cimon
  // mockup 20.png). Defaults to 'all' / '' / 'any' so the filter bar
  // is a no-op until the bookkeeper opts in.
  var pfs = useState('all'); var projectFilter = pfs[0]; var setProjectFilter = pfs[1];
  var vss = useState('');    var vendorSearch  = vss[0]; var setVendorSearch  = vss[1];
  var dfs = useState('any'); var dateFilter    = dfs[0]; var setDateFilter    = dfs[1];
  function toggleTypeFilter(value) {
    setTypeFilter(function(prev) {
      var next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  }
  var isd = useState(false);   var isDragging = isd[0];    var setIsDragging = isd[1];
  var qs = useState({ done: 0, total: 0 });
  var queue = qs[0];           var setQueue = qs[1];

  // POs available for matching. Includes Complete because the
  // bookkeeper sometimes records vendor payment receipts AFTER the
  // workflow advances to Complete (legacy data correction, late CC
  // statement entry, missed deposit). Excluding Complete here used
  // to block those corrections — surfaced 2026-06-07 walking Hewn
  // 26HOB-PO-005 and O+G 26HOB-PO-003 after-the-fact reconciliation.
  var openPOs = useMemo(function() {
    return purchaseOrders.filter(function(po) {
      return po.status === 'Issued'
          || po.status === 'Confirmed'
          || po.status === 'Draft'
          || po.status === 'Complete';
    });
  }, [purchaseOrders]);

  // Lens: "all" shows every receipt the current viewer is allowed to see
  // by RLS (vendor receipts visible to everyone; reimbursables visible to
  // submitter, owner, and bookkeeper). "mine" narrows to the current
  // user's own submissions across all kinds.
  //
  // Each filter dimension is a standalone predicate so the row list AND
  // the chip counts compose the same logic. Chip counts apply every
  // dimension EXCEPT their own (faceted counts) — pre-2026-07-15 the
  // type counts ignored project/date/search, so with a search active a
  // chip could claim "Office · 15" while clicking it showed zero rows.
  var filterDims = useMemo(function() {
    var pass = function() { return true; };
    var lensFn = lensFilter === 'mine'
      ? function(r) { return (r.createdBy || '').toLowerCase() === userEmail; }
      : pass;
    var statusFn = (STATUS_FILTERS.find(function(f) { return f.value === statusFilter; }) || STATUS_FILTERS[0]).match;
    var typeFn = typeFilter.size === 0
      ? pass
      : function(r) { return typeFilter.has(kindCategory(r.kind)); };
    var projectFn = projectFilter === 'all'
      ? pass
      : function(r) { return (r.projectLabel || '') === projectFilter; };
    var dateFn = (function() {
      if (dateFilter === 'any') return pass;
      var now = new Date();
      var cutoff;
      if (dateFilter === 'week') {
        cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
      } else if (dateFilter === 'month') {
        cutoff = new Date(now.getFullYear(), now.getMonth(), 1);
      } else if (dateFilter === 'quarter') {
        var q = Math.floor(now.getMonth() / 3) * 3;
        cutoff = new Date(now.getFullYear(), q, 1);
      } else if (dateFilter === 'year') {
        cutoff = new Date(now.getFullYear(), 0, 1);
      } else {
        return pass;
      }
      return function(r) {
        if (!r.receiptDate) return false;
        var d = new Date(String(r.receiptDate) + 'T00:00:00');
        return !isNaN(d.getTime()) && d >= cutoff;
      };
    })();
    var q = String(vendorSearch || '').trim();
    var searchFn = !q ? pass : function(r) {
      var po = r.poId ? (purchaseOrders || []).find(function(x) { return x.id === r.poId; }) : null;
      return receiptMatchesSearch(r, po, q);
    };
    return { lensFn: lensFn, statusFn: statusFn, typeFn: typeFn, projectFn: projectFn, dateFn: dateFn, searchFn: searchFn };
  }, [lensFilter, statusFilter, typeFilter, projectFilter, dateFilter, vendorSearch, purchaseOrders, userEmail]);

  var visibleReceipts = useMemo(function() {
    var d = filterDims;
    return receipts.filter(function(r) {
      return d.lensFn(r) && d.statusFn(r) && d.typeFn(r) && d.projectFn(r) && d.dateFn(r) && d.searchFn(r);
    });
  }, [receipts, filterDims]);

  // Per-status counts for the filter chips — every dimension EXCEPT
  // status applies, so each chip answers "how many rows if I click it."
  var statusCounts = useMemo(function() {
    var d = filterDims;
    var counts = {};
    STATUS_FILTERS.forEach(function(f) {
      counts[f.value] = receipts.filter(function(r) {
        return d.lensFn(r) && d.typeFn(r) && d.projectFn(r) && d.dateFn(r) && d.searchFn(r) && f.match(r);
      }).length;
    });
    return counts;
  }, [receipts, filterDims]);

  // Per-type counts for the type-filter chips — every dimension EXCEPT
  // type applies (same faceted rule as statusCounts), so "Office · 15"
  // always yields 15 rows on click even with a search or date active.
  var typeCounts = useMemo(function() {
    var d = filterDims;
    var counts = {};
    TYPE_FILTERS.forEach(function(f) {
      counts[f.value] = receipts.filter(function(r) {
        return d.lensFn(r) && d.statusFn(r) && d.projectFn(r) && d.dateFn(r) && d.searchFn(r) && f.match(r);
      }).length;
    });
    return counts;
  }, [receipts, filterDims]);

  // ── Upload pipeline ──
  // Note: the upload button and drop card use <label htmlFor="receipts-file-input">
  // to trigger the native file picker. pickFile() is kept as a programmatic fallback.
  function pickFile() {
    console.log('[Receipts] pickFile called');
    setUploadErr(''); setUploadMsg(''); setUploadSuccess('');
    var el = fileInputRef.current;
    if (el) {
      console.log('[Receipts] triggering file input click');
      el.click();
    } else {
      console.error('[Receipts] fileInputRef is null — file input not rendered');
    }
  }

  async function processFile(file) {
    if (!file) throw new Error('No file');
    setUploadMsg('Reading ' + file.name + '…');

    // HEIC arrives → convert to JPEG FIRST; the converted file flows
    // through everything below (extraction, storage upload, preview),
    // so no HEIC ever lands in the archive.
    if (isHeicFile(file)) {
      setUploadMsg('Converting ' + file.name + ' from HEIC…');
      file = await convertHeicToJpeg(file);
    }

    var extractFile = await fileForExtraction(file);
    setUploadMsg('Asking Gilda to read the receipt…');

    var apiResult = await callExtractionAPI({
      fileBase64: extractFile.fileBase64,
      mimeType:   extractFile.mimeType,
      kindHint:   kindHint || undefined,
      availablePOs: openPOs.map(function(po) {
        // product_at_cost: vendor's product subtotal (no fees) — the legacy
        // `total_net` value. Useful when the vendor invoices deposits as a
        // simple % of product only.
        // total_at_cost: vendor's full PO obligation including freight,
        // crating, tariff, custom, and per-item installation. Matches the
        // PO PDF subtotal. Most real receipts include some or all fees,
        // so this is the correct comparison baseline for vendor_full_payment
        // and for finals that bundle remaining product + fees.
        // project + project_location + vendor_aliases (2026-07-11): the
        // Mehraban audit showed the matcher's two real failure modes are
        // same-vendor-different-project picks and vendor name variants.
        // The receipt's ship-to address vs the PO's project location is
        // the disambiguator; directory aliases bridge DBAs/showroom names.
        var poProj = projects.find(function(pr) { return pr && pr.label === po.project; });
        var poVendor = vendors.find(function(v) {
          return v && String(v.name || '').toLowerCase() === String(po.vendorName || '').toLowerCase();
        });
        return {
          id: po.id,
          po_ref: po.po_ref,
          vendor_name: po.vendorName,
          vendor_aliases: (poVendor && Array.isArray(poVendor.aliases)) ? poVendor.aliases : [],
          project: po.project || '',
          project_location: poProj ? [poProj.city, poProj.state].filter(Boolean).join(', ') : '',
          product_at_cost: po.total_net,
          total_at_cost: poSubtotalLive(po, items),
        };
      }),
      availableProjects: projects.map(function(p) {
        return { label: p.label, code: p.projectCode, city: p.city, state: p.state };
      }),
    });

    var ex = apiResult.extraction || {};
    var receiptId = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : (Date.now() + '-' + Math.random());

    setUploadMsg('Saving file…');
    var storagePath = await uploadReceiptFile(file, ex.suggested_kind || kindHint || 'unknown', receiptId);

    var newReceipt = {
      id: receiptId,
      kind: ex.suggested_kind && ex.suggested_kind !== 'unknown' ? ex.suggested_kind : (kindHint || 'personal'),
      status: apiResult.suggested_status || 'pending',
      storagePath: storagePath,
      filename: file.name,
      mimeType: file.type || '',
      vendorName: ex.vendor_name || '',
      // Receipt date (2026-06-28 — Cimon: use OCR-extracted date when
      // Gilda can read it, blank otherwise). Prior behavior defaulted
      // to today, which masked OCR failures (bookkeeper would accept
      // today silently when the real date was a week ago). Blank now
      // forces the bookkeeper to look at the receipt and confirm.
      // The required-to-approve gate at line ~957 blocks vendor-kind
      // approvals if this is still empty. itemPatchesForReceiptCascade
      // uses this date to stamp items' orderPlacedDate (lead-time
      // clock start), so a real date here matters.
      receiptDate: ex.receipt_date || '',
      paidDate: new Date().toISOString().slice(0, 10),
      totalAmount: parseFloat(ex.total_amount) || 0,
      currency: ex.currency || 'USD',
      extractedJson: ex,
      matchConfidence: parseFloat(ex.match_confidence) || 0,
      poId: ex.suggested_po_id || '',
      itemId: '',
      projectLabel: ex.suggested_project_label || '',
      createdBy: userEmail,
      feeType: '',
      allocationMethod: 'net_price_weighted',
      allocationBreakdown: [],
      billedOnInvoiceId: '',
      qbTarget: '',
      qbId: '',
      qbPushedAt: '',
      qbError: '',
      // Notes starts BLANK — it belongs to the human (2026-09-04).
      //
      // This used to seed ex.match_reasoning, which was wrong twice over:
      //   1. `notes` is the bookkeeper's own annotation field, and a 3-5
      //      line AI paragraph buried whatever they typed.
      //   2. `notes` is CLIENT-FACING for reimbursables —
      //      receiptSummaryDescription() returns it FIRST, so it becomes
      //      the line description on the billing-summary statement. Match
      //      prose about PO confidence and _at_cost comparisons was one
      //      picked receipt away from printing on Anne's invoice.
      //
      // Nothing is lost: the full extraction (match_reasoning included)
      // is already persisted on extracted_json / r.extractedJson.
      notes: '',
      approvedAt: '',
      approvedBy: '',
      rejectedAt: '',
      rejectedReason: '',
      paidWithCardId: '',
    };

    // Auto-link a payment method by last_4 from Claude's extracted
    // payment_method object. Only auto-link when there's exactly ONE
    // eligible card matching for the current user (avoids guessing
    // when a holder has two cards ending in the same digits).
    var pm = (ex.payment_method && typeof ex.payment_method === 'object') ? ex.payment_method : null;
    var pmLast4 = pm && pm.last_4 ? String(pm.last_4).slice(-4) : '';
    if (pmLast4 && /^[0-9]{4}$/.test(pmLast4)) {
      var matches = (ctx.firmCreditCards || []).filter(function(c) {
        return c.isActive
          && (c.holderEmail || '').toLowerCase() === (userEmail || '').toLowerCase()
          && c.last4 === pmLast4;
      });
      if (matches.length === 1) {
        newReceipt.paidWithCardId = matches[0].id;
      }
    }

    saveReceipt(newReceipt);
    return receiptId;
  }

  // ── Manual (no-document) receipt (2026-10-04) ──
  // Cash tips to movers, parking meters, anything paid with no paper
  // to scan. Before this, Cimon had to write up a document and upload
  // it just to get a receipt row. Same receipt record as an upload —
  // kind/status/approval/cascade all unchanged — but born with no
  // storage file and no OCR extraction. The detail wizard opens on
  // Step 1 (Receipt type) so the bookkeeper fills the facts by hand.
  // A paper trail can be attached later via "+ Add another file".
  // Receipt date defaults to today here (unlike uploads, which leave it
  // blank to force a look at the paper): there is no paper to check.
  function createManualReceipt() {
    var receiptId = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : (Date.now() + '-' + Math.random());
    var today = new Date().toISOString().slice(0, 10);
    var manual = {
      id: receiptId,
      kind: 'personal',
      status: 'pending',
      storagePath: '',
      filename: '',
      mimeType: '',
      vendorName: '',
      receiptDate: today,
      paidDate: today,
      totalAmount: 0,
      currency: 'USD',
      extractedJson: {},
      matchConfidence: 0,
      poId: '',
      itemId: '',
      projectLabel: '',
      createdBy: userEmail,
      feeType: '',
      allocationMethod: 'net_price_weighted',
      allocationBreakdown: [],
      billedOnInvoiceId: '',
      qbTarget: '',
      qbId: '',
      qbPushedAt: '',
      qbError: '',
      notes: '',
      approvedAt: '',
      approvedBy: '',
      rejectedAt: '',
      rejectedReason: '',
      paidWithCardId: '',
    };
    saveReceipt(manual);
    setDetailId(receiptId);
    return receiptId;
  }

  async function processFiles(files) {
    if (!files || files.length === 0) return;
    setUploading(true); setUploadErr(''); setUploadMsg(''); setUploadSuccess('');
    setQueue({ done: 0, total: files.length });

    var lastId = null;
    var failures = [];
    var successCount = 0;
    for (var i = 0; i < files.length; i++) {
      try {
        if (files.length > 1) setUploadMsg('Receipt ' + (i + 1) + ' of ' + files.length + '…');
        lastId = await processFile(files[i]);
        successCount += 1;
      } catch (err) {
        console.error('[Receipts] processFile failed for', files[i].name, err);
        failures.push(files[i].name + ' — ' + (err.message || 'failed'));
      }
      setQueue({ done: i + 1, total: files.length });
    }

    setUploading(false);
    setUploadMsg('');
    setQueue({ done: 0, total: 0 });

    if (failures.length > 0) {
      setUploadErr(failures.length + ' file' + (failures.length === 1 ? '' : 's') + ' failed: ' + failures.join('; '));
    }
    if (successCount > 0) {
      setUploadSuccess(successCount + ' receipt' + (successCount === 1 ? '' : 's') + ' added.');
      setTimeout(function() { setUploadSuccess(''); }, 4000);
    }
    // Auto-open detail only when a single file was uploaded successfully
    if (lastId && files.length === 1 && failures.length === 0) {
      setDetailId(lastId);
    }
  }

  async function handleFileSelected(e) {
    // Capture BEFORE clearing: e.target.files is a LIVE FileList, so
    // resetting .value empties it — the old order made click-to-choose
    // silently do nothing (drag-and-drop worked; it reads
    // dataTransfer.files). Found by the 2026-09-02 training smoke run.
    var files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;
    await processFiles(files);
  }

  // ── Drag-and-drop ──
  function dragHasFiles(e) {
    if (!e.dataTransfer) return false;
    var types = Array.from(e.dataTransfer.types || []);
    return types.indexOf('Files') >= 0;
  }
  function handleDragEnter(e) {
    e.preventDefault(); e.stopPropagation();
    if (!dragHasFiles(e)) return;
    dragCounterRef.current += 1;
    setIsDragging(true);
  }
  function handleDragOver(e) {
    if (!dragHasFiles(e)) return;
    e.preventDefault(); e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  }
  function handleDragLeave(e) {
    e.preventDefault(); e.stopPropagation();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setIsDragging(false);
  }
  function handleDrop(e) {
    e.preventDefault(); e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragging(false);
    if (uploading) return;
    var files = e.dataTransfer && e.dataTransfer.files;
    if (files && files.length > 0) processFiles(Array.from(files));
  }

  // ── Detail / approval actions ──
  //
  // Post-2026-05-29 incident structure: cascade runs FIRST. Receipt
  // status flip to 'approved' is conditional on cascade success.
  // Failure leaves the receipt at its existing status (pending /
  // needs_link) so the UI never shows the half-state where a receipt
  // looks approved but the linked vendor_payments row / item updates
  // never landed. Re-approval after fixing the underlying cause
  // retries idempotently (per-row idempotent on source_receipt_id +
  // payment_type; item updates are forward-only via updateItem).
  //
  // Exception: when the cascade returns ok:true with
  // failure_at: 'item_updates' — the financial rows landed, only some
  // item-status updates failed. We DO flip status to approved
  // (financial side committed) and warn the user that item statuses
  // may be partial.
  //
  // Non-vendor kinds (project_reimbursable, personal): no cascade,
  // status flip happens directly.
  //
  // Alert tone: first-name greeting, Cimon/Quinn framing, plain English.
  // Lead with what to do, not what failed (post-2026-05-29 friendly pass).
  function greeting() {
    if (isOwner) return 'Hi Cimon';
    var n = displayNameFor(userEmail, firmSettings);
    return n ? 'Hi ' + n : 'Hey there';
  }
  function namesForFailedItems(failedItemIds) {
    return (failedItemIds || []).map(function(id) {
      var it = (items || []).find(function(x) { return x.id === id; });
      if (it) return it.item || it.product_name || ('item ' + String(id).slice(0, 8));
      return 'item ' + String(id).slice(0, 8);
    });
  }

  // Append "Bill as adjustment line" choices to the PO's queue so the
  // next FIN generated for items on this PO picks them up. Each
  // adjustment carries the new shape (description, amount, taxable,
  // applyMarkup, markupPct, reason, source_ref, category) per
  // Decision 2 (2026-06-09). Markup defaults to 30% (matches the
  // standard item markup); applyMarkup=true by default because the
  // bookkeeper picking "Bill as adj." implies they want the firm to
  // mark up the recovery (otherwise they'd pick "Don't bill"). The
  // bookkeeper can edit the queued entry later via the FIN-level
  // Adjustments UI before the FIN is generated.
  async function applyFeeChoicesToPoAdjustments(receipt, feeChoices, po, descriptionsOverride) {
    var result = { categoriesQueued: [], adjustmentsQueued: 0, saveFailed: false, skipped: '' };
    if (!feeChoices || !po || !savePOToDb) { result.skipped = 'preconditions'; return result; }
    var poItemIds = Array.isArray(po.items) ? po.items : (Array.isArray(po.item_ids) ? po.item_ids : []);
    var poItems = (items || []).filter(function(it) { return it && poItemIds.indexOf(it.id) >= 0; });
    var bd = receipt.feeBreakdown || {};
    var cats = [
      { key: 'freight',      label: 'Freight' },
      { key: 'crating',      label: 'Crating' },
      { key: 'installation', label: 'Installation' },
      { key: 'tariff',       label: 'Tariff' },
      { key: 'custom',       label: 'Custom fees' },
    ];
    var descriptions = descriptionsOverride || {};
    var newEntries = [];
    cats.forEach(function(c) {
      if (feeChoices[c.key] !== 'bill_as_adj') return;
      var actual = parseFloat(bd[c.key]) || 0;
      var estSum = computeFeeSumForCategory(poItems, c.key);
      var delta = Math.round((actual - estSum) * 100) / 100;
      if (Math.abs(delta) < 0.01) return;
      var entry = buildPoAdjustmentEntry(receipt, c.key, c.label, delta, po.po_ref);
      // 2026-06-26 — user-authored description overrides the
      // auto-generated one. The bookkeeper enters this in Step 1's
      // "Show as adjustment" expansion; spec requires the client-facing
      // line on the FIN to read exactly what they typed.
      var userDesc = String(descriptions[c.key] || '').trim();
      if (userDesc) entry = Object.assign({}, entry, { description: userDesc });
      newEntries.push(entry);
      result.categoriesQueued.push(c.key);
    });
    if (newEntries.length === 0) return result;
    result.adjustmentsQueued = newEntries.length;
    var nextQueue = (po.pendingFinAdjustments || []).concat(newEntries);
    var patched = Object.assign({}, po, { pendingFinAdjustments: nextQueue });
    // Optimistic local update + DB write
    if (setPurchaseOrders) {
      setPurchaseOrders(function(prev) {
        return (prev || []).map(function(x) { return x && x.id === po.id ? patched : x; });
      });
    }
    try { await savePOToDb(patched); }
    catch (e) {
      console.error('applyFeeChoicesToPoAdjustments: savePOToDb failed', e);
      result.saveFailed = true;
    }
    return result;
  }

  // Distribute per-fee delta evenly across PO items per Decision 1
  // (2026-06-09). Updates items.fees on each item. Used by approve()
  // when the bookkeeper picks "Bill as fee" on a non-zero delta in
  // the verification panel. Custom fees append a new {label, amount}
  // entry tagged with the PO ref so the source stays traceable.
  async function applyFeeChoicesToItemsFees(receipt, feeChoices, po, selectedIds) {
    var result = { categoriesUpdated: [], itemsUpdated: 0, itemsFailed: 0, failedItemIds: [], skipped: '' };
    if (!feeChoices || !po || !updateItem) { result.skipped = 'preconditions'; return result; }
    var poItemIds = Array.isArray(po.items) ? po.items : (Array.isArray(po.item_ids) ? po.item_ids : []);
    var poItems = (items || []).filter(function(it) { return it && poItemIds.indexOf(it.id) >= 0; });
    // The receipt's WHICH-ITEMS selection scopes the fee adjustment
    // (2026-08-05 — Cimon: a final receipt for part of a PO adjusted
    // freight/tariff on all 10 PO items despite the wizard selection).
    // The shipped items own the shipment's fees: estimate basis AND the
    // delta distribution both narrow to the selection when it's a real
    // subset; the untouched items keep their estimates for their own
    // later receipts.
    if (Array.isArray(selectedIds) && selectedIds.length > 0 && selectedIds.length < poItems.length) {
      var sel = poItems.filter(function(it) { return selectedIds.indexOf(it.id) >= 0; });
      if (sel.length > 0) poItems = sel;
    }
    if (poItems.length === 0) { result.skipped = 'no_po_items'; return result; }
    var bd = receipt.feeBreakdown || {};
    var cats = ['freight','crating','installation','tariff','custom'];
    for (var i = 0; i < cats.length; i++) {
      var k = cats[i];
      if (feeChoices[k] !== 'bill_as_fee') continue;
      var actual = parseFloat(bd[k]) || 0;
      var estSum = computeFeeSumForCategory(poItems, k);
      var delta = Math.round((actual - estSum) * 100) / 100;
      if (Math.abs(delta) < 0.01) continue;
      var patches = distributeFeeDeltaAcrossItems(poItems, k, delta, po.po_ref);
      if (patches.length === 0) continue;
      result.categoriesUpdated.push(k);
      // ACCUMULATE across categories, ONE write per item (2026-08-07 —
      // the 26MOS-PO-003 clobber: each category's patches carried a full
      // fees object built from the same pre-loop snapshot, so the tariff
      // pass silently reverted the freight pass. poItems is refreshed
      // in place so the next category builds on this one's result.
      for (var j = 0; j < patches.length; j++) {
        var pIdx = -1;
        for (var q = 0; q < poItems.length; q++) { if (poItems[q].id === patches[j].itemId) { pIdx = q; break; } }
        if (pIdx >= 0) poItems[pIdx] = Object.assign({}, poItems[pIdx], { fees: patches[j].fees });
      }
    }
    var touched = poItems.filter(function(it) {
      var orig = (items || []).find(function(x) { return x && x.id === it.id; });
      return orig && JSON.stringify(orig.fees) !== JSON.stringify(it.fees);
    });
    for (var j2 = 0; j2 < touched.length; j2++) {
      try {
        var ok = await updateItem(touched[j2].id, { fees: touched[j2].fees });
        if (ok === false) {
          result.itemsFailed += 1;
          result.failedItemIds.push(touched[j2].id);
        } else {
          result.itemsUpdated += 1;
        }
      } catch (e) {
        console.error('applyFeeChoicesToItemsFees: updateItem failed for', touched[j2].id, e);
        result.itemsFailed += 1;
        result.failedItemIds.push(touched[j2].id);
      }
    }
    return result;
  }

  // Phase 5C (2026-06-24) — write the per-fee-category allocation
  // into vendor_payment_items.fee_share_<cat> for the principal
  // payment row created by this receipt's cascade. Mirrors the
  // sibling helpers (applyFeeChoicesToItemsFees writes to items.fees;
  // applyFeeChoicesToPoAdjustments writes to po.pendingFinAdjustments).
  // Runs AFTER applyVendorReceiptSideEffects has created the vpi rows
  // (with fee_share_*=0 stub values); UPDATEs those rows with the
  // derived shares.
  //
  // Ships freight + tariff as the cascade write path. Crating /
  // installation / custom continue to flow through items.fees + the
  // pending-adjustments queue only until their respective phases land.
  //
  // Returns a summary object for the post-approve alert (same shape
  // as the sibling helpers — categoriesWritten + itemsUpdated).
  async function applyFeeChoicesToVendorPaymentItems(receipt, feeChoices, po, knownPaymentId) {
    var result = { categoriesWritten: [], itemsUpdated: 0, itemsFailed: 0, skipped: '' };
    if (!receipt || !po) { result.skipped = 'preconditions'; return result; }
    var poItemIds = Array.isArray(po.items) ? po.items : (Array.isArray(po.item_ids) ? po.item_ids : []);
    var poItems = (items || []).filter(function(it) { return it && poItemIds.indexOf(it.id) >= 0 && (it.status || '') !== 'Cancelled'; });
    if (poItems.length === 0) { result.skipped = 'no_po_items'; return result; }

    // Locate the principal vendor_payments row this cascade created.
    //
    // FIXED 2026-09-09 — this lookup used to read ctx.vendorPayments ONLY,
    // and that could never work on a first approve. applyVendorReceiptSideEffects
    // inserts the principal row and calls setVendorPayments during the SAME
    // click; a React context value does not change mid-handler, so the array
    // this closure can see never contained the row it was looking for. The
    // function bailed at 'no_principal_payment' before issuing any UPDATE —
    // silently, because `skipped` was never surfaced. Net effect measured in
    // prod on 2026-09-09: 65 approved vendor receipts owed a fee_share, 3 had
    // one, and all three traced to a hand-run backfill SQL script, not to the
    // app. The write had never once succeeded. See
    // scripts/audit-fee-share-write-20260909.mjs for the audit and
    // src/__tests__/feeShareWriteExecutes.test.jsx for the executing repro
    // (test A fails pre-fix; control test B passes, isolating the defect here).
    //
    // The cascade already returns the id it created. Prefer it. Fall back to
    // the state scan only for the paths that DON'T report one — a replay or a
    // second approve on an already-cascaded receipt, where the row pre-dates
    // this render and therefore IS in state. Never scan first: on the common
    // path the scan is guaranteed to miss.
    var principalId = String(knownPaymentId || '').trim();
    if (!principalId) {
      var refreshedVps = (ctx.vendorPayments || vendorPayments);
      var principal = refreshedVps.find(function(vp) {
        if (!vp) return false;
        var srcId = vp.sourceReceiptId || vp.source_receipt_id;
        if (String(srcId) !== String(receipt.id)) return false;
        var pt = vp.paymentType || vp.payment_type || '';
        var isCcRow = !!(vp.isCcFeeRow != null ? vp.isCcFeeRow : vp.is_cc_fee_row);
        return pt !== 'Fee' && !isCcRow;
      });
      if (principal) principalId = String(principal.id);
    }
    if (!principalId) { result.skipped = 'no_principal_payment'; return result; }

    var patches = computeFeeSharePatchesForReceipt({
      receipt: receipt,
      feeChoices: feeChoices,
      poItems: poItems,
      categories: FEE_CATEGORIES_PHASE_5C,
    });

    // UPDATE each vendor_payment_items row tied to the principal.
    // Phase 5C ships freight + tariff only; the other three columns
    // are NOT touched (stays at the cascade's default of 0 or any
    // value the future-phase write paths land).
    for (var i = 0; i < patches.length; i++) {
      var patch = patches[i];
      var updateCols = {};
      FEE_CATEGORIES_PHASE_5C.forEach(function(cat) {
        updateCols['fee_share_' + cat] = patch['fee_share_' + cat];
      });
      try {
        var upd = await supabase.from('vendor_payment_items')
          .update(updateCols)
          .eq('payment_id', principalId)
          .eq('item_id', patch.item_id);
        if (upd.error) {
          console.error('applyFeeChoicesToVendorPaymentItems: UPDATE failed for item ' + patch.item_id, upd.error);
          result.itemsFailed += 1;
        } else {
          result.itemsUpdated += 1;
        }
      } catch (e) {
        console.error('applyFeeChoicesToVendorPaymentItems: threw for item ' + patch.item_id, e);
        result.itemsFailed += 1;
      }
    }

    // Optimistically refresh local state so a same-session FIN build
    // sees the new fee_share values without a page reload.
    if (setVendorPaymentItems && result.itemsUpdated > 0) {
      setVendorPaymentItems(function(prev) {
        return (prev || []).map(function(vpi) {
          if (!vpi) return vpi;
          if (String(vpi.payment_id) !== String(principalId)) return vpi;
          var match = patches.find(function(p) { return String(p.item_id) === String(vpi.item_id); });
          if (!match) return vpi;
          var copy = Object.assign({}, vpi);
          FEE_CATEGORIES_PHASE_5C.forEach(function(cat) {
            copy['fee_share_' + cat] = match['fee_share_' + cat];
          });
          return copy;
        });
      });
    }

    result.categoriesWritten = FEE_CATEGORIES_PHASE_5C.slice();
    return result;
  }

  async function approve(r, opts) {
    var rawFeeChoices = (opts && opts.feeChoices) || {};
    var adjDescs       = (opts && opts.feeAdjustmentDescriptions) || {};
    var absorptionReasons = (opts && opts.feeAbsorptionReasons) || {};
    // 2026-06-26 — principal-mis-tag override. When the bookkeeper hit
    // the principalMisclassMsg hard guard and chose to proceed anyway,
    // their reason text comes through here. Prepend it to receipt.notes
    // so the rationale stays auditable on the receipt row itself (the
    // vendor_payments rows the cascade writes will carry the chosen
    // payment_type as-is; this is the "yes I really mean it" trail).
    var kindOverrideReasonRaw = String((opts && opts.kindOverrideReason) || '').trim();
    var rWithOverrideNote = r;
    if (kindOverrideReasonRaw) {
      var stamp = new Date().toISOString().slice(0, 10);
      var marker = '[kind-override ' + stamp + '] picked=' + (r.kind || '?') + ': ' + kindOverrideReasonRaw;
      var prevNotes = String(r.notes || '').trim();
      rWithOverrideNote = Object.assign({}, r, {
        notes: prevNotes ? (marker + '\n\n' + prevNotes) : marker,
      });
    }
    r = rWithOverrideNote;

    // 2026-06-26 implicit-bill default. The Step 1 redesign no longer
    // requires the bookkeeper to explicitly pick "Bill as fee" for a
    // non-zero-delta category — that's now the implicit default applied
    // here, before the existing cascade helpers fire. We synthesize a
    // 'bill_as_fee' entry for any non-zero-delta category the user
    // didn't explicitly override with 'bill_as_adj' or 'dont_bill', so
    // applyFeeChoicesToItemsFees + applyFeeChoicesToPoAdjustments work
    // unchanged. The audit-trail-required absorption reasons are
    // persisted separately to receipts.fee_absorptions below.
    var feeChoices = Object.assign({}, rawFeeChoices);
    var isVendorKindEarly = (r.kind === 'vendor_deposit' || r.kind === 'vendor_final' || r.kind === 'vendor_full_payment' || r.kind === 'vendor_fee');
    if (isVendorKindEarly && r.poId) {
      var poForFill = (purchaseOrders || []).find(function(x) { return x && x.id === r.poId; });
      // Project heal (2026-07-11, the Mehraban cross-project fix): the
      // linked PO's project is authoritative. OCR can guess the wrong
      // project for a vendor who sells into several (Mehraban rugs on
      // both Hoback and Moskito), and receipts auto-linked at extraction
      // never pass through setPo's sync. Correct it HERE, before any
      // side effect, so the approved receipt row AND the vendor_payments
      // row the cascade writes both land in the right project's ledger.
      if (poForFill && poForFill.project && (r.projectLabel || '') !== poForFill.project) {
        r = Object.assign({}, r, { projectLabel: poForFill.project });
      }
      if (poForFill) {
        var poItemIdsFill = Array.isArray(poForFill.items) ? poForFill.items : (Array.isArray(poForFill.item_ids) ? poForFill.item_ids : []);
        var poItemsFill = (items || []).filter(function(it) { return it && poItemIdsFill.indexOf(it.id) >= 0; });
        // Fee-confirmation hard gate (2026-07-02, Cimon's directive) —
        // estimated fees must be confirmed-or-edited before approval.
        // The toolbar Approve button is already disabled in this state;
        // this backstop catches any other path into approve(). No
        // owner/admin bypass — confirming takes one click on the Fee
        // details step. Fires before any side effect, so the receipt
        // stays at its existing status.
        var feeGateRes = feeConfirmationGate(r, poItemsFill);
        if (feeGateRes.blocked) {
          window.alert(greeting() + copy.alert.feesUnconfirmed(feeGateRes.missing.map(feeCategoryLabel)));
          return;
        }
        // Hardened synthesis (2026-07-02) — see synthesizeImplicitFeeChoices
        // at module scope: defaults fire only for categories with a POSITIVE
        // entered actual; an empty fee panel can no longer zero estimates.
        feeChoices = synthesizeImplicitFeeChoices(r.feeBreakdown, poItemsFill, rawFeeChoices);
      }
    }

    // Build the receipts.fee_absorptions array from the user's
    // explicit 'dont_bill' choices so the audit trail lands with the
    // approval. Each entry: { category, amount, reason, decided_at }.
    var feeAbsorptionsEntries = [];
    if (isVendorKindEarly && r.poId) {
      Object.keys(feeChoices).forEach(function(cat) {
        if (feeChoices[cat] !== 'dont_bill') return;
        var bd = (r.feeBreakdown && typeof r.feeBreakdown === 'object') ? r.feeBreakdown : {};
        var amt = parseFloat(bd[cat]) || 0;
        feeAbsorptionsEntries.push({
          category:   cat,
          amount:     Math.round(amt * 100) / 100,
          reason:     absorptionReasons[cat] || '',
          decided_at: new Date().toISOString(),
        });
      });
    }

    var approved = Object.assign({}, r, {
      status: 'approved',
      approvedAt: new Date().toISOString(),
      approvedBy: userEmail,
      feeAbsorptions: feeAbsorptionsEntries,
    });
    var isVendorKind = (r.kind === 'vendor_deposit' || r.kind === 'vendor_final' || r.kind === 'vendor_full_payment' || r.kind === 'vendor_fee');

    // Date-paid required gate (2026-06-14, Part A). The receiptDate
    // drives vendor_payments.payment_date via the cascade
    // (utils.js buildVendorPaymentFromReceipt). The cascade now throws
    // if receiptDate is empty (no silent today-fallback), so this gate
    // is the user-facing surface: catch the empty case here with a
    // friendly alert rather than letting the cascade explode. The
    // field defaults to today on upload but is editable and required,
    // so the only way to reach this branch is if the bookkeeper
    // deliberately cleared it.
    if (isVendorKind && (!r.receiptDate || String(r.receiptDate).trim() === '')) {
      window.alert(greeting() + copy.alert.datePaidMissing());
      return;  // receipt stays at its existing status; no cascade fires
    }

    // Duplicate-payment tripwire (non-blocking) — AUDIT_FINANCIAL 2026-06
    // DR-2. If a principal vendor payment already exists for this PO at
    // this amount within a week of the receipt date, this receipt is
    // probably the same real-world wire re-uploaded (delete → re-scan,
    // double upload, etc.). Warn with the existing payment's details;
    // the bookkeeper can proceed when it's genuinely a second payment.
    if (isVendorKind) {
      var dupVp = findPotentialDuplicateVendorPayment(r, vendorPayments);
      if (dupVp) {
        var proceed = window.confirm(
          greeting() + ' — heads up: a vendor payment of ' + fmtMoney(dupVp.amount) +
          ' against this same PO is already on the books' +
          (dupVp.paymentDate ? ' (recorded ' + dupVp.paymentDate + ')' : '') +
          (dupVp.reference ? ', ref "' + dupVp.reference + '"' : '') + '.\n\n' +
          'Approving this receipt will record a SECOND ' + fmtMoney(parseFloat(r.totalAmount) || 0) +
          ' payment. If this is the same wire re-uploaded, cancel and check the PO’s payment history first.\n\n' +
          'Record it anyway?'
        );
        if (!proceed) return;
      }
    }

    // The principal vendor_payments row id, as reported by the cascade that
    // just created it. Threaded into applyFeeChoicesToVendorPaymentItems
    // below instead of re-discovering the row by scanning React state —
    // see the FIXED 2026-09-09 note in that function. Stays '' on the paths
    // that report no id (the idempotent skip on an already-cascaded
    // receipt), where the state scan is the correct fallback because the
    // row pre-dates this render.
    var cascadePaymentId = '';
    if (isVendorKind && applyVendorReceiptSideEffects) {
      var res;
      try {
        res = await applyVendorReceiptSideEffects(approved);
      } catch (e) {
        console.error('vendor receipt cascade threw:', e);
        window.alert(greeting() + copy.alert.approvalCrashed(e && e.message));
        return;  // receipt stays at its existing status
      }

      if (res && res.ok === false) {
        // Cascade aborted before status flip — financial side did not
        // land (or only partially landed). Receipt stays at existing
        // status. Four failure branches with tailored copy:
        //   vendor_mismatch — user-fixable via aliases (open the
        //                     receipt, click VENDOR field, pick or add)
        //   cc_fee_insert   — half-success; principal+children landed,
        //                     CC fee row didn't (retried via idempotency)
        //   principal_rpc / item_lookup / unknown — system-side issue
        //                     (principal RPC failure, missing item in
        //                     catalog, or any other failure_at value)
        if (res.failure_at === 'vendor_mismatch') {
          window.alert(greeting() + copy.alert.vendorMismatch(res.vendorName));
        } else if (res.failure_at === 'cc_fee_insert') {
          window.alert(greeting() + copy.alert.ccFeeInsertFailed(res.reason));
        } else {
          window.alert(greeting() + copy.alert.genericDbError(res.reason));
        }
        return;  // receipt stays at its existing status
      }

      // Cascade succeeded (fully, or with partial item-update failure that
      // still committed the money side). Capture the principal row id it
      // created so the fee-share write can target it directly.
      cascadePaymentId = String((res && res.paymentId) || '');

      if (res && res.ok && res.failure_at === 'item_updates') {
        // Financial side landed. Status flip proceeds. But item-side
        // updates were partial — name the affected items in the warning.
        var failedNames = namesForFailedItems(res.failedItemIds);
        var itemList = failedNames.length > 0 ? failedNames.join(', ') : '(' + res.itemsFailed + ' items)';
        window.alert(greeting() + copy.alert.itemUpdatePartialFailure(res.itemsFailed, itemList));
        // Fall through to saveReceipt — status flip should happen.
      } else if (res && res.ok && !res.skipped && res.itemsUpdated > 0) {
        console.log('Vendor payment cascade: ' + res.itemsUpdated + ' items updated on PO');
      }
    }

    // Has-this-shipped (tracker rebuild item 7). A 'yes' on the vendor-
    // final Confirm step advances the receipt's items to Shipped with
    // the answered ship date — AFTER the money cascade (which lands
    // Ordered), forward-only, never walking back an item already at
    // At Receiver or beyond. Best-effort: a failed ship write never
    // blocks the approval (the Tracker ship queue re-surfaces it).
    if (isVendorKind && r.kind === 'vendor_final'
        && opts && opts.shippedAnswer && opts.shippedAnswer.answer === 'yes' && updateItem) {
      var shipDate = opts.shippedAnswer.date || new Date().toISOString().slice(0, 10);
      var shipTargetIds = (opts.itemAllocations && opts.itemAllocations.length > 0)
        ? opts.itemAllocations.map(function(a) { return a && (a.item_id || a.itemId); }).filter(Boolean)
        : (function() {
            var poShip = (purchaseOrders || []).find(function(x) { return x && x.id === r.poId; });
            return poShip ? (Array.isArray(poShip.items) ? poShip.items : (poShip.item_ids || [])) : [];
          })();
      var shippedIdx = STATUSES.indexOf('Shipped');
      for (var si = 0; si < shipTargetIds.length; si++) {
        var shipIt = (items || []).find(function(x) { return x && x.id === shipTargetIds[si]; });
        if (!shipIt) continue;
        var curIdx = STATUSES.indexOf(canonicalItemStatus(shipIt.status));
        if (curIdx === -1 || curIdx >= shippedIdx || shipIt.status === 'Cancelled') continue;
        try {
          // eslint-disable-next-line no-await-in-loop
          await updateItem(shipIt.id, { status: 'Shipped', shippedDate: shipDate });
        } catch (e) {
          console.error('shipped-answer item update failed for', shipIt.id, e);
        }
      }
    }

    // Apply per-fee verification choices to items.fees + PO adjustment
    // queue BEFORE the status flip so the bookkeeper's intent lands
    // with the same approval action. Three paths per Decision 2:
    //   "Bill as fee" → items.fees updated per Decision 1 (even split)
    //   "Bill as adj." → entry queued on po.pendingFinAdjustments;
    //                    picked up at FIN generation
    //   "Don't bill"   → no DB action (firm absorbs)
    // Only fires for vendor receipts with a linked PO + non-empty feeChoices.
    //
    // 2026-06-13 defensive layer (the install-fee silent-fail incident):
    // both helpers now return summary objects, surfaced as a confirmation
    // pre-approve and a result alert post-approve. Closes the silent-fail
    // class — no more "I picked Bill as fee but items didn't update."
    var feesResult = null;
    var adjResult = null;
    var feeShareResult = null;
    // Phase 5C: the fee_share_* write runs even when feeChoices is
    // empty — categories with no panel choice (i.e., matches-estimate
    // auto-state) still need fee_share populated for the read-path
    // primary lookup. Gate fires when there's a vendor PO + this is a
    // vendor receipt; the helper itself filters categories.
    if (isVendorKind && r.poId) {
      var poForFees = (purchaseOrders || []).find(function(x) { return x && x.id === r.poId; });
      if (poForFees) {
        var feeTargetIds = (opts && Array.isArray(opts.itemAllocations))
          ? opts.itemAllocations.map(function(a) { return a && a.item_id; }).filter(Boolean)
          : null;
        if (Object.keys(feeChoices).length > 0) {
          try { feesResult = await applyFeeChoicesToItemsFees(approved, feeChoices, poForFees, feeTargetIds); }
          catch (e) { console.error('applyFeeChoicesToItemsFees threw:', e); }
          try { adjResult = await applyFeeChoicesToPoAdjustments(approved, feeChoices, poForFees, adjDescs); }
          catch (e) { console.error('applyFeeChoicesToPoAdjustments threw:', e); }
        }
        // Phase 5C cascade — always runs for vendor receipts on a PO
        // so matches-estimate categories still write fee_share = estimate.
        try { feeShareResult = await applyFeeChoicesToVendorPaymentItems(approved, feeChoices, poForFees, cascadePaymentId); }
        catch (e) { console.error('applyFeeChoicesToVendorPaymentItems threw:', e); }
      }
    }

    // Cascade either succeeded, was skipped (non-vendor kind), or had only
    // partial-item failure (financial side OK). Commit the status flip.
    saveReceipt(approved);

    // Surface what actually happened on the fee-verification side so
    // silent fail becomes visible. Only alerts when there's something
    // worth reporting (a Bill-as-fee or Bill-as-adj was picked but
    // didn't land OR did land — both worth confirming).
    // Phase 1 copy refresh (2026-06-27) — plain-language summary lines.
    // Pre-fix these read like raw cascade telemetry ("Updated items.fees
    // on N item(s)", "Wrote fee_share allocations on N vendor_payment_
    // items row(s)") which mentioned internal jargon Cimon flagged as
    // alarming on a successful save. Now the summary only surfaces
    // bookkeeper-relevant facts (X items had their fees adjusted; a fee
    // adjustment was queued for the next final invoice) and routes
    // technical failures via "send Cimon a screenshot" rather than
    // raw error names.
    var summary = [];
    if (feesResult) {
      if (feesResult.itemsUpdated > 0) {
        summary.push('Adjusted fees on ' + feesResult.itemsUpdated + ' item(s) (' + feesResult.categoriesUpdated.join(', ') + ').');
      }
      if (feesResult.itemsFailed > 0) {
        summary.push('Couldn\u2019t adjust fees on ' + feesResult.itemsFailed + ' item(s) — send Cimon a screenshot.');
      }
      if (feesResult.skipped && feesResult.skipped !== 'preconditions' && Object.values(feeChoices).indexOf('bill_as_fee') >= 0) {
        summary.push('Fee adjustment skipped: ' + feesResult.skipped + '.');
      }
    }
    if (adjResult) {
      if (adjResult.adjustmentsQueued > 0) {
        summary.push('Queued ' + adjResult.adjustmentsQueued + ' fee adjustment(s) for the next final invoice on this PO (' + adjResult.categoriesQueued.join(', ') + ').');
      }
      if (adjResult.saveFailed) {
        summary.push('Couldn\u2019t save the queued fee adjustment — send Cimon a screenshot.');
      }
    }
    // Per-item fee allocation write (Phase 5C). Don't surface "wrote N
    // vendor_payment_items rows" — bookkeepers don't think in row counts.
    // Only surface the FAILURE case, since success is the silent default.
    if (feeShareResult) {
      if (feeShareResult.itemsFailed > 0) {
        summary.push('Couldn\u2019t record per-item fee shares on ' + feeShareResult.itemsFailed + ' item(s) — send Cimon a screenshot.');
      }
      // A skip used to print NOTHING anywhere — that silence is what hid
      // the 2026-09-09 defect for months. 'preconditions' stays quiet (it
      // can only fire on a call this approve path structurally cannot
      // make); every other reason is a real "this did not happen".
      if (feeShareResult.skipped && feeShareResult.skipped !== 'preconditions') {
        console.error('applyFeeChoicesToVendorPaymentItems skipped:',
          feeShareResult.skipped, { receiptId: approved && approved.id });
        summary.push(copy.alert.feeShareSkipped(feeShareResult.skipped));
      }
    }
    if (summary.length > 0) {
      window.alert(greeting() + copy.alert.approvedSummary(summary));
    } else if (Object.values(feeChoices).some(function(v) { return v === 'bill_as_fee' || v === 'bill_as_adj'; })) {
      // Bill-as-fee or Bill-as-adj was picked but nothing happened — the
      // silent-fail class. Plain-language explanation surfaced via the
      // copy module.
      window.alert(greeting() + copy.alert.billAsFeeNoOp());
    }
  }
  function reject(r, reason) {
    saveReceipt(Object.assign({}, r, { status: 'rejected', rejectedAt: new Date().toISOString(), rejectedReason: reason || '' }));
  }
  function unreject(r) {
    saveReceipt(Object.assign({}, r, { status: 'pending', rejectedAt: '', rejectedReason: '' }));
  }
  // Absorb (2026-08-23 billing simplification): the firm eats a small
  // reimbursable cost instead of billing it (samples etc. — itemized
  // small charges read as nickel-and-diming). The receipt stays
  // approved — a real firm cost, visible internally — but never bills:
  // excluded from the billing-summary picker and from both cover/draw
  // sum paths (utils.receiptIsAbsorbed). The action is gated in the UI
  // to receipts never tagged onto a sent summary, so no historical
  // statement can reinterpret.
  function absorb(r) {
    saveReceipt(Object.assign({}, r, { absorbedAt: new Date().toISOString(), absorbedBy: userEmail }));
  }
  function restoreBillable(r) {
    saveReceipt(Object.assign({}, r, { absorbedAt: '', absorbedBy: '' }));
  }

  // Replay the vendor-receipt cascade on an already-approved receipt.
  // The cascade only fires at the moment of Approve; if cc_fee or other
  // receipt fields are edited post-approval (now possible via the
  // canManage-extended edit gate from 48a614f), the vendor_payments +
  // vendor_payment_items rows still reflect the values at original
  // approval time. This button re-runs the cascade so post-approval
  // edits propagate to the side-effect tables and downstream FIN math.
  //
  // The cascade itself is idempotent — see replayReceiptCascade in
  // AppContext for the full doc. Calling it twice is safe.
  async function replayCascade(r) {
    if (!r || !replayReceiptCascade) return;
    // Date-paid pre-check (2026-06-14, Part A). Legacy receipts approved
    // before the prefill change may carry receiptDate=''; the cascade's
    // throw on empty date would surface here via the generic catch
    // below, but a targeted message lets the bookkeeper fix it directly.
    if (!r.receiptDate || String(r.receiptDate).trim() === '') {
      window.alert(greeting() + copy.alert.refreshDatePaidMissing());
      return;
    }
    var res;
    try {
      res = await replayReceiptCascade(r);
    } catch (e) {
      window.alert(greeting() + copy.alert.refreshCrashed(e && e.message));
      return;
    }
    if (!res || res.ok === false) {
      window.alert(greeting() + copy.alert.refreshIncomplete(
        res && res.reason,
        res && res.failure_at
      ));
      return;
    }
    var parts = [];
    if (res.itemsUpdated) parts.push(res.itemsUpdated + ' item status update(s)');
    if (res.ccFeePaymentId) parts.push('CC fee row written');
    if (res.ccFeeReplay) parts.push(res.ccFeeReplay);
    var summary = parts.length > 0 ? parts.join(' \u00b7 ') : 'no changes needed';
    window.alert(greeting() + copy.alert.refreshComplete(summary));
  }
  // Ref-based snapshot of the receipt currently being edited in the
  // detail modal. The closure-captured rOpen drifts within a single
  // click event when a blur-triggered patch fires synchronously before
  // a button click handler — both run against the SAME render's
  // closure, so the click handler sees the pre-blur receipt even though
  // patch already started a save with the post-blur value. The fix:
  // every render of the open modal AND every patch update this ref
  // synchronously. Action handlers (approve/reject/etc.) read from the
  // ref instead of the closure.
  //
  // Concrete bug shape (2026-06-07): user types CC fee, clicks Approve.
  // Blur fires patch → saveReceipt(merged{ccFee:100}) → DB upsert
  // pending. Click fires approve(stale rOpen{ccFee:0}) → cascade reads
  // 0 (no Fee row written) and a second saveReceipt({...stale,
  // status:'approved'}) overwrites cc_fee back to 0 in the DB. The ref
  // fix makes approve read {ccFee:100}.
  var rOpenRef = React.useRef(null);
  // changes may be a plain object OR a function of the CURRENT receipt
  // (post-any-blur-patch, via the ref) returning the changes object.
  // The functional form exists for updates derived from fields that a
  // blur in the same click event may have just patched — e.g. "Confirm
  // fees" merging into feeBreakdown (2026-07-02). Same bug shape as the
  // CC-fee race described above.
  function patch(r, changes) {
    var current = rOpenRef.current || r;
    var resolved = (typeof changes === 'function') ? changes(current) : changes;
    var merged = Object.assign({}, current, resolved);
    rOpenRef.current = merged;
    saveReceipt(merged);
  }

  // ── Detail modal ──
  if (detailId) {
    var rOpen = receipts.find(function(x) { return x.id === detailId; });
    if (!rOpen) return null;
    // Sync the ref on every render so action handlers (approve/reject/
    // unreject/push/etc.) always read the latest receipt — including
    // any patch that fired during the same click event as the action.
    // Without this, the within-event blur-then-click race overwrites
    // recently-typed field values (the 2026-06-07 CC-fee-not-capturing
    // bug). The ref is also updated synchronously inside patch().
    rOpenRef.current = rOpen;
    return (
      <>
        <ReceiptDetail
          receipt={rOpen}
          purchaseOrders={purchaseOrders}
          projects={projects}
          vendors={vendors}
          setVendors={ctx.setVendors}
          items={items}
          vendorPayments={vendorPayments}
          vendorPaymentItems={vendorPaymentItems}
          vendorCreditLedger={ctx.vendorCreditLedger || []}
          onCorrectPayment={ctx.correctRecordedPayment}
          onAddVendor={addVendorInline}
          firmCreditCards={ctx.firmCreditCards || []}
          firmSettings={firmSettings}
          userEmail={userEmail}
          /* Phase 4B (2026-06-27) — multi-file attachments. Pass the
             attachment collection + the upload/update/delete helpers
             through to the detail modal so it can render the secondary
             attachments list + the "+ Add another file" affordance. */
          receiptAttachments={ctx.receiptAttachments || []}
          onAddReceiptAttachment={ctx.addReceiptAttachment}
          onUpdateReceiptAttachmentRole={ctx.updateReceiptAttachmentRole}
          onDeleteReceiptAttachment={ctx.deleteReceiptAttachment}
          // Edit gate opens to BOTH owner and admin post-approval
          // (2026-06-07 fix). Pre-fix, only the owner could edit
          // approved receipts; admin was locked out, which broke the
          // "go back and add a missing CC fee or payment source"
          // workflow because Cimon (admin role on some receipts) and
          // any other admin couldn't fix staff-uploaded receipts. Staff
          // still can't edit approved receipts — pending/needs_link
          // remains open to everyone, approved opens only to canManage.
          canEditLink={rOpen.status === 'pending' || rOpen.status === 'needs_link' || canManage}
          canApprove={canManage}
          // canManage is passed through so the card picker inside the
          // detail can show ALL active cards (not just the
          // user-visibility-filtered subset) when the editor is admin
          // or owner. Lets admin assign Quinn's private card to her
          // own receipt when she forgot to set it at upload time.
          canManage={canManage}
          canBill={false}
          // Direct item writes from the wizard (lead weeks on the Which
          // items step) — audited via change_history like any item edit.
          updateItem={updateItem}
          alreadyBilled={!!rOpen.billedOnInvoiceId}
          onClose={function() { setDetailId(null); }}
          // Action handlers read from the ref, not the closure-captured
          // rOpen, so any blur-triggered patch within the same click
          // event has its merged value visible here. See rOpenRef
          // declaration for the bug shape this defends against.
          onApprove={function(opts) { approve(rOpenRef.current || rOpen, opts); setDetailId(null); }}
          onReject={function(reason) { reject(rOpenRef.current || rOpen, reason); setDetailId(null); }}
          onUnreject={function() { unreject(rOpenRef.current || rOpen); }}
          onAbsorb={function() { absorb(rOpenRef.current || rOpen); }}
          onRestoreBillable={function() { restoreBillable(rOpenRef.current || rOpen); }}
          onBillToClient={function() {}}
          onPatch={function(changes) { patch(rOpenRef.current || rOpen, changes); }}
          onReplay={function() { replayCascade(rOpenRef.current || rOpen); }}
          onDelete={function() {
            // Approved/pushed receipts are not deletable — the cascade has
            // already written a vendor_payments row, and a delete →
            // re-upload cycle would record the same wire twice (the new
            // receipt id defeats the idempotency key). AUDIT_FINANCIAL
            // 2026-06 DR-2; guard logic + copy live in utils.js.
            var guard = receiptDeletionBlocked(rOpenRef.current || rOpen);
            if (guard.blocked) {
              window.alert(guard.reason);
              return;
            }
            if (window.confirm(copy.confirm.deleteReceipt)) {
              deleteReceipt(rOpen.id);
              setDetailId(null);
            }
          }}
        />
      </>
    );
  }

  // ── List view ──
  // RLS gates project_reimbursable and personal-kind receipts to submitter
  // + owner + bookkeeper. Vendor receipts are visible to everyone (procurement
  // operations rely on that). The "All" lens shows everything the current
  // viewer is allowed to see; "My Expenses" narrows to the viewer's own
  // submissions across all kinds.
  var totalCount = (lensFilter === 'mine'
    ? receipts.filter(function(r) { return (r.createdBy || '').toLowerCase() === userEmail; })
    : receipts).length;

  return (
    <div
      style={S.wrap}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}>

      {/* Drop overlay */}
      {isDragging && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 900,
          background: DRAG_OVERLAY_BG,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none',
        }}>
          <div style={{ border: '2px dashed ' + SURFACE, borderRadius: 12, padding: '60px 80px', textAlign: 'center', color: SURFACE, fontFamily: "'DM Sans', sans-serif" }}>
            <div style={{ fontSize: 48, marginBottom: 12, lineHeight: 1 }}>▭</div>
            <div style={{ fontSize: 22, fontWeight: 500, letterSpacing: '-0.01em' }}>{copy.dropzone.dragHeading}</div>
            <div style={{ fontSize: 13, color: LINE_SHELL, marginTop: 8, letterSpacing: '0.04em' }}>{copy.dropzone.dragSubhead}</div>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div style={S.toolbar}>
        <span style={S.pageTitle}>Receipts</span>
        <div style={S.vsep} />
        <button
          style={Object.assign({}, S.tbtnGhost, lensFilter === 'all' ? { background: INK_1, color: SURFACE, borderColor: INK_1 } : {})}
          onClick={function() { setLensFilter('all'); }}>
          All
        </button>
        <button
          style={Object.assign({}, S.tbtnGhost, lensFilter === 'mine' ? { background: INK_1, color: SURFACE, borderColor: INK_1 } : {})}
          onClick={function() { setLensFilter('mine'); }}>
          My Expenses
        </button>
        <div style={{ flex: 1 }} />
        {/* The "+ Upload receipt" toolbar button was removed 2026-06-28
            per Cimon — the compact dropstrip below the filter bar is
            the sole upload affordance (no duplication). The hidden
            file input stays in the DOM so the dropstrip's
            <label htmlFor="receipts-file-input"> still resolves. */}
        {uploading && (
          <span style={{ fontSize: 11, color: INK_3, paddingRight: 8 }}>
            {queue.total > 1 ? 'Reading ' + queue.done + '/' + queue.total + '…' : (uploadMsg || 'Working…')}
          </span>
        )}
        <input
          id="receipts-file-input"
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,.heic,.heif,application/pdf,image/*"
          multiple
          onChange={handleFileSelected}
          style={{ position: 'absolute', width: 1, height: 1, opacity: 0, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0, padding: 0, margin: -1 }} />
      </div>

      {/* Bill Reimbursables project picker + BillToClientModal removed 2026-05-18.
          Receipts now attach to a billing summary draft via the picker on
          Client Invoices → Billing Summary. */}

      {/* Status banners. Bauhaus discipline: white ground, hairline
          border in the alert color, colored text. No tinted fills. */}
      {uploadErr && (
        <div style={{ padding: '9px 14px', background: SURFACE, borderBottom: HAIRLINE_RED, color: ALERT_RED_TEXT, fontSize: 11 }}>
          {uploadErr}
        </div>
      )}
      {uploadSuccess && !uploadErr && (
        <div style={{ padding: '9px 14px', background: SURFACE, borderBottom: '0.5px solid ' + ZONE_GREEN, color: ZONE_GREEN, fontSize: 11 }}>
          ✓ {uploadSuccess}
        </div>
      )}

      {/* ── V9 Landing page (2026-06-28) ─────────────────────────────────
          Replaces the prior split-pane Reader/Inbox layout. Single-column
          full-width list with:
            (1) Filter bar — Kind chips (multi-select), reusing TYPE_FILTERS.
            (2) Compact dropstrip — single-row drop+click upload affordance.
                Replaces the prior big drop card; the dropstrip is the SOLE
                upload affordance (toolbar "+ Upload receipt" button gone).
            (3) Three status sections — Inbox / Approved / Rejected. Each
                with a count and the receipts that fall into that bucket.
          The Reader pane's primer cells + Kind pre-classify segmented are
          gone. Primer copy lives in receiptCopy.js still if a future home
          wants to surface it. Kind pre-classify is replaced by Claude's
          auto-classification (kindHint stays at '' permanently). */}

      {/* Filter bar — Kind chips + Project + Vendor/PO/Amount search + Date range.
          Matches mockup 20.png (2026-06-28). All four controls intersect
          additively in visibleReceipts; defaults are no-op so the bar
          doesn't restrict the list until the bookkeeper opts in. */}
      <div data-testid="receipts-filter-bar"
           style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px 8px', borderBottom: HAIRLINE_ROW, flexWrap: 'wrap' }}>
        {TYPE_FILTERS.map(function(f) {
          var active = typeFilter.has(f.value);
          var count = typeCounts[f.value] || 0;
          return (
            <button key={f.value}
              data-testid={'type-filter-' + f.value}
              data-active={active ? 'true' : 'false'}
              onClick={function() { toggleTypeFilter(f.value); }}
              style={{
                height: 24, padding: '0 11px', borderRadius: 12,
                border: active ? HAIRLINE_THICK_INK : HAIRLINE_SHELL,
                background: active ? INK_1 : SURFACE,
                color: active ? SURFACE : INK_2,
                fontSize: 10, fontWeight: 500, fontFamily: 'inherit',
                cursor: 'pointer', whiteSpace: 'nowrap',
              }}>
              {f.label}{count > 0 ? ' · ' + count : ''}
            </button>
          );
        })}
        <div style={{ width: 8 }} />
        <select data-testid="receipts-project-filter"
                value={projectFilter}
                onChange={function(e) { setProjectFilter(e.target.value); }}
                style={{ height: 24, padding: '0 6px', border: HAIRLINE_SHELL, borderRadius: 12, background: SURFACE, color: INK_2, fontSize: 10, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer' }}>
          <option value="all">All projects</option>
          {projects.map(function(prj) {
            return <option key={prj.id || prj.label} value={prj.label || ''}>{prj.label || prj.displayName || ''}</option>;
          })}
        </select>
        <input data-testid="receipts-search-input"
               type="text"
               value={vendorSearch}
               onChange={function(e) { setVendorSearch(e.target.value); }}
               placeholder="Search vendor, PO, invoice #, amount, date…" autoCorrect="off" autoCapitalize="none" spellCheck={false} autoComplete="off"
               style={{ height: 24, padding: '0 10px', border: HAIRLINE_SHELL, borderRadius: 12, background: SURFACE, color: INK_1, fontSize: 10, fontFamily: 'inherit', minWidth: 200 }} />
        <select data-testid="receipts-date-filter"
                value={dateFilter}
                onChange={function(e) { setDateFilter(e.target.value); }}
                style={{ height: 24, padding: '0 6px', border: HAIRLINE_SHELL, borderRadius: 12, background: SURFACE, color: INK_2, fontSize: 10, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer' }}>
          <option value="any">Any date</option>
          <option value="week">This week</option>
          <option value="month">This month</option>
          <option value="quarter">This quarter</option>
          <option value="year">This year</option>
        </select>
      </div>

      {/* Compact dropstrip — sole upload affordance. Wider visual weight
          than the prior toolbar button + smaller vertical footprint than
          the prior big drop card. Drag-and-drop also works from anywhere
          on the page via the full-screen overlay above.

          2026-06-28: switched from <label htmlFor=…> to <div onClick=…>
          that explicitly calls fileInputRef.current.click(). The label-
          based trigger silently broke when the file input moved to a
          different sub-tree of the DOM (toolbar vs main column); modern
          browsers should still link them but Cimon reported clicks not
          opening the picker. Calling .click() directly is unambiguous
          and avoids any label-resolution quirks. */}
      <div
        data-testid="receipts-dropstrip"
        onClick={function() {
          if (uploading) return;
          if (fileInputRef.current) fileInputRef.current.click();
        }}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14,
          margin: '12px 18px 16px',
          padding: '14px 20px',
          // Solid fuchsia border (2026-06-29 per Cimon) — the OCR
          // dropstrip is the receipts page's primary call-to-action
          // affordance, so it earns the ZONE_FUCHSIA accent. Prior
          // dashed-gray treatment read as quiet metadata; solid
          // fuchsia matches the firm's project-related cue
          // (Reimbursable receipt rows, Kind cards, etc.) and reads
          // as "this is the active upload target."
          border: '1.5px solid ' + ZONE_FUCHSIA,
          borderRadius: 3,
          background: uploading ? GROUND_OFF : SURFACE,
          cursor: uploading ? 'default' : 'pointer',
          userSelect: 'none',
          transition: 'border-color 0.15s, background 0.15s',
        }}
        onMouseEnter={function(e) {
          if (uploading) return;
          e.currentTarget.style.borderColor = INK_1;
        }}
        onMouseLeave={function(e) {
          if (uploading) return;
          e.currentTarget.style.borderColor = ZONE_FUCHSIA;
        }}>
        <span style={{ fontSize: 16, lineHeight: 1, color: INK_2 }}>▭</span>
        <span style={{ fontSize: 13, fontWeight: 500, color: INK_1, letterSpacing: '-0.005em' }}>
          {uploading
            ? (queue.total > 1 ? 'Reading ' + queue.done + ' of ' + queue.total + '…' : (uploadMsg || 'Reading…'))
            : <>Drop a receipt here<span style={{ color: INK_3, fontWeight: 400, margin: '0 5px' }}>or</span><span style={{ textDecoration: 'underline', textUnderlineOffset: 3 }}>click to choose</span></>}
        </span>
        <span style={{ width: 1, height: 16, background: LINE_CONTAINER }} />
        <span style={{ fontSize: 11, color: INK_3 }}>JPG, PNG, or PDF · multiple files accepted</span>
      </div>
      {/* No-document path — cash tips, meters, anything with no paper.
          Quiet text link under the dropstrip; the dropstrip stays the
          primary affordance. */}
      <div style={{ margin: '-8px 18px 14px', textAlign: 'center', fontSize: 11, color: INK_3 }}>
        No receipt to scan?{' '}
        <button type="button"
          data-testid="receipts-record-without-document"
          disabled={uploading}
          onClick={createManualReceipt}
          style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', fontSize: 11, color: INK_1, textDecoration: 'underline', textUnderlineOffset: 3, cursor: uploading ? 'default' : 'pointer' }}>
          Record a cash or manual expense
        </button>
      </div>

      {/* Status sections — Inbox / Approved / Rejected.
          Each section groups receipts by status from visibleReceipts
          (already lens + type filtered). Sections render even when empty
          so the bookkeeper always knows where things would land; the
          (N) count + an italic "empty" line keep empty sections honest
          without growing them. */}
      {(function() {
        // Group visibleReceipts by status bucket.
        function bucketFor(status) {
          if (status === 'pending' || status === 'needs_link') return 'inbox';
          if (status === 'approved' || status === 'pushed')    return 'approved';
          if (status === 'rejected' || status === 'push_failed') return 'rejected';
          return 'inbox';  // defensive fallback for unknown statuses
        }
        var buckets = { inbox: [], approved: [], rejected: [] };
        visibleReceipts.forEach(function(r) { buckets[bucketFor(r.status)].push(r); });

        var sections = [
          { key: 'inbox',    name: 'Inbox',    hint: 'Needs review',  rows: buckets.inbox    },
          { key: 'approved', name: 'Approved', hint: 'Recorded',      rows: buckets.approved },
          { key: 'rejected', name: 'Rejected', hint: 'Out of system', rows: buckets.rejected },
        ];

        // Collapse long sections to the first SECTION_LIMIT rows by
        // default with a "Show all N →" expander. Inbox always shows
        // everything (these need triage). Approved + Rejected default
        // to collapsed since they're historical.
        var SECTION_LIMIT = 6;
        return sections.map(function(section) {
          var alwaysExpanded = section.key === 'inbox' || String(vendorSearch || '').trim() !== '';
          var expanded = alwaysExpanded || expandedSections.has(section.key);
          var visibleRows = expanded ? section.rows : section.rows.slice(0, SECTION_LIMIT);
          var hasMore = !expanded && section.rows.length > SECTION_LIMIT;

          return (
            <div key={section.key} data-testid={'receipts-section-' + section.key}
                 style={{ borderTop: HAIRLINE_ROW }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, padding: '10px 18px 6px' }}>
                <span style={Object.assign({}, TYPE_EYEBROW, { color: INK_1, fontSize: 11 })}>
                  {section.name}
                </span>
                <span style={{ fontSize: 11, color: INK_3 }}>({section.rows.length})</span>
                <span style={{ fontSize: 10, color: INK_3, marginLeft: 4 }}>{section.hint}</span>
              </div>
              {section.rows.length === 0 ? (
                <div style={{ padding: '12px 18px 16px', color: INK_4, fontSize: 11, fontStyle: 'italic' }}>
                  {section.key === 'inbox' && totalCount === 0
                    ? (lensFilter === 'mine' ? copy.emptyState.inboxNoReceiptsSubmitterOnly : copy.emptyState.inboxNoReceiptsAll)
                    : 'Nothing here.'}
                </div>
              ) : (
                <div>
                  <SectionColHead />
                  {visibleRows.map(function(r) {
                    var po = r.poId ? purchaseOrders.find(function(x) { return x.id === r.poId; }) : null;
                    var card = r.paidWithCardId ? (ctx.firmCreditCards || []).find(function(c) { return c.id === r.paidWithCardId; }) : null;
                    return <ReceiptRow key={r.id} receipt={r} po={po} items={ctx.items || []} card={card} canManage={canManage} onOpen={function() { setDetailId(r.id); }} />;
                  })}
                  {hasMore && (
                    <button
                      data-testid={'receipts-section-show-all-' + section.key}
                      onClick={function() {
                        setExpandedSections(function(prev) {
                          var next = new Set(prev);
                          next.add(section.key);
                          return next;
                        });
                      }}
                      style={{
                        display: 'block', width: '100%',
                        padding: '10px 18px',
                        background: SURFACE, border: 'none',
                        borderBottom: HAIRLINE_ROW,
                        cursor: 'pointer',
                        fontSize: 11, color: INK_2, fontFamily: 'inherit', textAlign: 'left',
                      }}>
                      Show all {section.rows.length}{' \u2192'}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        });
      })()}
    </div>
  );
}

// ── PRIMER CELL ───────────────────────────────────────────────────────────────

// ── RECEIPT ROW (flat list, status as a tag inline) ─────────────────────────

// Shared grid template for receipt rows + the column header.
// Cols: status dot · thumb · Vendor/Kind · Project · PO · Amount · Date · Conf.
// (PO column added 2026-06-28 per Cimon — stacked vendor + po_ref under
// the linked PO; "—" when receipt has no PO link.)
var ROW_GRID = '16px 38px 1.5fr 1fr 1.1fr 110px 70px 64px';

function statusDotColor(status) {
  if (status === 'needs_link')  return ZONE_FUCHSIA;
  if (status === 'pending')     return ZONE_YELLOW;
  if (status === 'approved')    return ZONE_GREEN;
  if (status === 'pushed')      return ZONE_GREEN;
  if (status === 'push_failed') return ZONE_GREEN;
  return ZONE_GREY;  // rejected + fallback
}

// Per-section column header row (v9 mockup): tiny uppercase labels
// aligned to the grid that the receipt rows below use. The 2 leftmost
// cells (status dot + thumb) get empty headers.
function SectionColHead(p) {
  var hdrStyle = Object.assign({}, TYPE_EYEBROW, { fontSize: 9, color: INK_3 });
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: ROW_GRID,
      gap: 14,
      padding: '6px 18px',
      borderBottom: HAIRLINE_ROW,
    }}>
      <span />
      <span />
      <span style={hdrStyle}>Vendor / Kind</span>
      <span style={hdrStyle}>Project</span>
      <span style={hdrStyle}>Items / PO</span>
      <span style={Object.assign({}, hdrStyle, { textAlign: 'right' })}>Amount</span>
      <span style={Object.assign({}, hdrStyle, { textAlign: 'right' })}>Date paid</span>
      <span style={Object.assign({}, hdrStyle, { textAlign: 'right' })}>{p.showConf !== false ? 'Conf.' : ''}</span>
    </div>
  );
}

function ReceiptRow(p) {
  var r = p.receipt;
  var po = p.po;
  var category = kindCategory(r.kind);
  var projectLabel = r.projectLabel || (po && po.po_ref) || '\u2014';
  var dot = statusDotColor(r.status);
  var confText = r.matchConfidence > 0 ? Math.round(r.matchConfidence * 100) + '%' : '';

  return (
    <div
      data-testid="receipt-row"
      data-receipt-id={r.id}
      data-receipt-category={category}
      onClick={p.onOpen}
      style={{
        display: 'grid',
        gridTemplateColumns: ROW_GRID,
        gap: 14,
        padding: '11px 18px',
        borderBottom: HAIRLINE_ROW,
        cursor: 'pointer',
        alignItems: 'center',
        background: SURFACE,
      }}
      onMouseEnter={function(e) { e.currentTarget.style.background = GROUND_OFF; }}
      onMouseLeave={function(e) { e.currentTarget.style.background = SURFACE; }}>

      {/* Status dot — color signals workflow position */}
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: dot }} />

      {/* Thumb — placeholder for now; future could surface the file
          thumbnail when one exists on Storage. */}
      <span style={{ width: 28, height: 28, border: HAIRLINE, borderRadius: 3, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: INK_4, fontSize: 12 }}>▭</span>

      {/* Vendor / Kind — vendor bold, kind label as small uppercase eyebrow */}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 500, color: INK_1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {r.vendorName || '(unknown vendor)'}
        </div>
        <div style={Object.assign({}, TYPE_EYEBROW, { fontSize: 9, color: INK_3, marginTop: 2 })}>
          {kindLabelFor(r.kind, p.canManage)}{r.absorbedAt ? ' · absorbed' : ''}
        </div>
      </div>

      {/* Project — project_label OR PO ref OR — */}
      <div style={{ fontSize: 11, color: INK_2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {projectLabel}
      </div>

      {/* Items / PO — line 1 lists the item name(s) on the linked PO so
          the bookkeeper sees what was actually being paid for at a
          glance. Line 2 is the PO ref. Multiple items truncate to
          "Item A, Item B (+3 more)". When the receipt has no PO link,
          the cell shows an em-dash. (Updated 2026-06-28 per Cimon —
          prior version showed the PO's vendor name, which duplicated
          the Vendor/Kind column.) */}
      <div style={{ minWidth: 0 }}>
        {po ? (function() {
          var itemIds = Array.isArray(po.items) ? po.items : (po.item_ids || []);
          var allItems = p.items || [];
          var byId = {};
          allItems.forEach(function(it) { byId[it.id] = it; });
          var names = itemIds
            .map(function(id) { var it = byId[id]; return it ? (it.productName || it.item || '') : ''; })
            .filter(Boolean);
          var label;
          if (names.length === 0) {
            label = '\u2014';
          } else if (names.length <= 2) {
            label = names.join(', ');
          } else {
            label = names.slice(0, 2).join(', ') + ' (+' + (names.length - 2) + ' more)';
          }
          return (
            <>
              <div style={{ fontSize: 11, color: INK_1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                   title={names.join(', ')}>
                {label}
              </div>
              <div style={Object.assign({}, TYPE_EYEBROW, { fontSize: 9, color: INK_3, marginTop: 2 })}>
                {po.po_ref || ''}
              </div>
            </>
          );
        })() : (
          <span style={{ fontSize: 11, color: INK_4 }}>{'\u2014'}</span>
        )}
      </div>

      {/* Amount */}
      <div style={{ fontSize: 12, fontWeight: 500, color: INK_1, textAlign: 'right', whiteSpace: 'nowrap', fontFeatureSettings: TABULAR }}>
        {fmtMoney(r.totalAmount)}
      </div>

      {/* Date */}
      <div style={{ fontSize: 11, color: INK_3, textAlign: 'right', whiteSpace: 'nowrap' }}>
        {fmtDateOnly(r.paidDate || (r.createdAt ? String(r.createdAt).slice(0, 10) : ''))}
      </div>

      {/* Match confidence */}
      <div style={{ fontSize: 11, color: INK_3, textAlign: 'right', whiteSpace: 'nowrap' }}>
        {confText}
      </div>
    </div>
  );
}

// ── DETAIL MODAL ──────────────────────────────────────────────────────────────

// Owner-only in-place correction for an approved receipt's recorded
// payment (2026-07-13). Renders under the kind-lock banner. Two-click
// apply; routes through AppContext.correctRecordedPayment, which
// UPDATEs the existing vendor_payments principal + per-item shares and
// syncs the receipt row — it can never record a second payment.
function CorrectPaymentPanel(props) {
  var r = props.receipt;
  var TYPE_BY_KIND = { vendor_deposit: 'Deposit', vendor_final: 'Final', vendor_full_payment: 'Full' };
  var initialType = TYPE_BY_KIND[r.kind] || 'Deposit';
  // The input is the BANK total (vendor amount + CC fee) — the number on
  // the statement, the one people actually check against. AppContext
  // subtracts the receipt's Fee-type ledger rows to get the principal.
  var recordedFee = Math.round((parseFloat(r.ccFee) || 0) * 100) / 100;
  var recordedBank = Math.round(((parseFloat(r.totalAmount) || 0) + recordedFee) * 100) / 100;
  var amtState = React.useState(String(recordedBank || r.totalAmount || ''));
  var amt = amtState[0], setAmt = amtState[1];
  var typeState = React.useState(initialType);
  var ptype = typeState[0], setPtype = typeState[1];
  var armState = React.useState(false);
  var armed = armState[0], setArmed = armState[1];
  var busyState = React.useState(false);
  var busy = busyState[0], setBusy = busyState[1];

  async function apply() {
    if (busy) return;
    if (!armed) { setArmed(true); return; }
    setBusy(true);
    try {
      var res = await props.onCorrect(r, { amount: amt, paymentType: ptype });
      if (res && res.ok && res.noChange) {
        window.alert('Already matches: ' + fmtMoney(res.principal) + ' vendor' +
          (res.feeTotal > 0 ? ' + ' + fmtMoney(res.feeTotal) + ' CC fee' : '') +
          ' = ' + fmtMoney(res.bankTotal) + ' receipt total. Nothing changed.');
      } else if (res && res.ok) {
        window.alert('Corrected: ' + fmtMoney(res.principal) + ' vendor' +
          (res.feeTotal > 0 ? ' + ' + fmtMoney(res.feeTotal) + ' CC fee' : '') +
          ' = ' + fmtMoney(res.bankTotal) + ' receipt total.');
      } else {
        window.alert('Could not correct the payment: ' + ((res && res.reason) || 'unknown error'));
      }
    } finally {
      setBusy(false);
      setArmed(false);
    }
  }

  return (
    <div data-testid="correct-payment-panel"
         style={{ marginTop: 6, padding: '10px 12px', background: SURFACE,
                  border: HAIRLINE, borderRadius: 4 }}>
      <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.07em',
                    textTransform: 'uppercase', color: INK_3, marginBottom: 8 }}>
        Correct recorded payment
      </div>
      <div style={{ fontSize: 10, color: INK_2, marginBottom: 8 }}>
        {'Recorded: ' + fmtMoney(parseFloat(r.totalAmount) || 0) + ' vendor'}
        {recordedFee > 0 ? ' + ' + fmtMoney(recordedFee) + ' CC fee' : ''}
        {' = '}
        <strong>{fmtMoney(recordedBank)}</strong>
        {' receipt total.'}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ fontSize: 10, color: INK_3 }}>
          Receipt total (vendor + CC fee)
          <input
            type="text" inputMode="decimal" value={amt}
            data-testid="correct-payment-amount"
            onChange={function(e) { setAmt(e.target.value); setArmed(false); }}
            style={{ display: 'block', marginTop: 3, height: 26, width: 130, padding: '0 8px',
                     border: HAIRLINE, borderRadius: 4, fontSize: 11, fontFamily: 'inherit',
                     boxSizing: 'border-box' }}
          />
        </label>
        <label style={{ fontSize: 10, color: INK_3 }}>
          What it was
          <select
            value={ptype}
            data-testid="correct-payment-type"
            onChange={function(e) { setPtype(e.target.value); setArmed(false); }}
            style={{ display: 'block', marginTop: 3, height: 26, fontSize: 11,
                     fontFamily: 'inherit', border: HAIRLINE, borderRadius: 4, background: SURFACE }}>
            <option value="Deposit">Deposit (partial, balance to come)</option>
            <option value="Final">Final (the remaining balance)</option>
            <option value="Full">Full payment (100% up front)</option>
          </select>
        </label>
        <button
          onClick={apply}
          disabled={busy}
          data-testid="correct-payment-apply"
          style={{ height: 26, padding: '0 12px', borderRadius: 4, alignSelf: 'flex-end',
                   border: '0.5px solid ' + (armed ? '#D4007A' : '#D8D7D2'),
                   background: SURFACE, color: armed ? '#D4007A' : '#111111',
                   fontSize: 10, fontWeight: 500, fontFamily: 'inherit',
                   cursor: busy ? 'wait' : 'pointer' }}>
          {busy ? 'Correcting\u2026' : armed ? 'Click again to confirm' : 'Correct payment'}
        </button>
      </div>
      <div style={{ fontSize: 9, color: INK_3, marginTop: 6, lineHeight: 1.5 }}>
        Enter the bank-statement total. The CC fee keeps its own row; the vendor amount
        adjusts. Never records a second payment.
      </div>
    </div>
  );
}

export function ReceiptDetail(p) {
  var r = p.receipt;
  // rejectMode + rejectReason state removed 2026-06-27 — Reject is now
  // a one-click action gated by window.confirm. Receipt.rejectedReason
  // stays on the schema but is always '' for new rejects.
  var fus = useState(null);  var fileUrl = fus[0];      var setFileUrl = fus[1];

  // Signed URLs for additional (secondary) attachments — keyed by
  // attachment id. Fetched once per attachment so the preview pane
  // can stack the primary + each additional file inline in a single
  // scroll container, per Cimon (2026-06-28).
  var aus = useState({});
  var attachmentUrls = aus[0];
  var setAttachmentUrls = aus[1];

  React.useEffect(function() {
    if (!r.storagePath) return;
    supabase.storage.from('receipts').createSignedUrl(r.storagePath, 60 * 10).then(function(res) {
      if (res.data && res.data.signedUrl) setFileUrl(res.data.signedUrl);
    });
  }, [r.storagePath]);

  React.useEffect(function() {
    var attachments = receiptAttachmentsFor(r.id, p.receiptAttachments || [])
      .filter(function(a) { return !a.isPrimary && a.storagePath; });
    attachments.forEach(function(a) {
      // Skip if we already have a URL for this id.
      if (attachmentUrls[a.id]) return;
      supabase.storage.from('receipts').createSignedUrl(a.storagePath, 60 * 10).then(function(res) {
        if (res.data && res.data.signedUrl) {
          setAttachmentUrls(function(prev) {
            var next = Object.assign({}, prev);
            next[a.id] = res.data.signedUrl;
            return next;
          });
        }
      });
    });
    // Dep on r.id + a snapshot of attachment ids so we re-fetch when
    // new attachments are added but don't loop on the state we set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [r.id, (p.receiptAttachments || []).filter(function(a){return a.receiptId===r.id;}).map(function(a){return a.id;}).join(',')]);

  // OCR vendor-name reconciliation. When the receipt arrives, Claude
  // vision extracts a vendor string (e.g. "Casa Design Group"). If it
  // exactly matches a directory entry, treat it as already-selected
  // (VendorPicker shows selected state). If a high-confidence fuzzy
  // match exists, auto-snap the value to the canonical directory name
  // so the picker shows the directory record rather than the raw OCR
  // string. If no confident match, leave the raw value — VendorPicker
  // renders the orphan warning and the bookkeeper picks or adds via
  // the duplicate-check modal. Fires once per receipt.
  //
  // Procurement kinds only. Reimbursable / Office receipts let the OCR
  // string flow through as a free-form label — there is no canonical
  // form to snap to, the directory shouldn't grow Lyft / Starbucks /
  // Amazon, and the downstream QB target (Expense, not Bill) carries
  // no Vendor reference.
  var [ocrAutoMatched, setOcrAutoMatched] = useState(false);
  useEffect(function() {
    if (ocrAutoMatched) return;
    if (!vendorInputUsesDirectory(r.kind)) { setOcrAutoMatched(true); return; }
    if (!r.vendorName) return;
    var vendors = p.vendors || [];
    var exact = vendors.find(function(v) { return v.name === r.vendorName; });
    if (exact) { setOcrAutoMatched(true); return; }
    // Alias check before fuzzy fallback (added 2026-06-28). When OCR
    // returns a known alias for an existing vendor (case-insensitive
    // match on either the canonical name or any vendor.aliases entry),
    // snap to the canonical name. resolveVendorByName lives in utils.js
    // and is already used by the post-pick auto-learn path; pre-fix the
    // OCR-mount effect ignored aliases entirely, so receipts whose
    // vendor matched only via alias were treated as orphans.
    var aliasHit = resolveVendorByName(r.vendorName, vendors);
    if (aliasHit && aliasHit.name && aliasHit.name !== r.vendorName) {
      p.onPatch({ vendorName: aliasHit.name });
      setOcrAutoMatched(true);
      return;
    }
    if (vendors.length === 0) return;
    var fuse = new Fuse(vendors, {
      // Aliases included in the search keys so fuzzy variants of an
      // alias surface alongside fuzzy variants of the primary name.
      // Fuse handles array-valued keys natively (searches each element).
      keys: ['name', 'aliases'], threshold: 0.25, ignoreLocation: true,
      minMatchCharLength: 3, includeScore: true,
    });
    var hit = fuse.search(r.vendorName)[0];
    if (hit && hit.score < 0.2 && hit.item && hit.item.name) {
      p.onPatch({ vendorName: hit.item.name });
    }
    setOcrAutoMatched(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [r.id, p.vendors, r.kind]);

  // PO picker scope (2026-10-21): open POs on one project, vendor
  // matches first. See linkablePosForReceipt in utils.js.
  var poPickState = useState({ project: null, showAll: false });
  var poPick = poPickState[0]; var setPoPick = poPickState[1];
  // Fresh scope each time a different receipt opens in this modal.
  useEffect(function() { setPoPick({ project: null, showAll: false }); }, [r.id]);
  var linkedPoForScope = r.poId ? (p.purchaseOrders || []).find(function(x) { return x && x.id === r.poId; }) : null;
  // Default project: the linked PO's, else the receipt's own label.
  // null = untouched (follow the receipt); '' = user chose All.
  var poPickProject = poPick.project == null
    ? ((linkedPoForScope && linkedPoForScope.project) || r.projectLabel || '')
    : poPick.project;
  var linkablePoEntries = linkablePosForReceipt({
    purchaseOrders: p.purchaseOrders || [],
    items: p.items || [],
    vendorPayments: p.vendorPayments || [],
    vendorPaymentItems: p.vendorPaymentItems || [],
    vendorCreditLedger: p.vendorCreditLedger || [],
    vendors: p.vendors || [],
    vendorName: r.vendorName || '',
    project: poPickProject,
    showAll: poPick.showAll,
    currentPoId: r.poId || '',
  });
  var linkablePOs = linkablePoEntries.map(function(e) { return e.po; });
  var poPickProjects = (function() {
    var seen = {}; var out = [];
    (p.purchaseOrders || []).forEach(function(po) {
      if (po && po.project && !seen[po.project]) { seen[po.project] = true; out.push(po.project); }
    });
    return out.sort();
  })();

  // Per-category fee verification choices (transient — applied at
  // Approve time, not persisted to the receipt for the "Bill as fee"
  // implicit-default case; explicit absorptions persist to
  // receipts.fee_absorptions via the approve cascade). Keys: freight,
  // crating, installation, tariff, custom. Values: 'dont_bill' /
  // 'bill_as_adj' (no 'bill_as_fee' — that's the implicit default
  // applied at cascade time for any non-zero-delta category not
  // explicitly overridden here). 2026-06-26.
  var feeChoicesState = useState({});
  var feeChoices = feeChoicesState[0];
  var setFeeChoices = feeChoicesState[1];
  function setFeeChoice(category, choice) {
    setFeeChoices(function(prev) {
      var next = Object.assign({}, prev);
      if (choice) next[category] = choice;
      else delete next[category];
      return next;
    });
  }
  // 2026-06-26 — per-category extras attached to the explicit overrides.
  //   feeAdjustmentDescriptions[cat]  → user-authored description for
  //     'bill_as_adj' choices. Overrides the auto-generated description
  //     in applyFeeChoicesToPoAdjustments.
  //   feeAbsorptionReasons[cat]       → user-authored reason for
  //     'dont_bill' choices. Persisted to receipts.fee_absorptions on
  //     approve so the firm can audit later.
  //   feeExpansion                    → uncommitted inline form state.
  //     Shape: { category, kind: 'adjustment'|'absorb', draft: '' }.
  //     null when no expansion is open. Continue → Step 2 is gated on
  //     this being null (no half-saved expansions can advance).
  //   step                            → 'edit' | 'review'. Drives the
  //     two-step flow; only meaningful when hasFeeReviewWork is true.
  var feeAdjStateRaw = useState({});
  var feeAdjustmentDescriptions = feeAdjStateRaw[0];
  var setFeeAdjustmentDescriptions = feeAdjStateRaw[1];
  var feeAbsStateRaw = useState({});
  var feeAbsorptionReasons = feeAbsStateRaw[0];
  var setFeeAbsorptionReasons = feeAbsStateRaw[1];
  var feeExpansionState = useState(null);
  var feeExpansion = feeExpansionState[0];
  var setFeeExpansion = feeExpansionState[1];
  // Legacy 'edit' / 'review' step state preserved for compat — kept as a
  // local boolean inside the wizard for the fee-verification two-pass
  // flow. The PRIMARY wizard step is wizardStep (1-N). Phase 3 (2026-06-27).
  var stepState = useState('edit');
  var step = stepState[0];
  var setStep = stepState[1];
  // wizardStep — 1..N, where N is wizardStepsForKind(r.kind).length.
  // Resets to 1 when the receipt changes; clamps to valid range when
  // r.kind changes (a 5-step Vendor → 3-step Office switch could leave
  // an out-of-range step otherwise).
  var wizardStepState = useState(1);
  var wizardStep = wizardStepState[0];
  var setWizardStep = wizardStepState[1];
  // Phase 5A (2026-06-27) — "Which items?" step state. The bookkeeper
  // confirms which PO items are on THIS receipt; defaults to all items.
  // Gets threaded into the cascade as opts.itemAllocations on Approve
  // so the vendor_payment_items children only land on the items the
  // bookkeeper checked (closes Gap 1 from the receipt-to-FIN audit —
  // pre-fix the cascade spread the principal across ALL PO items
  // regardless of which ones actually shipped on this receipt).
  var selectedItemsState = useState(function() { return new Set(); });
  var selectedItemIds = selectedItemsState[0];
  var setSelectedItemIds = selectedItemsState[1];
  // Reset FEE choices when the receipt or its PO link changes — the pool
  // of relevant categories + their deltas can shift entirely with the PO.
  useEffect(function() {
    setFeeChoices({});
    setFeeAdjustmentDescriptions({});
    setFeeAbsorptionReasons({});
    setFeeExpansion(null);
  }, [r.id, r.poId]);
  // Rewind the WIZARD only when a different receipt opens. Pre-2026-07-11
  // this rewind also keyed on r.poId, so picking a PO on the Vendor & PO
  // step punted the bookkeeper back to the Kind step every time (Cimon's
  // report) — the fee-choice reset above is what actually needed the PO
  // dependency, never the step position.
  useEffect(function() {
    setStep('edit');
    setWizardStep(1);
  }, [r.id]);
  // Clamp wizardStep when r.kind changes — a 4-step Vendor receipt
  // switched to a 3-step Office shouldn't be stuck on step 4.
  useEffect(function() {
    var maxStep = wizardStepsForKind(r.kind).length;
    if (wizardStep > maxStep) setWizardStep(maxStep);
  }, [r.kind]);
  // Phase 5A — initialize selectedItemIds. Restore from localStorage
  // when the receipt has a saved selection (a prior open of the same
  // receipt persisted unchecks); otherwise default to all eligible PO
  // items. Cancelled items are excluded from the default — they're
  // terminal and shouldn't be billed against the new receipt. The
  // bookkeeper unchecks items that AREN'T on THIS receipt (e.g. items
  // shipping in a separate batch later).
  //
  // Persistence (2026-06-28 — Cimon reported unchecks losing across
  // modal close/reopen): selection is stored in window.localStorage
  // keyed by receipt id. The companion useEffect below writes the
  // current Set on every change, so reopening the same receipt
  // restores whatever the bookkeeper last picked.
  useEffect(function() {
    if (!r.poId) { setSelectedItemIds(new Set()); return; }
    var po = (p.purchaseOrders || []).find(function(x) { return x && x.id === r.poId; });
    if (!po) { setSelectedItemIds(new Set()); return; }
    var poItemIds = Array.isArray(po.items) ? po.items : (Array.isArray(po.item_ids) ? po.item_ids : []);
    var eligible = poItemIds.filter(function(itId) {
      var it = (p.items || []).find(function(x) { return x && x.id === itId; });
      return it && String(it.status || '').trim() !== 'Cancelled';
    });
    // Try localStorage restore first.
    var storageKey = 'receipts-selected-items-' + r.id;
    try {
      var saved = window.localStorage.getItem(storageKey);
      if (saved) {
        var savedIds = JSON.parse(saved);
        if (Array.isArray(savedIds)) {
          // Intersect saved IDs with the current PO's eligible items
          // — guards against stale entries from a PO that was relinked
          // since the last save, and silently drops items that were
          // since cancelled.
          var eligibleSet = new Set(eligible);
          var restored = savedIds.filter(function(id) { return eligibleSet.has(id); });
          setSelectedItemIds(new Set(restored));
          return;
        }
      }
    } catch (e) { /* localStorage unavailable / JSON parse failed — fall through */ }
    setSelectedItemIds(new Set(eligible));
  }, [r.id, r.poId]);

  // Persist selectedItemIds on every change. Keyed by receipt id so
  // each receipt remembers its own selection independently. Cleared
  // automatically when the parent useEffect's restore path overwrites
  // the Set on receipt switch.
  useEffect(function() {
    if (!r.id) return;
    try {
      var storageKey = 'receipts-selected-items-' + r.id;
      window.localStorage.setItem(storageKey, JSON.stringify(Array.from(selectedItemIds)));
    } catch (e) { /* localStorage write failed — silently skip persistence */ }
  }, [r.id, selectedItemIds]);

  function setKind(kind)      { p.onPatch({ kind: kind }); }
  function setPo(poId) {
    // The linked PO is the AUTHORITATIVE project for procurement
    // receipts (2026-07-11, the Mehraban cross-project fix — the same
    // vendor sells into multiple projects, and the OCR project guess
    // can name the wrong one). Picking a PO snaps projectLabel to the
    // PO's project so a wrong guess can't survive the link.
    var patch = { poId: poId, status: poId ? 'pending' : r.status };
    var po = poId ? (p.purchaseOrders || []).find(function(x) { return x && x.id === poId; }) : null;
    if (po && po.project) patch.projectLabel = po.project;
    p.onPatch(patch);
  }
  function setProject(label)  { p.onPatch({ projectLabel: label }); }
  function setVendorName(v) {
    var originalName = (r.vendorName || '').trim();
    p.onPatch({ vendorName: v });

    // Auto-alias on directory-vendor re-pick: when the user changes
    // vendor_name on a receipt from a value that didn't resolve to a
    // directory vendor → to a value that does, capture the original
    // string as an alias on the picked vendor. Future receipts with the
    // same OCR'd legal name auto-match without manual re-picking
    // (post-2026-05-30 aliases feature).
    if (!vendorInputUsesDirectory(r.kind)) return;
    var picked = (v || '').trim();
    if (!picked || !originalName) return;
    if (picked.toLowerCase() === originalName.toLowerCase()) return;
    var vendors = p.vendors || [];
    var pickedVendor = resolveVendorByName(picked, vendors);
    if (!pickedVendor) return;
    // Skip if the original IS the picked vendor's primary name or
    // already an alias.
    var primaryLc = String(pickedVendor.name || '').toLowerCase();
    var origLc = originalName.toLowerCase();
    if (origLc === primaryLc) return;
    var existingAliases = Array.isArray(pickedVendor.aliases) ? pickedVendor.aliases : [];
    if (existingAliases.some(function(a) { return String(a).toLowerCase() === origLc; })) return;
    // Append the alias and propagate to in-memory state + SQL mirror.
    var updatedVendor = Object.assign({}, pickedVendor, {
      aliases: existingAliases.concat([originalName]),
    });
    if (typeof p.setVendors === 'function') {
      p.setVendors(function(prev) {
        return (prev || []).map(function(vv) {
          return vv.id === pickedVendor.id ? updatedVendor : vv;
        });
      });
    }
    dualWriteVendorRow(updatedVendor);
  }
  function setReceiptDate(v)  { p.onPatch({ receiptDate: v }); }
  function setTotalAmount(v)  { p.onPatch({ totalAmount: parseFloat(v) || 0 }); }
  function setMarkupPct(v)    { var n = parseFloat(v); p.onPatch({ markupPct: isFinite(n) && n >= 0 ? n : 0 }); }
  function setCcFee(v)        { p.onPatch({ ccFee: parseFloat(v) || 0 }); }
  // Per-category fee actual setter. Merges a partial update into the
  // existing feeBreakdown jsonb so we don't drop other categories.
  // Keys: freight, crating, installation, tariff, custom.
  // Functional onPatch form (2026-07-02): reads the CURRENT receipt via
  // the parent's rOpenRef so two same-event fee edits can't drop each
  // other's categories (the CC-fee race shape).
  function setFeeBreakdownCategory(category, v) {
    p.onPatch(function(cur) {
      var current = (cur.feeBreakdown && typeof cur.feeBreakdown === 'object') ? cur.feeBreakdown : {};
      var next = Object.assign({}, current);
      next[category] = parseFloat(v) || 0;
      return { feeBreakdown: next };
    });
  }
  // Explicit per-fee answer (2026-08-23 — Cimon: the panel must ask
  // "is this fee included in this receipt?" in so many words).
  //   Yes → mark presence and, when no amount is saved yet, suggest the
  //         PO's expected number as the STARTING value — clearly labeled
  //         a suggestion in the row; the number must end up matching
  //         what's printed on the paper.
  //   No  → DELETE the category's value (empty = "this receipt says
  //         nothing about that fee" — the 2026-08-10 semantics; nothing
  //         is recorded, no delta, no billing choice) and remember the
  //         explicit answer under feeBreakdown._onReceipt so the panel
  //         can tell "answered No" apart from "not answered yet".
  // _onReceipt is an underscore sibling inside the same jsonb — every
  // consumer (prefill, gate, delta loops, synthesize) iterates the five
  // category keys only, so the flag map is invisible to the machinery.
  function setFeeOnReceipt(category, yes, suggestedEst) {
    p.onPatch(function(cur) {
      var current = (cur.feeBreakdown && typeof cur.feeBreakdown === 'object') ? cur.feeBreakdown : {};
      var next = Object.assign({}, current);
      var flags = Object.assign({}, next._onReceipt || {});
      flags[category] = !!yes;
      next._onReceipt = flags;
      if (yes) {
        if (!feeValueIsPresent(next[category])) {
          next[category] = Math.round((parseFloat(suggestedEst) || 0) * 100) / 100;
        }
      } else {
        delete next[category];
      }
      return { feeBreakdown: next };
    });
  }
  // Bank charge total = totalAmount (vendor principal) + ccFee. When the
  // bookkeeper edits the bank total directly (e.g., transcribing the CC
  // statement number), back-compute the vendor principal so that the
  // canonical model holds: vendor_payments.amount = vendor principal,
  // separate CC fee row carries the processor charge. Surfaced 2026-06-08
  // — the bare "Total amount" field was ambiguous (could mean either).
  function setBankTotal(v) {
    var bank = parseFloat(v) || 0;
    var cc   = parseFloat(r.ccFee) || 0;
    p.onPatch({ totalAmount: Math.max(0, Math.round((bank - cc) * 100) / 100) });
  }
  function setNotes(v)        { p.onPatch({ notes: v }); }
  function setCategory(v)     { p.onPatch({ category: v }); }
  function setPaidWithCardId(v) { p.onPatch({ paidWithCardId: v || '' }); }

  // Cards eligible for the current user + receipt kind. Plus the
  // currently-selected card (if any) so the dropdown still renders the
  // selected option even if it's no longer "eligible" (kind change,
  // deactivation, etc.).
  //
  // Admin/owner (p.canManage) gets the includeAll bypass: they see
  // every active card regardless of visibility, so they can assign a
  // staff member's private card to a staff-uploaded receipt when the
  // staff member forgot to set "Paid with" at upload time. Staff still
  // see only firm-visibility cards + their own private cards.
  var eligibleCards = eligibleCardsForReceipt(
    p.firmCreditCards || [],
    p.userEmail || '',
    r.kind,
    { includeAll: !!p.canManage }
  );
  if (r.paidWithCardId && !eligibleCards.find(function(c) { return c.id === r.paidWithCardId; })) {
    var current = (p.firmCreditCards || []).find(function(c) { return c.id === r.paidWithCardId; });
    if (current) eligibleCards = eligibleCards.concat([current]);
  }

  // Categories for office + project_reimbursable receipts. Forces a choice from
  // the list; "Custom" reveals a free-text input for one-off cases.
  var CATEGORY_SUGGESTIONS = [
    'Sample', 'Travel', 'Site Visit', 'Storage', 'Software', 'Office Supplies',
    'Food & Beverage', 'Trade Show', 'Marketing', 'Books / Subscriptions',
    'Shipping / Postage', 'Equipment', 'Other',
  ];

  var cms = useState(function() {
    return !!(r.category && CATEGORY_SUGGESTIONS.indexOf(r.category) === -1);
  });
  var customMode = cms[0]; var setCustomMode = cms[1];

  // Item typeahead for merchandise_direct (2026-08-23) — the item link
  // replaces the PO link for this kind. Search string only; the picked
  // id lives on the receipt row (r.itemId) like every other field.
  var mis = useState('');   var itemSearch = mis[0]; var setItemSearch = mis[1];

  // Has-this-shipped (tracker rebuild item 7). A vendor FINAL receipt is
  // the natural "did it ship?" moment — the vendor is settled, goods
  // usually move. Answered on the Confirm step; 'yes' advances the
  // receipt's items to Shipped at approve time (forward-only). Never
  // auto-marked: money never implies shipping.
  var sas = useState('');   var shippedAnswer = sas[0]; var setShippedAnswer = sas[1];
  var sds = useState(new Date().toISOString().slice(0, 10));
  var shipDateAns = sds[0]; var setShipDateAns = sds[1];
  var canEditByRole = p.canEditLink;
  var isLocked = r.status === 'approved' || r.status === 'pushed';
  // Edit-mode gate for approved/pushed receipts (2026-06-28 — Cimon:
  // 'see all the info, but you won't necessarily go edit it without
  // a thought'). Owner/admin can edit approved receipts, but only
  // after deliberately clicking Edit on the lock banner. Defaults to
  // false on every receipt mount so reopening doesn't carry over the
  // prior session's edit-mode flag. The useEffect below resets when
  // the modal switches to a different receipt.
  var editModeState = useState(false);
  var editMode = editModeState[0];
  var setEditMode = editModeState[1];
  useEffect(function() { setEditMode(false); }, [r.id]);
  // canEdit = role permission AND (not locked OR explicit edit mode).
  // Form inputs across the modal read this; status-changing actions
  // (Approve / Reject / Delete buttons) still gate on isLocked +
  // canApprove independently.
  var canEdit = canEditByRole && (!isLocked || editMode);

  // Kind auto-detection from running-tab math (2026-06-10). Computes
  // the canonical kind for THIS payment given prior payments on the PO.
  // Used as a UI hint near the Kind dropdown + the Approve hard guard
  // against the Hewn-style double-count bug.
  var poForKindCheck = r.poId ? (p.purchaseOrders || []).find(function(x) { return x && x.id === r.poId; }) : null;
  var detectedKind = (function() {
    if (!poForKindCheck) return null;
    var poTotal = poSubtotalLive(poForKindCheck, p.items || []);
    var paidBefore = computePoPaidBefore(p.vendorPayments || [], poForKindCheck.id, r.id);
    var thisAmount = parseFloat(r.totalAmount) || 0;
    return detectKindFromContext(paidBefore, thisAmount, poTotal);
  })();
  var destructiveKindMsg = (function() {
    if (!poForKindCheck) return null;
    var paidBefore = computePoPaidBefore(p.vendorPayments || [], poForKindCheck.id, r.id);
    return kindIsDestructive(r.kind, paidBefore);
  })();

  // 2026-06-26 — principal-mis-tag guard (the 26HOB-PO-020 class).
  // Fires when the bookkeeper picked a non-principal kind (vendor_fee)
  // for a receipt that strongly LOOKS like a principal payment. Without
  // this guard, OCR misclassification flows straight through to a
  // payment_type='Fee' principal row that downstream consumers
  // (computePoPaidBefore, isPoFullyPaid, FIN math) all exclude — the
  // PO appears unpaid even after the vendor has been paid in full.
  //
  // Conditions (all must hold):
  //   • Picked kind is NOT principal (vendor_fee, project_reimbursable,
  //     personal, unknown — anything not in PRINCIPAL_RECEIPT_KINDS).
  //   • detectKindFromContext returns vendor_full_payment OR
  //     vendor_deposit (running-tab math says this looks like principal).
  //   • paidBefore == 0 (no prior principal payments on the PO; this
  //     check distinguishes a legitimate fee bill landing later in the
  //     PO's life from the first-and-only payment that's been
  //     mis-tagged).
  //   • r.totalAmount >= 30% of poSubtotalLive. Heuristic threshold
  //     that distinguishes principal payments from genuine fee bills
  //     (a $200 freight charge on a $5,000 PO is 4% — won't trigger;
  //     a $5,000 fabric purchase on a $5,000 PO is 100% — will).
  //
  // Block clears when the bookkeeper either changes kind to the
  // detected suggestion OR types a non-empty override reason. The
  // reason flows into receipt.notes on approve so the rationale stays
  // auditable.
  var PRINCIPAL_RECEIPT_KINDS_LOCAL = ['vendor_deposit', 'vendor_final', 'vendor_full_payment'];
  var principalMisclassMsg = (function() {
    if (!poForKindCheck) return null;
    if (PRINCIPAL_RECEIPT_KINDS_LOCAL.indexOf(r.kind) >= 0) return null;
    if (!detectedKind) return null;
    if (detectedKind !== 'vendor_full_payment' && detectedKind !== 'vendor_deposit') return null;
    var paidBefore = computePoPaidBefore(p.vendorPayments || [], poForKindCheck.id, r.id);
    if (paidBefore > 0.01) return null;
    var poTotal = poSubtotalLive(poForKindCheck, p.items || []);
    if (poTotal <= 0) return null;
    var thisAmount = parseFloat(r.totalAmount) || 0;
    var ratio = thisAmount / poTotal;
    if (ratio < 0.30) return null;
    var suggested = detectedKind === 'vendor_full_payment' ? 'Full payment' : 'Deposit';
    return copy.principalMisclassBanner({
      amount: Math.round(thisAmount),
      ratioPct: Math.round(ratio * 100),
      suggestedLabel: suggested,
    });
  })();

  // Override reason — non-empty value clears the principalMisclassMsg
  // block. Reset to '' when the receipt changes so the field never
  // carries stale text between receipts.
  var kors = useState('');
  var kindOverrideReason = kors[0]; var setKindOverrideReason = kors[1];
  useEffect(function() { setKindOverrideReason(''); }, [r.id, r.kind]);
  var principalMisclassBlocked = !!principalMisclassMsg && !kindOverrideReason.trim();

  // 2026-06-26 — kind-edit lock on cascaded receipts. The financial
  // cascade is one-shot at approval time. Once a vendor_payments row
  // has been written tagged to this receipt's source_receipt_id,
  // editing the kind on the receipt does NOT re-fire the cascade —
  // the vendor_payments row keeps the original kind's payment_type.
  // The existing Replay button is idempotent + additive, not
  // destructive, so it would create a duplicate principal row at the
  // new payment_type rather than re-tagging the existing one.
  //
  // Soft option per the 2026-06-26 review: block kind edits on
  // approved/pushed receipts that already have cascaded vendor_payments
  // rows. The only safe correction path is SQL hand-correction by an
  // admin — surface that explicitly so the bookkeeper doesn't try to
  // reject+re-approve their way out.
  var kindLockedAfterCascade = (function() {
    if (!isLocked) return false;
    if (VENDOR_RECEIPT_KINDS.indexOf(r.kind) < 0) return false;
    if (!r.id) return false;
    var refTag = 'RECEIPT:' + r.id;
    return (p.vendorPayments || []).some(function(vp) {
      if (!vp) return false;
      var srcId = vp.sourceReceiptId || vp.source_receipt_id;
      if (srcId && String(srcId) === String(r.id)) return true;
      // Older rows pre-date source_receipt_id — match by reference tag.
      var ref = String(vp.reference || '');
      return ref.indexOf(refTag) >= 0;
    });
  })();

  // Approve gate: when a vendor receipt has per-fee deltas vs items.fees
  // Pre-compute the per-category fee deltas + summary used by both the
  // verification panel (Step 1) and the review screen (Step 2). The
  // 2026-06-26 redesign treats "Bill to client" as the implicit default,
  // so a non-zero delta with NO explicit choice no longer blocks
  // approval — it flows through the cascade as 'bill_as_fee'. The only
  // blocker now is an open-and-uncommitted expansion (the bookkeeper
  // hasn't finished entering an override yet).
  var feeReview = (function() {
    var blank = { hasWork: false, rows: [], po: null, poItems: [] };
    if (!r.poId) return blank;
    var isVendor = r.kind === 'vendor_deposit' || r.kind === 'vendor_final' || r.kind === 'vendor_full_payment' || r.kind === 'vendor_fee';
    if (!isVendor) return blank;
    var po = (p.purchaseOrders || []).find(function(x) { return x && x.id === r.poId; });
    if (!po) return blank;
    var poItemIds = Array.isArray(po.items) ? po.items : (Array.isArray(po.item_ids) ? po.item_ids : []);
    var poItems = (p.items || []).filter(function(it) { return it && poItemIds.indexOf(it.id) >= 0; });
    var bd = (r.feeBreakdown && typeof r.feeBreakdown === 'object') ? r.feeBreakdown : {};
    var cats = [
      { key: 'freight',      label: 'freight' },
      { key: 'crating',      label: 'crating' },
      { key: 'installation', label: 'installation' },
      { key: 'tariff',       label: 'tariff' },
      { key: 'custom',       label: 'custom fees' },
    ];
    function feeSum(category) {
      return poItems.reduce(function(s, it) {
        var fees = it.fees || {};
        if (category === 'custom') {
          return s + (fees.custom || []).reduce(function(t, c) { return t + (parseFloat(c && c.amount) || 0); }, 0);
        }
        return s + (parseFloat(fees[category]) || 0);
      }, 0);
    }
    var rows = [];
    cats.forEach(function(c) {
      // Per-receipt semantics (2026-08-10): an EMPTY category is "not
      // on this receipt" — no delta, no choice, no item writes. Only
      // entered values participate.
      if (!feeValueIsPresent(bd[c.key])) return;
      var est   = Math.round(feeSum(c.key) * 100) / 100;
      var act   = Math.round((parseFloat(bd[c.key]) || 0) * 100) / 100;
      var delta = Math.round((act - est) * 100) / 100;
      if (Math.abs(delta) >= 0.01) rows.push({ category: c.key, label: c.label, est: est, actual: act, delta: delta });
    });
    // NOTE (2026-07-02): hasWork reflects nonzero DELTAS only — it is a
    // legacy signal for the Step 2 review rows and must NOT be used to
    // hide the fee panel or skip the confirmation. Whether the fee step
    // owes a confirmation is feeStepRequired / feeConfirmationGate
    // (module scope), which fire on nonzero ESTIMATES even when every
    // delta is zero.
    return { hasWork: rows.length > 0, rows: rows, po: po, poItems: poItems };
  })();
  // Fee-confirmation approve gate (2026-07-02, Cimon's directive). When
  // the linked PO's items carry any nonzero fee estimate, Approve stays
  // disabled until fee_breakdown holds a saved value for every estimated
  // category — one click on "Confirm fees" (Fee details step) satisfies
  // it. Pure predicate at module scope; no owner/admin bypass.
  var feeGate = feeConfirmationGate(r, feeReview.poItems || []);
  var feeGateMsg = feeGate.blocked
    ? copy.approveGate.feesUnconfirmed(feeGate.missing.map(feeCategoryLabel))
    : '';
  // Continue (→ Step 2) is disabled while an expansion is open. The
  // bookkeeper must Save or Cancel it first — half-saved overrides
  // can't slip into the review screen.
  var continueBlockedReason = feeExpansion
    ? 'Save or cancel the open ' + (feeExpansion.kind === 'adjustment' ? 'adjustment' : 'absorption') + ' before continuing'
    : '';

  return (
    <div style={{ position: 'fixed', inset: 0, background: SCRIM, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: '95vw', maxWidth: 1400, height: '95vh', background: SURFACE, borderRadius: 6, overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: SHADOW_MODAL, fontFamily: "'DM Sans', sans-serif", fontFeatureSettings: TABULAR }}>

        <div style={S.toolbar}>
          <span style={Object.assign({}, S.pageTitle, { flex: 1 })}>{r.vendorName || 'Untitled receipt'} · {fmtMoney(r.totalAmount)}</span>
          <StatusPill status={r.status} />
          <div style={{ flex: 1 }} />
          {!isLocked && p.canApprove && r.status !== 'rejected' && (
            (function() {
              // Block approve when the receipt is missing context that the
              // downstream cascade needs:
              //  - 'unknown' kind has no QB target and no link to anything,
              //    so approving it just dead-ends a row in 'approved' status.
              //  - vendor_* kinds need a poId so the cascade can write a
              //    vendor_payments row tied to the right PO and advance items.
              //  - project_reimbursable needs a category for the bill-to-
              //    client invoice line item.
              if (r.kind === 'unknown' || !r.kind) {
                return (
                  <button style={Object.assign({}, S.tbtnSuccess, { opacity: 0.45, cursor: 'not-allowed' })}
                    disabled
                    title={copy.approveGate.noKind}>
                    Approve
                  </button>
                );
              }
              var isVendor = r.kind === 'vendor_deposit' || r.kind === 'vendor_final' || r.kind === 'vendor_full_payment' || r.kind === 'vendor_fee';
              if (isVendor && !r.poId) {
                return (
                  <button style={Object.assign({}, S.tbtnSuccess, { opacity: 0.45, cursor: 'not-allowed' })}
                    disabled
                    title={copy.approveGate.noPO}>
                    Approve
                  </button>
                );
              }
              var needsCategory = r.kind === 'project_reimbursable' && !r.category;
              if (needsCategory) {
                return (
                  <button style={Object.assign({}, S.tbtnSuccess, { opacity: 0.45, cursor: 'not-allowed' })}
                    disabled
                    title={copy.approveGate.noCategory}>
                    Approve
                  </button>
                );
              }
              // merchandise_direct needs its item link — the receipt is
              // filed on the item's record; approving unlinked would
              // orphan it (no PO, no summary, nothing to find it by).
              var needsItem = r.kind === 'merchandise_direct' && !r.itemId;
              if (needsItem) {
                return (
                  <button data-testid="receipt-approve-blocked-no-item"
                    style={Object.assign({}, S.tbtnSuccess, { opacity: 0.45, cursor: 'not-allowed' })}
                    disabled
                    title={copy.approveGate.noItem}>
                    {wizardStep < wizardStepsForKind(r.kind).length ? 'Continue →' : 'Approve'}
                  </button>
                );
              }
              if (continueBlockedReason) {
                return (
                  <button style={Object.assign({}, S.tbtnSuccess, { opacity: 0.45, cursor: 'not-allowed' })}
                    disabled
                    title={continueBlockedReason}>
                    {wizardStep < wizardStepsForKind(r.kind).length ? 'Continue \u2192' : 'Approve'}
                  </button>
                );
              }
              // Hard guard: vendor_full_payment on top of existing
              // payments would double-count (Hewn 2026-06-07 bug shape).
              if (destructiveKindMsg) {
                return (
                  <button style={Object.assign({}, S.tbtnSuccess, { opacity: 0.45, cursor: 'not-allowed' })}
                    disabled
                    title={destructiveKindMsg}>
                    {wizardStep < wizardStepsForKind(r.kind).length ? 'Continue \u2192' : 'Approve'}
                  </button>
                );
              }
              // Hard guard (2026-06-26): non-principal kind on a receipt
              // that strongly looks like principal (the 26HOB-PO-020
              // class). Block clears when the bookkeeper either changes
              // kind OR types an override reason — the reason flows
              // into receipt.notes on approve.
              if (principalMisclassBlocked) {
                return (
                  <button data-testid="receipt-approve-blocked-principal-misclass"
                    style={Object.assign({}, S.tbtnSuccess, { opacity: 0.45, cursor: 'not-allowed' })}
                    disabled
                    title={principalMisclassMsg}>
                    {wizardStep < wizardStepsForKind(r.kind).length ? 'Continue \u2192' : 'Approve'}
                  </button>
                );
              }
              // Phase 3 fix (2026-06-27) — the toolbar action button now
              // mirrors the wizard navigation. When wizardStep < lastStep,
              // it shows "Continue →" (advance wizardStep). When the user
              // has reached the final Confirm step (wizardStep === lastStep),
              // it shows "Approve" which fires the cascade with the
              // collected fee choices + override reason.
              //
              // Pre-fix: the toolbar Continue ran the LEGACY two-step
              // flow (setStep('review')), which clashed with the wizard
              // stepper — clicking Continue left wizardStep at 1 and
              // the form pane went blank because step='review' rendered
              // ReceiptReviewView instead. Users got stuck after one
              // click. The legacy 'edit'/'review' step state remains
              // declared (kept for backward-compat referenced sites) but
              // no longer drives navigation.
              var labels = wizardStepsForKind(r.kind);
              var isLastStep = wizardStep >= labels.length;
              if (!isLastStep) {
                return (
                  <button data-testid="receipt-continue-to-review"
                    style={S.tbtnSuccess}
                    onClick={function() { setWizardStep(wizardStep + 1); }}>
                    Continue →
                  </button>
                );
              }
              // Fee-confirmation gate (2026-07-02, Cimon's directive):
              // Approve stays disabled until every estimated fee category
              // has a confirmed value in fee_breakdown ("Confirm fees" on
              // the Fee details step is one click). Checked here — the
              // last step only — so wizard navigation is never blocked.
              // No owner/admin bypass.
              if (feeGate.blocked) {
                return (
                  <button data-testid="receipt-approve-blocked-fees-unconfirmed"
                    style={Object.assign({}, S.tbtnSuccess, { opacity: 0.45, cursor: 'not-allowed' })}
                    disabled
                    title={feeGateMsg}>
                    Approve
                  </button>
                );
              }
              // Final step — fire approve with the collected fee choices
              // PLUS the per-item allocation derived from the Step 3
              // "Which items?" selection. opts.itemAllocations is an
              // array of { item_id, amount_share, cc_fee_share } that
              // the cascade's atomic RPC writes as vendor_payment_items
              // children. When the user checked all PO items (the
              // default), the allocation is net-weighted across them —
              // identical to the cascade's default behavior. When they
              // unchecked some, only the checked items receive shares
              // (closes Gap 1 from the receipt-to-FIN audit).
              return (
                <button data-testid="receipt-approve"
                  style={S.tbtnSuccess}
                  onClick={function() {
                    var itemAllocations = (function() {
                      // Only meaningful for vendor receipts with a PO.
                      var isVendor = (r.kind === 'vendor_deposit' || r.kind === 'vendor_final'
                        || r.kind === 'vendor_full_payment' || r.kind === 'vendor_fee');
                      if (!isVendor || !r.poId) return null;
                      var po = (p.purchaseOrders || []).find(function(x) { return x && x.id === r.poId; });
                      if (!po) return null;
                      var poItemIds = Array.isArray(po.items) ? po.items : (Array.isArray(po.item_ids) ? po.item_ids : []);
                      var checkedItems = (p.items || []).filter(function(it) {
                        return it && poItemIds.indexOf(it.id) >= 0
                          && selectedItemIds.has(it.id)
                          && String(it.status || '').trim() !== 'Cancelled';
                      });
                      // No items checked → fall through to cascade default
                      // (cascade will allocate across all items on the PO).
                      if (checkedItems.length === 0) return null;
                      // All items checked (the default state) → also fall
                      // through to cascade default, since the cascade's
                      // built-in allocateVendorPaymentToItems would produce
                      // the same result and gets tagged 'cascade_net_weighted'.
                      var poItemCount = poItemIds.filter(function(itId) {
                        var it = (p.items || []).find(function(x) { return x && x.id === itId; });
                        return it && String(it.status || '').trim() !== 'Cancelled';
                      }).length;
                      if (checkedItems.length === poItemCount) return null;
                      // Subset checked — compute net-weighted split across
                      // the checked subset. Returns the array shape the
                      // cascade expects.
                      var totalAmount = parseFloat(r.totalAmount) || 0;
                      var ccFee = parseFloat(r.ccFee) || 0;
                      // Phase 5A fix (2026-06-27): the helper reads opts.amount,
                      // not opts.totalAmount. Pre-fix, every amount_share was $0
                      // silently — worse than the cascade default. Caught by
                      // the whichItemsWizardStep.test.js audit fixture's
                      // production-bug guard block.
                      var subset = allocateVendorPaymentToItems({
                        amount: totalAmount,
                        ccFee: ccFee,
                        items: checkedItems,
                        allocationMethod: 'manual_override',
                      });
                      // allocateVendorPaymentToItems returns child rows
                      // with all the cols filled in; we pass through.
                      return subset;
                    })();
                    p.onApprove({
                      feeChoices: feeChoices,
                      feeAdjustmentDescriptions: feeAdjustmentDescriptions,
                      feeAbsorptionReasons: feeAbsorptionReasons,
                      kindOverrideReason: kindOverrideReason.trim() || '',
                      itemAllocations: itemAllocations,
                      allocationMethod: itemAllocations ? 'manual_override' : null,
                      shippedAnswer: (r.kind === 'vendor_final' && shippedAnswer === 'yes')
                        ? { answer: 'yes', date: shipDateAns } : null,
                    });
                  }}>
                  Approve
                </button>
              );
            })()
          )}
          {/* Role-aware completion for non-approvers (2026-07-02,
              Cimon's directive). Staff enter receipts but approval is
              owner/bookkeeper only (canApprove = isOwner ||
              hasAdminAccess). Pre-fix, a staff member reaching the
              final wizard step saw no toolbar button at all — the
              approve-mirror block above is gated on p.canApprove, and
              the footer copy told them to "Use the Approve button in
              the toolbar above," which doesn't exist for them. That's
              a dead end with no way to signal "done."
              This mirrors ONLY the final step: earlier steps already
              navigate fine via the wizard footer's Back/Continue
              (unconditional, below). "Save & close" doesn't change
              receipt.status — every field already autosaves via
              patch() as it's entered, so closing IS the "I'm done"
              signal; the receipt stays pending/needs_link for an
              approver to review. Blocked only while an inline fee
              expansion (adjustment/absorption draft) is open and
              uncommitted — same guard the approver's Continue uses —
              so a half-typed override can't be silently discarded. */}
          {!isLocked && !p.canApprove && r.status !== 'rejected' && (
            (function() {
              var labels = wizardStepsForKind(r.kind);
              var isLastStep = wizardStep >= labels.length;
              if (!isLastStep) return null;
              if (continueBlockedReason) {
                return (
                  <button data-testid="receipt-save-and-close-blocked"
                    style={Object.assign({}, S.tbtnPrimary, { opacity: 0.45, cursor: 'not-allowed' })}
                    disabled
                    title={continueBlockedReason}>
                    {copy.wizardComplete.saveAndCloseButton}
                  </button>
                );
              }
              return (
                <button data-testid="receipt-save-and-close"
                  style={S.tbtnPrimary}
                  onClick={function() {
                    // Blur any focused input first so its pending value
                    // (e.g. a CurrencyInput mid-edit, which only commits
                    // via patch() on blur) flushes before the wizard
                    // closes — nothing pending gets lost.
                    if (document.activeElement && typeof document.activeElement.blur === 'function') {
                      document.activeElement.blur();
                    }
                    p.onClose();
                  }}>
                  {copy.wizardComplete.saveAndCloseButton}
                </button>
              );
            })()
          )}
          {/* Refresh (formerly "Replay cascade") — visible on approved/pushed
              vendor receipts for admin/owner only. Re-runs the side-effect
              cascade so any post-approval field edits (added CC fee, etc.)
              flow through to vendor_payments + vendor_payment_items.
              Idempotent: safe to click multiple times.
              Renamed Replay → Refresh per 2026-06-27 copy audit (bookkeeper
              language). */}
          {(r.status === 'approved' || r.status === 'pushed')
            && p.canApprove
            && (r.kind === 'vendor_deposit' || r.kind === 'vendor_final'
                || r.kind === 'vendor_full_payment' || r.kind === 'vendor_fee')
            && p.onReplay && (
            <button style={S.tbtnGhost} onClick={p.onReplay}
              title={copy.tooltip.refreshCascade}>
              Refresh
            </button>
          )}
          {/* Edit button — 2026-06-28 per Cimon. Lives in the toolbar
              next to Refresh, surfaces only on approved/pushed receipts
              for owner/admin (canEditByRole). Clicking unlocks the form
              fields for deliberate edits; clicking again (now "Done")
              returns to view-only. Read-only viewers (staff) never see
              the button — they get the friendly banner mid-modal
              telling them to flag changes to Cimon. */}
          {isLocked && canEditByRole && (
            !editMode ? (
              <button data-testid="receipt-edit-button"
                style={S.tbtnGhost}
                onClick={function() { setEditMode(true); }}
                title="Unlock fields to edit this approved receipt">
                Edit
              </button>
            ) : (
              <button data-testid="receipt-edit-done-button"
                style={Object.assign({}, S.tbtnGhost, { borderColor: ALERT_AMBER_BORDER, color: ALERT_AMBER_TEXT })}
                onClick={function() { setEditMode(false); }}
                title="Return to view-only">
                Done editing
              </button>
            )
          )}
          {/* "Bill to client" button removed 2026-05-18. Approved reimbursable
              receipts now attach directly to a billing summary draft via the
              picker on Client Invoices → Billing Summary. */}
          {/* Absorb (2026-08-23 billing simplification): the firm eats the
              cost — receipt stays approved (internal cost visibility) but
              leaves the billing-summary picker and cover/draw sums for
              good. Reimbursables only; never once tagged onto a sent
              summary (billedOnSummaryId), so history can't reinterpret. */}
          {r.kind === 'project_reimbursable' && isLocked && canEditByRole
            && !r.billedOnSummaryId && !r.billedOnInvoiceId && !r.absorbedAt && (
            <button data-testid="receipt-absorb-button"
              style={S.tbtnGhost}
              onClick={function() {
                if (window.confirm(copy.confirm.absorbReceipt)) p.onAbsorb();
              }}
              title="Firm absorbs this cost — leaves billing summaries, stays on the books internally">
              Don't bill
            </button>
          )}
          {!!r.absorbedAt && (
            <span data-testid="receipt-absorbed-tag"
              style={{ fontSize: 11, color: '#888880', fontWeight: 500, padding: '0 8px' }}>
              Absorbed — not billed to client
            </span>
          )}
          {!!r.absorbedAt && canEditByRole && (
            <button data-testid="receipt-restore-billable-button"
              style={S.tbtnGhost}
              onClick={p.onRestoreBillable}
              title="Make this receipt billable again — it returns to the billing-summary picker">
              Restore to billable
            </button>
          )}
          {p.alreadyBilled && (
            <span style={{ fontSize: 11, color: ZONE_GREEN, fontWeight: 500, padding: '0 8px' }}>✓ Billed</span>
          )}
          {r.status === 'rejected' && (
            <button style={S.tbtnGhost} onClick={p.onUnreject}>Move back to Inbox</button>
          )}
          {/* One-click Reject (2026-06-27 simplification per copy audit
              row #13). Replaces the prior reason-input flow; the
              window.confirm is the single friction point. The receipt's
              rejectedReason field stays in the schema but is always
              empty for new rejects. */}
          {r.status !== 'rejected' && !isLocked && (
            <button style={S.tbtnDanger} onClick={function() {
              if (window.confirm(copy.confirm.rejectReceipt)) {
                p.onReject('');
              }
            }}>Reject</button>
          )}
          <button style={S.tbtnGhost} onClick={p.onDelete}>Delete</button>
          <button style={S.tbtnGhost} onClick={p.onClose}>Close</button>
        </div>

        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          {/* Left pane: file preview. minWidth:0 lets it shrink below the
              image's natural width — without this a wide receipt scan
              pushes the parent wider, squeezing the right (form) pane
              off-screen. */}
          <div style={{ flex: 1, minWidth: 0, borderRight: HAIRLINE, background: SURFACE, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* RECEIPT PDF eyebrow — makes it explicit that this pane shows
                the actual receipt document the user is reading off of while
                filling out the form on the right. (Phase 1 of UI redesign,
                2026-06-27.) */}
            <div style={Object.assign({}, TYPE_EYEBROW, { padding: '8px 12px 6px', borderBottom: HAIRLINE_ROW })}>
              {r.storagePath ? copy.preview.eyebrow : copy.preview.eyebrowNoFile}
            </div>
            {/* Scroll container: stacks the primary file + every additional
                file inline (Cimon 2026-06-28). Vendor receipts often arrive
                as multiple PDFs (vendor invoice + shipping receipt + payment
                confirmation) — combining them visually here lets the
                bookkeeper read the whole story by scrolling, without
                bouncing between separate previews. */}
            <div data-testid="receipt-preview-scroll" style={{ flex: 1, overflowY: 'auto', minHeight: 0, background: GROUND_OFF, display: 'flex', flexDirection: 'column' }}>
              {/* Primary wrapper stretches to fill the available pane
                  height when there are no additional attachments —
                  the iframe inside also flex:1 so it consumes that
                  height. Result: single-attachment receipts have ONE
                  scrollbar (the iframe's PDF page scroll); no outer
                  container scroll. (2026-06-29 — fixes Cimon's
                  double-scrollbar report. Pre-fix the iframe had a
                  fixed 700px height that was taller than the pane in
                  most viewports, so the outer container also scrolled
                  to show the iframe's bottom.) For multi-attachment,
                  secondary attachments below push total content
                  taller than the pane → outer scroll appears as
                  before. */}
              <div data-testid="receipt-preview-primary" style={{ flex: '1 0 auto', minHeight: 500, display: 'flex', flexDirection: 'column' }}>
                {fileUrl ? (
                  r.mimeType === 'application/pdf'
                    ? <iframe src={fileUrl} style={{ display: 'block', border: 'none', width: '100%', flex: 1, minHeight: 500, background: SURFACE }} title={r.filename || 'Receipt'} />
                    : <img src={fileUrl} alt={r.filename || 'Receipt'} style={{ display: 'block', width: '100%', maxWidth: '100%', padding: 12, boxSizing: 'border-box' }} />
                ) : !r.storagePath ? (
                  <div data-testid="receipt-preview-none" style={{ padding: 40, textAlign: 'center', color: INK_3, fontSize: 12, lineHeight: 1.6 }}>
                    No document on file.<br />
                    Recorded manually — attach a file below if one turns up.
                  </div>
                ) : (
                  <div style={{ padding: 40, textAlign: 'center', color: INK_3, fontSize: 12 }}>
                    Loading preview…
                  </div>
                )}
              </div>
              {/* Additional files stacked below the primary. Each gets a
                  small filename header so the bookkeeper knows the
                  transition between docs while scrolling. */}
              {(function() {
                var secondary = receiptAttachmentsFor(r.id, p.receiptAttachments || [])
                  .filter(function(a) { return !a.isPrimary; });
                return secondary.map(function(a) {
                  var url = attachmentUrls[a.id];
                  var mime = a.mimeType || '';
                  return (
                    <div key={a.id} data-testid={'receipt-preview-attachment-' + a.id} style={{ borderTop: HAIRLINE_ROW }}>
                      <div style={{ padding: '6px 12px', fontSize: 10, color: INK_3, fontFamily: 'monospace', background: SURFACE, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {a.filename}
                      </div>
                      {url ? (
                        mime === 'application/pdf'
                          ? <iframe src={url} style={{ display: 'block', border: 'none', width: '100%', height: 700, background: SURFACE }} title={a.filename || 'Attachment'} />
                          : <img src={url} alt={a.filename || 'Attachment'} style={{ display: 'block', width: '100%', maxWidth: '100%', padding: 12, boxSizing: 'border-box' }} />
                      ) : (
                        <div style={{ padding: 20, textAlign: 'center', fontSize: 11, color: INK_3 }}>Loading…</div>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
            {/* Standalone filename row removed 2026-06-28 — Cimon: the
                primary doc now lives in the ribbon as the first chip
                (filled-dot marker), so the redundant mono filename row
                above the ribbon is noise. */}

            {/* Bottom ribbon — every doc on this receipt, one chip each.
                First chip is the primary file (filled fuchsia dot —
                "this is the receipt itself"), subsequent chips are
                additional attachments (outlined dot — "supporting
                docs"). '+ Add another file' is leftmost. The redundant
                trailing 'N files ▾' pulldown was removed 2026-06-28
                per Cimon — the chips themselves are the nav (click a
                filename to scroll to that file in the preview). Per-
                tile chevron is the role/category picker; the word
                'supporting' (the default role) is hidden so categorized
                files surface their actual role and uncategorized ones
                stay quiet. */}
            {p.onAddReceiptAttachment && (function() {
              var allAttach = receiptAttachmentsFor(r.id, p.receiptAttachments || []);
              var secondary = allAttach.filter(function(a) { return !a.isPrimary; });
              var addFileInput = (
                <input type="file"
                       accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,.heic,.heif,application/pdf,image/*"
                       style={{ display: 'none' }}
                       onChange={async function(e) {
                         var file = e.target.files && e.target.files[0];
                         if (!file) return;
                         e.target.value = '';
                         var res = await p.onAddReceiptAttachment(r.id, file, 'supporting');
                         if (!res || !res.ok) {
                           window.alert(res && res.error ? res.error : 'Upload failed');
                         }
                       }} />
              );

              // Always render the ribbon — the primary chip + (optionally)
              // the + Add chip cover the case of a receipt with no
              // secondary attachments. Read-only viewers still see the
              // primary chip so the receipt's main doc is identified
              // in the chrome.
              if (!r.filename && secondary.length === 0 && !canEdit) return null;

              function truncateName(name, max) {
                var s = String(name || '');
                if (s.length <= max) return s;
                // Preserve the extension when truncating.
                var dot = s.lastIndexOf('.');
                if (dot > 0 && s.length - dot <= 6) {
                  var ext = s.slice(dot);
                  var keep = max - ext.length - 1;
                  if (keep > 3) return s.slice(0, keep) + '\u2026' + ext;
                }
                return s.slice(0, max - 1) + '\u2026';
              }

              function scrollToAttachment(attId) {
                var sel = '[data-testid="receipt-preview-attachment-' + attId + '"]';
                var el = document.querySelector(sel);
                if (el && el.scrollIntoView) {
                  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }

              function scrollToPrimary() {
                var el = document.querySelector('[data-testid="receipt-preview-primary"]');
                if (el && el.scrollIntoView) {
                  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }

              // Per-document download (2026-08-09 — Cimon: single-file
              // receipts saved via the browser's PDF toolbar, but multi-
              // document receipts had no reliable way to save each file).
              // storage.download returns a Blob, so the save works
              // regardless of signed-URL cross-origin rules.
              async function downloadDoc(storagePath, filename) {
                try {
                  var res = await supabase.storage.from('receipts').download(storagePath);
                  if (res.error || !res.data) throw new Error(res.error && res.error.message || 'download failed');
                  var url = URL.createObjectURL(res.data);
                  var a = document.createElement('a');
                  a.href = url; a.download = filename || 'receipt';
                  document.body.appendChild(a); a.click(); document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                } catch (e) {
                  window.alert('Could not download ' + (filename || 'file') + ': ' + (e && e.message ? e.message : e));
                }
              }

              // Tile factory — used for both the primary chip and each
              // secondary chip. Centralizes dot marker + truncated
              // filename + role chevron + × delete so the two render
              // paths can't drift.
              function renderTile(opts) {
                var isPrimary = opts.isPrimary;
                var att = opts.attachment;          // null for primary
                var label = truncateName(opts.filename, 16);
                var role  = att ? att.role : null;
                var roleText = role && role !== 'supporting'
                  ? String(role).replace(/_/g, ' ')
                  : '';
                var titleAttr = opts.filename + (roleText ? ' \u00b7 ' + roleText : '');
                var dotStyle = isPrimary
                  ? { width: 6, height: 6, borderRadius: '50%', background: ZONE_FUCHSIA, flexShrink: 0 }
                  : { width: 6, height: 6, borderRadius: '50%', border: '1px solid ' + INK_4, background: SURFACE, flexShrink: 0, boxSizing: 'border-box' };
                return (
                  <div key={isPrimary ? '__primary__' : att.id}
                       data-testid={isPrimary ? 'receipt-primary-chip' : ('receipt-attachment-' + att.id)}
                       title={titleAttr}
                       style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 4px 3px 7px', border: HAIRLINE, borderRadius: 4, background: SURFACE, fontSize: 10, color: INK_1, flexShrink: 0, maxWidth: 200 }}>
                    <span style={dotStyle} />
                    <span
                      onClick={function() { isPrimary ? scrollToPrimary() : scrollToAttachment(att.id); }}
                      style={{ cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                      {label}
                    </span>
                    {roleText && (
                      <span style={{ fontSize: 9, color: INK_3, whiteSpace: 'nowrap', fontStyle: 'italic' }}>
                        {roleText}
                      </span>
                    )}
                    <button type="button"
                      data-testid={isPrimary ? 'receipt-download-primary' : ('receipt-download-' + att.id)}
                      title={'Download ' + opts.filename}
                      onClick={function(e) { e.stopPropagation(); downloadDoc(isPrimary ? r.storagePath : att.storagePath, opts.filename); }}
                      style={{ border: 'none', background: 'none', color: INK_3, cursor: 'pointer', fontSize: 11, padding: '0 2px', lineHeight: 1 }}>
                      ⤓
                    </button>
                    {!isPrimary && canEdit && p.onUpdateReceiptAttachmentRole && (
                      <select
                        data-testid={'receipt-attachment-role-' + att.id}
                        value={att.role}
                        onChange={function(e) {
                          p.onUpdateReceiptAttachmentRole(att.id, e.target.value);
                        }}
                        title="Categorize this file"
                        style={{ fontSize: 9, width: 14, padding: 0, border: 'none', background: 'transparent', color: INK_3, fontFamily: 'inherit', cursor: 'pointer' }}>
                        {RECEIPT_ATTACHMENT_ROLES.map(function(rl) {
                          return <option key={rl} value={rl}>{rl.replace(/_/g, ' ')}</option>;
                        })}
                      </select>
                    )}
                    {!isPrimary && canEdit && p.onDeleteReceiptAttachment && (
                      <button type="button"
                        data-testid={'receipt-attachment-delete-' + att.id}
                        onClick={async function() {
                          if (!window.confirm('Delete this attached file?')) return;
                          var res = await p.onDeleteReceiptAttachment(att.id);
                          if (!res || !res.ok) {
                            window.alert(res && res.error ? res.error : 'Delete failed');
                          }
                        }}
                        title={'Delete ' + opts.filename}
                        style={{ padding: '0 4px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 12, lineHeight: 1, color: INK_3, fontFamily: 'inherit' }}>
                        ×
                      </button>
                    )}
                  </div>
                );
              }

              return (
                <div data-testid="receipt-attachments-ribbon"
                     style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderTop: HAIRLINE_ROW, background: SURFACE, overflowX: 'auto' }}>
                  {canEdit && (
                    <label data-testid="receipt-add-attachment-label"
                           title="Attach another file to this receipt"
                           style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', border: HAIRLINE, borderRadius: 4, background: SURFACE, cursor: 'pointer', fontSize: 10, color: INK_1, fontFamily: 'inherit', flexShrink: 0, whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: 12, lineHeight: 1 }}>+</span>
                      Add another file
                      {addFileInput}
                    </label>
                  )}
                  {/* Primary chip — the OCR source. Filled fuchsia dot
                      marks 'this is the main doc'. No role select (the
                      primary IS the receipt, not a supporting file).
                      No × delete (orphaning the primary would break the
                      receipt record). */}
                  {r.filename && renderTile({ isPrimary: true, filename: r.filename, attachment: null })}
                  {/* Secondary chips — each additional file. Outline
                      dot, role chevron, × delete. */}
                  {secondary.map(function(a) { return renderTile({ isPrimary: false, filename: a.filename, attachment: a }); })}
                </div>
              );
            })()}
          </div>

          {/* Right pane: edit form. flexShrink:0 reserves the width so a
              huge receipt scan can never collapse the form.
              Phase 5A+ fix (2026-06-28): width bumped 400→480 to fit the
              5-step wizard's longer content; outer pane is flex column
              with NO overflow:auto. The scroll lives on an inner body
              div + the wizard footer is a flex-shrink:0 sibling. Old
              shape had overflow on the outer pane + marginTop:auto on
              the footer, which left content trapped above the visible
              area when the form grew (e.g. PO with 4+ items on Step 3). */}
          <div style={{ width: 480, flexShrink: 0, background: SURFACE, display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 16 }}>
            {/* Phase 3 (2026-06-27) — wizard stepper. Visible at the top
                of the form; shows the user where they are in the flow.
                Step labels come from WIZARD_STEPS_BY_KIND. 2026-06-28:
                gate dropped — stepper renders for view-mode viewers too
                so the bookkeeper can navigate panels read-only. */}
            {(function() {
              var labels = wizardStepsForKind(r.kind);
              var safeStep = Math.min(wizardStep, labels.length);
              return (
                <div data-testid="receipt-wizard-stepper"
                     style={{ marginBottom: 14, padding: '8px 10px', background: SURFACE, border: HAIRLINE, borderRadius: 4 }}>
                  <div style={Object.assign({}, TYPE_EYEBROW)}>
                    Step {safeStep} of {labels.length}
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 500, color: INK_1, marginTop: 2 }}>
                    {labels[safeStep - 1]}
                  </div>
                  <div style={{ display: 'flex', gap: 3, marginTop: 6 }}>
                    {labels.map(function(_label, idx) {
                      return (
                        <div key={idx}
                             style={{ flex: 1, height: 2, background: (idx + 1) <= safeStep ? ZONE_FUCHSIA : LINE_ROW, borderRadius: 1 }} />
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {r.matchConfidence > 0 && (
              // Pertinent info only (Cimon 2026-06-28). The match_reasoning
              // prose from Claude was a 3-5 line italic block of noise the
              // bookkeeper rarely needed; the percentage alone tells them
              // "trust this or not."
              //
              // It lives on r.extractedJson.match_reasoning — NOT on
              // r.notes, which it used to pollute until 2026-09-04 (see the
              // create site). Do not re-seed notes from it: notes is the
              // human's field and is client-facing for reimbursables.
              <div style={{ marginBottom: 14, padding: '8px 10px', background: SURFACE, border: HAIRLINE, borderRadius: 4, fontSize: 11, color: INK_2 }}>
                <strong style={{ color: INK_1, fontWeight: 500 }}>Gilda confidence:</strong> {Math.round(r.matchConfidence * 100)}%
              </div>
            )}

            {step === 'review' && (
              <ReceiptReviewView
                r={r}
                feeReview={feeReview}
                feeChoices={feeChoices}
                feeAdjustmentDescriptions={feeAdjustmentDescriptions}
                feeAbsorptionReasons={feeAbsorptionReasons}
                userEmail={p.userEmail || ''}
                firmSettings={p.firmSettings || {}} />
            )}

            {step === 'edit' && (<>
            {/* Per-step guidance line (2026-08-23 — Cimon: "walk the user
                through each step with simple but very clear language").
                copy.stepGuidance is keyed by kind → wizard step number;
                kinds without entries render nothing. merchandise_direct
                is the first kind with a full set. */}
            {(function() {
              var g = copy.stepGuidance && copy.stepGuidance[r.kind];
              var line = g && g[wizardStep];
              if (!line) return null;
              return (
                <div data-testid="receipt-step-guidance"
                     style={{ marginBottom: 14, padding: '10px 12px', border: HAIRLINE, borderRadius: 4, fontSize: 12, color: INK_2, lineHeight: 1.5 }}>
                  {line}
                </div>
              );
            })()}
            {/* Phase 3 (2026-06-27) — Step 1: Receipt type (Kind cards).
                After picking, user clicks Continue to advance to Step 2.
                Cards collapse from view once wizardStep > 1 so subsequent
                steps aren't visually competing with the card grid. */}
            {wizardStep === 1 && (
            <div style={{ marginBottom: 12 }}>
              <Lbl>What type of receipt is this?</Lbl>
              {/* Phase 2 (2026-06-27) — Kind cards replace the dropdown.
                  Big, deliberate cards force the user to read what each
                  type means before picking. Each card carries a plain
                  description + a typical example. Locked receipts fall
                  back to the read-only dropdown shape.

                  When the receipt is locked (already cascaded), the
                  cards become un-clickable but stay visible so the
                  user sees what was originally picked. The lock banner
                  below explains why edits are blocked. */}
              {canEdit && !kindLockedAfterCascade ? (
                <div data-testid="receipt-kind-cards"
                     style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {KIND_CARDS.map(function(card) {
                    var selected = r.kind === card.value;
                    return (
                      <button
                        key={card.value}
                        type="button"
                        data-testid={'receipt-kind-card-' + card.value}
                        onClick={function() { setKind(card.value); }}
                        style={{
                          textAlign: 'left',
                          padding: '10px 12px',
                          background: SURFACE,
                          // Per Cimon 2026-06-28: selection cue uses the
                          // 1.5px thick-hairline tokens, matching the
                          // mockup. Project-related kinds wear fuchsia,
                          // non-project wear ink. Unselected stays at
                          // 1px HAIRLINE_1PX for container weight.
                          border: selected
                            ? (card.isProject ? HAIRLINE_THICK_FUCHSIA : HAIRLINE_THICK_INK)
                            : HAIRLINE_1PX,
                          borderRadius: 5,
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                          transition: 'border-color 0.1s',
                        }}
                        onMouseEnter={function(e) {
                          if (!selected) e.currentTarget.style.borderColor = INK_3;
                        }}
                        onMouseLeave={function(e) {
                          if (!selected) e.currentTarget.style.borderColor = LINE_CONTAINER;
                        }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                          {selected && (
                            <span style={{ width: 6, height: 6, borderRadius: '50%',
                                           background: card.isProject ? ZONE_FUCHSIA : INK_1,
                                           display: 'inline-block', flexShrink: 0 }} />
                          )}
                          <span style={{ fontSize: 11, fontWeight: 500, color: INK_1 }}>
                            {card.title}
                          </span>
                        </div>
                        <div style={{ fontSize: 10, color: INK_2, marginTop: 3, lineHeight: 1.4 }}>
                          {(!p.canApprove && card.bodyStaff) ? card.bodyStaff : card.body}
                        </div>
                        <div style={{ fontSize: 10, color: INK_3, marginTop: 3, fontStyle: 'italic', lineHeight: 1.4 }}>
                          {card.example}
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                // Locked or read-only — keep the existing select so the
                // user sees the chosen value but can't change it.
                <select data-testid="receipt-kind-select"
                  value={r.kind}
                  disabled
                  style={Object.assign({}, IS, { background: SURFACE })}>
                  {KIND_OPTIONS_FULL.map(function(o) {
                    return <option key={o.value} value={o.value}>{kindOptionLabel(o, p.canApprove)}</option>;
                  })}
                  <option value="unknown">Unknown</option>
                </select>
              )}
              {kindLockedAfterCascade && (
                <div data-testid="receipt-kind-locked-banner"
                     style={{ marginTop: 4, padding: '8px 10px', background: SURFACE,
                              border: HAIRLINE_AMBER, borderRadius: 3,
                              fontSize: 10, color: ALERT_AMBER_TEXT, lineHeight: 1.4 }}>
                  {p.canApprove ? copy.kindLockedOwner : copy.kindLocked}
                </div>
              )}
              {kindLockedAfterCascade && p.canApprove && p.onCorrectPayment && (
                <CorrectPaymentPanel receipt={r} onCorrect={p.onCorrectPayment} />
              )}
              {/* Kind hint — surfaces when the running-tab math suggests
                  a different kind than the user picked. Click to accept.
                  Hard-guard message appears in red when the picked kind
                  would cause a double-count (Hewn case). 2026-06-10. */}
              {destructiveKindMsg && (
                <div style={{ marginTop: 4, padding: '8px 10px', background: SURFACE,
                              border: HAIRLINE_RED, borderRadius: 3,
                              fontSize: 10, color: ALERT_RED_TEXT, lineHeight: 1.4,
                              whiteSpace: 'pre-line' }}>
                  {destructiveKindMsg}
                </div>
              )}
              {!destructiveKindMsg && detectedKind && detectedKind !== r.kind && canEdit && (
                <div style={{ marginTop: 4, fontSize: 10, color: INK_3 }}>
                  Looks like a{' '}
                  <button type="button"
                    onClick={function() { setKind(detectedKind); }}
                    style={{ border:HAIRLINE, background:SURFACE,
                             padding:'2px 8px', borderRadius:3, cursor:'pointer',
                             fontSize:10, fontFamily:'inherit', color:INK_1 }}>
                    {detectedKind.replace(/^vendor_/, '').replace(/_/g, ' ')}
                  </button>
                  {' '}— click to use that instead.
                </div>
              )}
              {/* Principal-mis-tag hard guard (2026-06-26). Shows the
                  explanatory message + an override-reason input. Typing
                  anything non-empty in the field clears principalMisclass
                  Blocked and re-enables Approve. The reason is preserved
                  in receipt.notes on approve so the rationale stays
                  auditable (the bookkeeper's "yes I really mean this is
                  a fee bill, not a principal payment"). */}
              {principalMisclassMsg && canEdit && (
                <div data-testid="receipt-principal-misclass-block"
                     style={{ marginTop: 4, padding: '8px 10px', background: SURFACE,
                              border: HAIRLINE_RED, borderRadius: 3,
                              fontSize: 10, color: ALERT_RED_TEXT, lineHeight: 1.4,
                              whiteSpace: 'pre-line' }}>
                  <div style={{ marginBottom: 6 }}>{principalMisclassMsg}</div>
                  <Lbl>{copy.label.overrideReasonForKind(kindLabelFor(r.kind, p.canApprove) || 'fee')}</Lbl>
                  <input data-testid="receipt-principal-misclass-override-input"
                    type="text"
                    value={kindOverrideReason}
                    onChange={function(e) { setKindOverrideReason(e.target.value); }}
                    placeholder="e.g. Vendor bills freight and duty together, it's a small PO so the freight is a big share"
                    style={Object.assign({}, IS, { fontSize: 10, height: 26, marginTop: 2, background: SURFACE, color: INK_1 })} />
                </div>
              )}
            </div>
            )}
            {/* End of Step 1 (Kind cards). Step 2 (Date paid) is its
                own dedicated step (2026-06-28) — the date drives all
                downstream order-tracking + bookkeeper card-statement
                reconciliation, and burying it in a busy Details step
                encouraged users to accept the default-today silently.
                As its own step the bookkeeper has to look at the
                receipt and confirm the actual payment date. */}

            {wizardStep === 2 && (
              <div data-testid="receipt-step-date-paid">
                {/* Receipt date: focus of "Gilda confidence" on this step.
                    Pre-filled from OCR (ex.receipt_date) at create time
                    when Gilda could read it; blank otherwise. No more
                    today-default — masking OCR failures with today's
                    date silently broke bookkeeper card-statement
                    reconciliation. Receipt date IS the item order date
                    via itemPatchesForReceiptCascade (lead-time clock). */}
                <div style={{ marginBottom: 16, maxWidth: 280 }}>
                  <Lbl>Receipt date <span style={{ color: ZONE_FUCHSIA, fontWeight: 600 }}>*</span></Lbl>
                  <input
                    type="date"
                    value={r.receiptDate || ''}
                    disabled={!canEdit}
                    required
                    onChange={function(e) { setReceiptDate(e.target.value); }}
                    style={Object.assign({}, IS, { fontSize: 14, height: 36 }, !r.receiptDate ? { border: '0.5px solid ' + ZONE_FUCHSIA } : {})} />
                  {(function() {
                    var ocrDate = (r.extractedJson && r.extractedJson.receipt_date) || '';
                    var hasDate = !!r.receiptDate;
                    var fromOcr = hasDate && ocrDate && r.receiptDate === ocrDate;
                    if (!hasDate) {
                      return <div style={{ fontSize: 11, color: ZONE_FUCHSIA, marginTop: 6 }}>Gilda couldn&apos;t read it — type it from the receipt.</div>;
                    }
                    if (fromOcr) {
                      return <div style={{ fontSize: 11, color: INK_3, marginTop: 6 }}>✓ Read from your receipt.</div>;
                    }
                    return <div style={{ fontSize: 11, color: INK_3, marginTop: 6 }}>Confirmed.</div>;
                  })()}
                </div>
                {/* Date paid — the day the money actually moved (2026-08-10;
                    Casa dates every invoice with the ORDER date, so the
                    receipt date alone made August wires read as May).
                    Feeds the vendor-payments ledger and the list's DATE
                    PAID column. */}
                <div style={{ marginBottom: 16, maxWidth: 280 }}>
                  <Lbl>Date paid</Lbl>
                  <input
                    type="date"
                    value={r.paidDate || ''}
                    disabled={!canEdit}
                    data-testid="receipt-paid-date"
                    onChange={function(e) { p.onPatch({ paidDate: e.target.value }); }}
                    style={Object.assign({}, IS, { fontSize: 14, height: 36 })} />
                  <div style={{ fontSize: 11, color: INK_3, marginTop: 6 }}>The day the money moved — not the invoice date.</div>
                </div>
                {/* Date added to Gilda — read-only system field. Not
                    actionable; just user awareness so the bookkeeper
                    can distinguish "when this happened" from "when we
                    recorded it." */}
                <div style={{ maxWidth: 280, fontSize: 10, color: INK_3 }}>
                  <span style={{ textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 500 }}>Date added to Gilda</span>
                  {' \u00b7 '}
                  <span style={{ color: INK_2 }}>{r.createdAt ? fmtDate(r.createdAt) : '—'}</span>
                </div>
              </div>
            )}

            {wizardStep === 3 && (<>

            <div style={{ marginBottom: 12 }} data-testid="receipt-vendor-field" data-kind={r.kind || ''}>
              {/* Procurement kinds (vendor_*): VendorPicker enforces
                  canonical match against the directory because the
                  vendor string drives the QB Bill push and the
                  vendor_payments cascade. Exact OCR hit → pre-selected;
                  high-confidence fuzzy match → auto-canonicalized; no
                  match → orphan warning + add-or-pick modal.
                  Reimbursable / Office: plain text input. The string
                  is informational only (Lyft, Starbucks, Amazon are
                  ad-hoc payees, not directory vendors) and the QB push
                  target is Expense, not Bill — there's no canonical
                  form to enforce. */}
              {vendorInputUsesDirectory(r.kind) ? (
                <VendorPicker
                  value={r.vendorName || ''}
                  onChange={function(v) { setVendorName(v); }}
                  vendors={p.vendors || []}
                  items={p.items || []}
                  purchaseOrders={p.purchaseOrders || []}
                  onAddVendor={p.onAddVendor} />
              ) : (
                <div>
                  <Lbl>Vendor</Lbl>
                  <input
                    type="text"
                    data-testid="receipt-vendor-plain-input"
                    value={r.vendorName || ''}
                    disabled={!canEdit}
                    onChange={function(e) { setVendorName(e.target.value); }}
                    placeholder="e.g. Lyft, Amazon, FedEx Office"
                    style={IS} />
                </div>
              )}
            </div>

            {/* merchandise_direct — link the item (2026-08-23). The link
                only FILES this receipt on the item's record (the item's
                Files & Documents panel derives receipts by itemId) and
                names the project. It changes NO numbers anywhere — the
                receipt records exactly what the paper says (Cimon's
                rule: never add information from POs or items that isn't
                on the receipt), and the client is billed through the
                item's final invoice, never from here. */}
            {r.kind === 'merchandise_direct' && (
              <div style={{ marginBottom: 12 }} data-testid="receipt-item-link">
                <Lbl required>Item this purchase was for</Lbl>
                {(function() {
                  var linked = r.itemId
                    ? (p.items || []).find(function(it) { return it && it.id === r.itemId; })
                    : null;
                  if (r.itemId) {
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', border: HAIRLINE, borderRadius: 4 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 500, color: INK_1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {linked ? (linked.item || linked.productName || '(unnamed item)') : '(item not found — ' + r.itemId + ')'}
                          </div>
                          {linked && (
                            <div style={{ fontSize: 10, color: INK_3, marginTop: 2 }}>
                              {(linked.itemNumber ? linked.itemNumber + ' · ' : '') + (linked.project || '')}
                            </div>
                          )}
                        </div>
                        {canEdit && (
                          <button style={S.tbtnGhost} data-testid="receipt-item-unlink"
                            onClick={function() { p.onPatch({ itemId: '' }); setItemSearch(''); }}>
                            Change
                          </button>
                        )}
                      </div>
                    );
                  }
                  var q = itemSearch.trim().toLowerCase();
                  var matches = !q ? [] : (p.items || []).filter(function(it) {
                    if (!it || it.status === 'Cancelled') return false;
                    var hay = ((it.item || '') + ' ' + (it.productName || '') + ' '
                      + (it.itemNumber || '') + ' ' + (it.project || '')).toLowerCase();
                    return hay.indexOf(q) >= 0;
                  }).slice(0, 8);
                  return (
                    <div>
                      <input
                        data-testid="receipt-item-search"
                        value={itemSearch}
                        disabled={!canEdit}
                        onChange={function(e) { setItemSearch(e.target.value); }}
                        placeholder="Search by item name, SKU, or project…"
                        autoCorrect="off" autoCapitalize="none" spellCheck={false} autoComplete="off"
                        style={IS} />
                      {q && matches.length === 0 && (
                        <div style={{ fontSize: 11, color: INK_3, marginTop: 6, fontStyle: 'italic' }}>
                          No items match. Create the item first (Items page), then come back and link it here.
                        </div>
                      )}
                      {matches.map(function(it) {
                        return (
                          <div key={it.id}
                            data-testid={'receipt-item-option-' + it.id}
                            onClick={function() {
                              if (!canEdit) return;
                              // The item names the project (pointer only —
                              // no amounts, no fees, nothing else copies).
                              p.onPatch({ itemId: it.id, projectLabel: it.project || r.projectLabel || '' });
                              setItemSearch('');
                            }}
                            style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '7px 10px', border: HAIRLINE, borderTop: 'none', cursor: 'pointer', fontSize: 12 }}>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {it.item || it.productName || '(unnamed item)'}
                            </span>
                            <span style={{ color: INK_3, whiteSpace: 'nowrap', fontSize: 11 }}>
                              {(it.itemNumber ? it.itemNumber + ' · ' : '') + (it.project || '')}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
                <div style={{ fontSize: 10, color: INK_3, marginTop: 4 }}>
                  Files this receipt on the item — changes no numbers.
                </div>
              </div>
            )}

            {/* Date paid moved to its own Step 2 (2026-06-28). The
                Vendor invoice amount stays here as a single-column
                block; the prior 2-column grid is no longer needed. */}
            <div style={{ marginBottom: 12 }}>
                <Lbl>
                  {/* Label depends on kind so the bookkeeper knows
                      exactly what this number means. For vendor receipts
                      the canonical model is: vendor_payments.amount =
                      vendor principal (what went to vendor), ccFee
                      stored separately. For office/reimbursable receipts
                      there's no vendor/CC split — total is total. */}
                  {(r.kind === 'vendor_deposit' || r.kind === 'vendor_final' || r.kind === 'vendor_full_payment' || r.kind === 'vendor_fee')
                    ? 'Vendor total (before CC fee)'
                    : 'Total amount'}
                </Lbl>
                <CurrencyInput data-testid="receipt-total-amount" value={r.totalAmount} disabled={!canEdit}
                  onValueChange={function(n) { setTotalAmount(n); }} style={IS} />
                {p.canApprove && r.kind === 'project_reimbursable' && (function() {
                  // Reimbursables are billed at cost + markup. The percent
                  // lives on the receipt (default 15) and is editable here
                  // by admins only — employees never see this block
                  // (canApprove = owner || admin). Show the billed figure so
                  // the approver knows what the client will see.
                  var cost   = parseFloat(r.totalAmount) || 0;
                  var pct    = receiptMarkupPct(r);
                  var billed = applyReimbursableMarkup(cost, pct);
                  return (
                    <div style={{ marginTop: 6 }}>
                      <Lbl>Client markup %</Lbl>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input type="text" inputMode="decimal" data-testid="receipt-markup-pct"
                          key={r.id + ':' + pct} defaultValue={pct} disabled={!canEdit}
                          onBlur={function(e) { if (parseFloat(e.target.value) !== pct) setMarkupPct(e.target.value); }}
                          onKeyDown={function(e) { if (e.key === 'Enter') e.target.blur(); }}
                          style={Object.assign({}, IS, { width: 72 })} />
                        {cost > 0 && (
                          <span style={{ fontSize: 11, color: INK_3 }}>
                            Billed to client: <strong style={{ color: INK_1 }}>{fmtMoney(billed)}</strong>
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })()}
                {(r.kind === 'vendor_deposit' || r.kind === 'vendor_final' || r.kind === 'vendor_full_payment' || r.kind === 'vendor_fee') && (
                  <div style={{ fontSize: 10, color: INK_3, marginTop: 4 }}>
                    Everything on the invoice except a card fee.
                  </div>
                )}
                {r.kind === 'merchandise_direct' && (
                  <div style={{ fontSize: 10, color: INK_3, marginTop: 4 }}>
                    Firm record only — the client is billed via the item's final invoice.
                  </div>
                )}
            </div>

            {/* Paid with — admin/owner sees ALL active cards (across
                every holder) so they can assign the correct payment
                source to staff-uploaded receipts. Staff see firm-
                visibility cards plus their own private cards. Card
                holder name is rendered when the editor is admin/owner
                and the card holder differs from the receipt uploader,
                so admin knows whose card they're picking. */}
            <div style={{ marginBottom: 12 }}>
              <Lbl>Paid with</Lbl>
              <select value={r.paidWithCardId || ''} disabled={!canEdit}
                onChange={function(e) { setPaidWithCardId(e.target.value); }}
                style={Object.assign({}, IS, { background: SURFACE })}>
                <option value="">— Not specified —</option>
                {eligibleCards.map(function(c) {
                  var suffix = c.last4 ? ' ··· ' + c.last4 : '';
                  // Holder hint shows for admin/owner ONLY on restricted-
                  // visibility cards (private or team). For firm-shared
                  // cards the holder-of-record name is operationally
                  // irrelevant — the card belongs to whoever's using it
                  // at the moment — so the parenthetical just adds noise
                  // and (worse) suggests the card is private when it's
                  // actually shared.
                  var holderHint = '';
                  if (p.canManage && c.holderEmail && c.visibility !== 'firm') {
                    var holderName = displayNameFor(c.holderEmail, p.firmSettings || {});
                    holderHint = holderName
                      ? ' (' + holderName + ')'
                      : ' (' + String(c.holderEmail).split('@')[0] + ')';
                  }
                  return <option key={c.id} value={c.id}>{c.label + suffix + holderHint}</option>;
                })}
              </select>
              {eligibleCards.length === 0 && (
                <div style={{ fontSize: 10, color: INK_3, marginTop: 4 }}>
                  No eligible payment methods for this kind. Add one under Firm → Payment Methods.
                </div>
              )}
            </div>

            {(r.kind === 'vendor_deposit' || r.kind === 'vendor_final' || r.kind === 'vendor_full_payment' || r.kind === 'vendor_fee') && (
              <div style={{ marginBottom: 12 }}>
                <Lbl>Project (narrows the PO list)</Lbl>
                <select value={poPickProject} disabled={!canEdit}
                  onChange={function(e) { setPoPick(Object.assign({}, poPick, { project: e.target.value })); }}
                  style={Object.assign({}, IS, { background: SURFACE, marginBottom: 8 })}>
                  <option value="">All projects</option>
                  {poPickProjects.map(function(label) {
                    return <option key={label} value={label}>{label}</option>;
                  })}
                </select>
                <Lbl required>Linked PO{poPick.showAll ? '' : ' (open only)'}</Lbl>
                <select value={r.poId || ''} disabled={!canEdit}
                  onChange={function(e) { setPo(e.target.value); }}
                  style={Object.assign({}, IS, { background: SURFACE })}>
                  <option value="">— Pick a PO —</option>
                  {(function() {
                    // Show poSubtotalLive (product + freight + crating + tariff
                    // + per-item installation) not total_net (product only) so
                    // the bookkeeper sees the TRUE PO total when picking which
                    // PO a receipt attaches to. total_net mislabels the choice
                    // when items carry freight on items.fees (the canonical case).
                    var optFor = function(e) {
                      var po = e.po;
                      return <option key={po.id} value={po.id}>{po.po_ref || '(no ref)'} · {po.vendorName || '(no vendor)'} · {fmtMoney(poSubtotalLive(po, p.items || []))}</option>;
                    };
                    var matches = linkablePoEntries.filter(function(e) { return e.vendorMatch; });
                    var others  = linkablePoEntries.filter(function(e) { return !e.vendorMatch; });
                    if (matches.length === 0) return others.map(optFor);
                    return [
                      <optgroup key="g-match" label={'Vendor matches ' + (r.vendorName || '')}>{matches.map(optFor)}</optgroup>,
                      others.length > 0 ? <optgroup key="g-other" label="Other POs">{others.map(optFor)}</optgroup> : null,
                    ];
                  })()}
                </select>
                {canEdit && (
                  <div style={{ fontSize: 10, color: '#888880', marginTop: 4 }}>
                    {linkablePOs.length} PO{linkablePOs.length === 1 ? '' : 's'} listed ·{' '}
                    <span role="button" onClick={function() { setPoPick(Object.assign({}, poPick, { showAll: !poPick.showAll })); }}
                      style={{ textDecoration: 'underline', cursor: 'pointer' }}>
                      {poPick.showAll ? 'Open POs only' : 'Show paid and complete POs too'}
                    </span>
                  </div>
                )}
                {!r.poId && r.status !== 'rejected' && (
                  <div style={{ fontSize: 10, color: ZONE_FUCHSIA, marginTop: 4 }}>
                    A PO link is required before this receipt can be approved.
                  </div>
                )}
                {(function() {
                  // The project follows the linked PO — say so, and flag
                  // loudly if this receipt still carries a different
                  // project (pre-2026-07-11 rows; approval self-corrects).
                  if (!r.poId) return null;
                  var linkedPo = (p.purchaseOrders || []).find(function(x) { return x && x.id === r.poId; });
                  if (!linkedPo || !linkedPo.project) return null;
                  var mismatch = (r.projectLabel || '') !== linkedPo.project;
                  return (
                    <div data-testid="receipt-po-project-note"
                      style={{ fontSize: 10, color: mismatch ? ALERT_AMBER_TEXT : INK_3, marginTop: 4 }}>
                      {mismatch
                        ? 'This receipt says ' + (r.projectLabel || 'no project') + ' but the linked PO belongs to ' + linkedPo.project + ' — the PO wins; approving corrects it. Re-pick the PO to fix it now.'
                        : 'Project: ' + linkedPo.project + ' — set by the linked PO.'}
                    </div>
                  );
                })()}

                {/* Running-tab summary — shows the PO's running ledger
                    in the context of this receipt. Helps the bookkeeper
                    see whether this receipt closes the PO (badge fires
                    when running tab reaches poSubtotalLive within $0.01).
                    Excludes Fee-type vendor_payments (CC fee rows) and
                    excludes this receipt's own existing principal row
                    (so editing an already-approved receipt doesn't
                    double-count itself). Surfaced 2026-06-08 as part of
                    the airtight vendor-record build. */}
                {r.poId && (function() {
                  var po = (p.purchaseOrders || []).find(function(x) { return x && x.id === r.poId; });
                  if (!po) return null;
                  var poTotal = poSubtotalLive(po, p.items || []);
                  var thisReceiptPrincipal = parseFloat(r.totalAmount) || 0;
                  // Sum prior vendor_payments for this PO via the pure
                  // helper — excludes Fee rows and this receipt's own
                  // principal row (idempotency on edits to approved
                  // receipts). Unit-tested in receiptRunningTabMath.test.js.
                  var paidBefore = computePoPaidBefore(p.vendorPayments || [], po.id, r.id);
                  var paidAfter = paidBefore + thisReceiptPrincipal;
                  var remaining = poTotal - paidAfter;
                  var closesPO = Math.abs(remaining) < 0.01;
                  // Phase 1 (2026-06-27): drop the F7F7F5 tinted fill,
                  // use hairline border on white ground. Label refresh per
                  // copy audit: "vendor principal" → "paying the vendor",
                  // "Paid after" → "Total paid after this receipt",
                  // "Over PO estimate" → "Paid more than expected" (info,
                  // not error — isPoFullyPaid still considers the PO paid).
                  return (
                    <div style={{
                      marginTop: 10, padding: '8px 10px', background: SURFACE,
                      border: HAIRLINE, borderRadius: 4, fontSize: 11,
                      fontFeatureSettings: '"tnum", "lnum"'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                        <span style={{ color: INK_3 }}>{copy.runningTab.poTotal(po.po_ref)}</span>
                        <span style={{ color: INK_1, fontWeight: 500 }}>{fmtMoney(poTotal)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                        <span style={{ color: INK_3 }}>{copy.runningTab.paidBefore}</span>
                        <span style={{ color: INK_1 }}>{fmtMoney(paidBefore)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                        <span style={{ color: INK_3 }}>{copy.runningTab.thisReceipt}</span>
                        <span style={{ color: INK_1 }}>{fmtMoney(thisReceiptPrincipal)}</span>
                      </div>
                      <div style={{ borderTop: HAIRLINE_ROW, marginTop: 4, paddingTop: 4, display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: INK_1, fontWeight: 500 }}>{copy.runningTab.paidAfter}</span>
                        <span style={{ color: INK_1, fontWeight: 500 }}>{fmtMoney(paidAfter)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
                        <span style={{ color: closesPO ? ZONE_GREEN : INK_3 }}>
                          {closesPO ? copy.runningTab.status.closes : (remaining < -0.01 ? copy.runningTab.status.over : copy.runningTab.status.remaining)}
                        </span>
                        <span style={{ color: closesPO ? ZONE_GREEN : INK_3 }}>
                          {fmtMoney(Math.abs(remaining))}
                        </span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {(r.kind === 'vendor_deposit' || r.kind === 'vendor_final' || r.kind === 'vendor_full_payment' || r.kind === 'vendor_fee') && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <Lbl>CC fee</Lbl>
                    <CurrencyInput data-testid="receipt-cc-fee" value={r.ccFee || 0} disabled={!canEdit}
                      onValueChange={function(n) { setCcFee(n); }} style={IS}
                      title={copy.tooltip.ccFee} />
                    <div style={{ fontSize: 10, color: INK_3, marginTop: 4 }}>
                      Any card fee — the vendor’s or our bank’s.
                    </div>
                  </div>
                  <div>
                    {/* Bank charge total — derived from vendor principal + CC fee.
                        Editable both ways: changing this back-computes the
                        vendor principal so the bookkeeper can transcribe
                        directly from a CC statement (which shows the bank total)
                        without doing mental math. The form always stores
                        principal + ccFee; the bank total is display only. */}
                    <Lbl>Receipt total (vendor + CC fee)</Lbl>
                    <CurrencyInput data-testid="receipt-bank-total"
                      value={Math.round(((parseFloat(r.totalAmount) || 0) + (parseFloat(r.ccFee) || 0)) * 100) / 100}
                      disabled={!canEdit}
                      onValueChange={function(n) { setBankTotal(n); }} style={IS}
                      title={copy.tooltip.bankTotal} />
                    <div style={{ fontSize: 10, color: INK_3, marginTop: 4 }}>
                      Matches the card statement. Type either box — the other fills in.
                    </div>
                  </div>
                </div>
              </div>
            )}

            </>)}
            {/* End of Step 2 (Details). Step 3 (Fee details — vendor only)
                + Step lastStep (Confirm) start below. */}

            {/* Per-fee comparison panel — surfaces drift between items.fees
                (the PO estimate) and the vendor invoice actuals so the
                bookkeeper can spot missed fees / underbilling at receipt
                approval time. Informational only in this MVP (2026-06-08):
                bookkeeper acts on the drift via the Item form. Auto-actions
                (Match / Update items.fees / Adjustment) ship in a followup
                pass — see docs/FOLLOWUPS.md "Fee verification panel". */}
            {/* Phase 5A (2026-06-27) — Step 3: "Which items?". Vendor
                receipts only. Shows every non-cancelled item on the
                linked PO as a checklist. Default: all items checked.
                The bookkeeper unchecks items NOT on this shipment
                (e.g. when items ship in batches and this receipt
                covers only some of them). The selection threads into
                opts.itemAllocations on Approve so vendor_payment_items
                children land only on the checked items.

                Closes Gap 1 from the receipt-to-FIN audit — pre-fix
                the cascade spread principal across ALL PO items even
                when only a subset shipped. */}
            {wizardStep === 4 && r.poId && (r.kind === 'vendor_deposit' || r.kind === 'vendor_final' || r.kind === 'vendor_full_payment' || r.kind === 'vendor_fee') && (function() {
              var po = (p.purchaseOrders || []).find(function(x) { return x && x.id === r.poId; });
              if (!po) {
                return (
                  <div style={{ marginBottom: 12, padding: '10px 12px', background: SURFACE, border: HAIRLINE, borderRadius: 4, fontSize: 11, color: INK_3 }}>
                    Link a PO on the previous step first.
                  </div>
                );
              }
              var poItemIds = Array.isArray(po.items) ? po.items : (Array.isArray(po.item_ids) ? po.item_ids : []);
              var poItems = (p.items || []).filter(function(x) { return x && poItemIds.indexOf(x.id) >= 0; });
              var isPrincipalKind = r.kind === 'vendor_deposit' || r.kind === 'vendor_final' || r.kind === 'vendor_full_payment';
              if (poItems.length === 0) {
                return (
                  <div style={{ marginBottom: 12, padding: '10px 12px', background: SURFACE, border: HAIRLINE, borderRadius: 4, fontSize: 11, color: INK_3 }}>
                    This PO has no items in the catalog. Add items to the PO before approving this receipt.
                  </div>
                );
              }
              function toggleItem(itemId) {
                setSelectedItemIds(function(prev) {
                  var next = new Set(prev);
                  if (next.has(itemId)) next.delete(itemId);
                  else next.add(itemId);
                  return next;
                });
              }
              function selectAll() {
                var eligible = poItems
                  .filter(function(it) { return String(it.status || '').trim() !== 'Cancelled'; })
                  .map(function(it) { return it.id; });
                setSelectedItemIds(new Set(eligible));
              }
              function selectNone() { setSelectedItemIds(new Set()); }
              var checkedCount = poItems.filter(function(it) { return selectedItemIds.has(it.id); }).length;
              return (
                <div data-testid="receipt-which-items"
                     style={{ marginBottom: 12, padding: '10px 12px', background: SURFACE, border: HAIRLINE, borderRadius: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
                    <div style={Object.assign({}, TYPE_EYEBROW)}>
                      Which items are on this receipt? ({checkedCount} of {poItems.length})
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button type="button" data-testid="receipt-which-items-select-all"
                        onClick={selectAll}
                        disabled={!canEdit}
                        style={{ padding: '2px 6px', fontSize: 9, border: HAIRLINE, background: SURFACE, borderRadius: 3, cursor: canEdit ? 'pointer' : 'not-allowed', fontFamily: 'inherit', color: INK_2 }}>
                        Select all
                      </button>
                      <button type="button" data-testid="receipt-which-items-select-none"
                        onClick={selectNone}
                        disabled={!canEdit}
                        style={{ padding: '2px 6px', fontSize: 9, border: HAIRLINE, background: SURFACE, borderRadius: 3, cursor: canEdit ? 'pointer' : 'not-allowed', fontFamily: 'inherit', color: INK_2 }}>
                        None
                      </button>
                    </div>
                  </div>
                  <div style={{ fontSize: 10, color: INK_3, marginBottom: 8, lineHeight: 1.4 }}>
                    Default: every item on PO {po.po_ref || ''} is checked. Uncheck any item that isn&rsquo;t on this receipt (e.g. items shipping in a separate batch later). The payment amount will split across the checked items.
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    {poItems.map(function(it) {
                      var checked = selectedItemIds.has(it.id);
                      var isCancelled = String(it.status || '').trim() === 'Cancelled';
                      var clientPrice = (parseFloat(it.netPrice) || 0) * (1 + (parseFloat(it.markupPct) || 0) / 100) * (parseFloat(it.qty) || 1);
                      return (
                        <label key={it.id}
                          data-testid={'receipt-which-items-item-' + it.id}
                          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderTop: HAIRLINE_ROW, cursor: (canEdit && !isCancelled) ? 'pointer' : 'not-allowed', opacity: isCancelled ? 0.4 : 1 }}>
                          <input type="checkbox"
                            checked={checked && !isCancelled}
                            disabled={!canEdit || isCancelled}
                            onChange={function() { if (!isCancelled) toggleItem(it.id); }}
                            style={{ cursor: canEdit && !isCancelled ? 'pointer' : 'not-allowed', flexShrink: 0 }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 11, color: INK_1, fontWeight: 500 }}>
                              {it.item || it.productName || 'Untitled item'}
                            </div>
                            <div style={{ fontSize: 10, color: INK_3, marginTop: 2, display: 'flex', gap: 8 }}>
                              {it.itemNumber && <span style={{ fontFamily: 'monospace' }}>{it.itemNumber}</span>}
                              {it.status && <span>· {it.status}</span>}
                              {isCancelled && <span style={{ color: INK_3, fontStyle: 'italic' }}>(cancelled — can&rsquo;t bill)</span>}
                            </div>
                          </div>
                          <div style={{ fontSize: 11, color: INK_1, fontFamily: 'inherit', textAlign: 'right', minWidth: 70, fontFeatureSettings: '"tnum", "lnum"' }}>
                            {fmtMoney(clientPrice)}
                          </div>
                          {/* Lead weeks — the receipt IS the order moment
                              (tracker rebuild item 7): capture per-item
                              lead time here so estimated deliveries exist
                              from day one. Writes the item directly
                              (audited); receipt date + weeks = the ETA
                              every tracker/calendar/schedule surface
                              projects from. Principal receipts only —
                              fee bills don't start clocks. */}
                          {isPrincipalKind && !isCancelled && (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: 88 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <input inputMode="numeric" placeholder="wks"
                                  data-testid={'receipt-lead-weeks-' + it.id}
                                  value={it.leadWeeks != null ? it.leadWeeks : ''}
                                  disabled={!canEdit || !p.updateItem}
                                  onChange={function(e) {
                                    var n = parseInt(String(e.target.value).replace(/[^\d]/g, ''), 10);
                                    if (p.updateItem) p.updateItem(it.id, { leadWeeks: isNaN(n) ? null : n });
                                  }}
                                  style={{ width: 46, padding: '3px 5px', fontSize: 10.5, border: HAIRLINE, borderRadius: 3, fontFamily: 'inherit', textAlign: 'right' }} />
                                <span style={{ fontSize: 9, color: INK_3 }}>wks</span>
                              </div>
                              {(function() {
                                var eta = receiptEtaFor(r.receiptDate, it.leadWeeks);
                                return eta
                                  ? <div style={{ fontSize: 9, color: INK_3, marginTop: 2 }}>{'(est. ' + eta + ')'}</div>
                                  : <div style={{ fontSize: 9, color: '#8A6D00', marginTop: 2 }}>no lead time</div>;
                              })()}
                            </div>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Phase 5A (2026-06-27) — Step 4 (Fee details). Renumbered
                from step 3 → step 4 once "Which items?" landed as the
                new step 3. Vendor receipts only; reimbursable + office
                have no fee-verification step. */}
            {wizardStep === 5 && r.poId && (r.kind === 'vendor_deposit' || r.kind === 'vendor_final' || r.kind === 'vendor_full_payment' || r.kind === 'vendor_fee') && (function() {
              var po = (p.purchaseOrders || []).find(function(x) { return x && x.id === r.poId; });
              if (!po) return null;
              // Aggregate items.fees across all items on the PO, per category.
              // poSubtotalLive's per-item installation rule (only items with
              // fees.installation > 0) is preserved by summing the field
              // directly off each item rather than re-deriving.
              var poItemIds = Array.isArray(po.items) ? po.items : (Array.isArray(po.item_ids) ? po.item_ids : []);
              var poItems = (p.items || []).filter(function(it) { return it && poItemIds.indexOf(it.id) >= 0; });
              function feeSum(category) {
                return poItems.reduce(function(s, it) {
                  var fees = it.fees || {};
                  if (category === 'custom') {
                    return s + (fees.custom || []).reduce(function(t, c) { return t + (parseFloat(c && c.amount) || 0); }, 0);
                  }
                  return s + (parseFloat(fees[category]) || 0);
                }, 0);
              }
              var categories = [
                { key: 'freight',      label: 'Freight' },
                { key: 'crating',      label: 'Crating' },
                { key: 'installation', label: 'Installation' },
                { key: 'tariff',       label: 'Tariff' },
                { key: 'custom',       label: 'Custom fees' },
              ];
              // Receipt actuals — bookkeeper entry via per-category
              // CurrencyInput. Persists to receipts.fee_breakdown jsonb
              // (migration 20260608_receipts_fee_breakdown.sql).
              // 2026-07-02 (Cimon's directive): each input PRE-FILLS via
              // prefillFeeBreakdown — the receipt's saved fee_breakdown
              // value when present, else the PO's estimate — so the
              // panel always opens showing the fees estimated for this
              // order. Pre-fix, inputs started at $0 unless OCR happened
              // to extract a fee (the Volk scenario 2026-06-08), and an
              // untouched panel meant "no fee data" — indistinguishable
              // from "fees match the estimate."
              var prefill = prefillFeeBreakdown(r.feeBreakdown, poItems);
              // Sum of all per-category actuals (prefilled) — used by
              // the math hint below so the bookkeeper can sanity-check
              // that the breakdown sums to a reasonable share of the
              // vendor principal (which is items + fees).
              var actualSum = categories.reduce(function(s, c) {
                return s + (prefill[c.key] || 0);
              }, 0);
              // Confirmation state for the confirm-fees block below the
              // table. Required when the PO carries any fee estimate (or
              // the receipt already has actuals); confirmed once every
              // estimated category has a saved fee_breakdown value.
              var confirmRequired = feeStepRequired(r, poItems);
              var confirmGate = feeConfirmationGate(r, poItems);
              return (
                <div style={{
                  marginBottom: 12, padding: '10px 10px', background: SURFACE,
                  border: HAIRLINE, borderRadius: 4,
                }}>
                  <div style={Object.assign({}, TYPE_EYEBROW, { marginBottom: 6 })}>
                    Fee verification ({po.po_ref})
                  </div>
                  {/* One-line framing (2026-08-23; tersened same day —
                      Cimon: "that's really really wordy"). The two rules:
                      fees are part of the total, and Expected (PO) is a
                      reference only. The per-row question + column names
                      carry the rest. */}
                  <div data-testid="receipt-fee-explainer"
                       style={{ fontSize: 11, color: INK_2, lineHeight: 1.5, marginBottom: 10 }}>
                    Fees below are part of this receipt's <strong style={{ color: INK_1 }}>{fmtMoney(parseFloat(r.totalAmount) || 0)}</strong> — never added on top. Expected (PO) is a reference only.
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                    <thead>
                      <tr style={Object.assign({}, TYPE_EYEBROW)}>
                        <th style={{ textAlign: 'left',  padding: '4px 4px', fontWeight: 500 }}>Fee</th>
                        <th style={{ textAlign: 'left',  padding: '4px 4px', fontWeight: 500, width: 108 }}>On this receipt?</th>
                        <th style={{ textAlign: 'right', padding: '4px 4px', fontWeight: 500, width: 100 }}>Amount on receipt</th>
                        <th style={{ textAlign: 'right', padding: '4px 4px', fontWeight: 500 }}>Expected (PO)</th>
                        <th style={{ textAlign: 'left',  padding: '4px 4px', fontWeight: 500, width: 200 }}>What happens</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categories.map(function(c) {
                        var est = Math.round(feeSum(c.key) * 100) / 100;
                        // Per-receipt semantics (2026-08-10): the input
                        // prefills ONLY what this receipt recorded.
                        // EMPTY = "this receipt says nothing about that
                        // fee" — no delta, no choice, nothing written.
                        var present = prefill[c.key] !== '' && prefill[c.key] != null;
                        var act = present ? (parseFloat(prefill[c.key]) || 0) : 0;
                        var delta = present ? Math.round((act - est) * 100) / 100 : 0;
                        var hasDelta = present && Math.abs(delta) >= 0.01;
                        var deltaColor = !present ? INK_3
                          : (est === 0 && act === 0) ? INK_3
                          : !hasDelta ? ZONE_GREEN
                          : (delta > 0 ? ZONE_FUCHSIA : INK_3);
                        var choice = feeChoices[c.key] || '';
                        var expandedHere = feeExpansion && feeExpansion.category === c.key;
                        // 2026-06-26 affordances. "Bill to client" is the
                        // implicit default — no user action required to
                        // bill a non-zero-delta fee through the cascade.
                        // Two explicit overrides per row:
                        //   "Show as adjustment" → inline expansion
                        //     captures description; sets choice='bill_as_adj'
                        //   "Firm absorbs" → inline expansion captures
                        //     reason; sets choice='dont_bill'
                        // The row never shows the old four-radio panel.
                        // Explicit answer state (2026-08-23): a saved value
                        // means Yes; an _onReceipt:false flag means the user
                        // answered No; neither means not answered yet. The
                        // question is asked in so many words — "is this fee
                        // on this receipt?" — because a bare empty input
                        // couldn't be told apart from "haven't looked yet".
                        var onReceiptFlags = (r.feeBreakdown && typeof r.feeBreakdown === 'object' && r.feeBreakdown._onReceipt) || {};
                        var answeredYes = present || onReceiptFlags[c.key] === true;
                        var answeredNo = !present && onReceiptFlags[c.key] === false;
                        var cellContent;
                        if (answeredNo) {
                          cellContent = (
                            <span style={{ color: INK_3 }}>Not on this receipt — nothing recorded.</span>
                          );
                        } else if (!present && !answeredYes) {
                          cellContent = (
                            <span style={{ color: est > 0 ? '#8A6D00' : INK_3 }}>
                              {est > 0 ? 'Answer Yes or No' : '—'}
                            </span>
                          );
                        } else if (!present) {
                          cellContent = (
                            <span style={{ color: '#8A6D00' }}>Enter the printed amount</span>
                          );
                        } else if (!hasDelta) {
                          cellContent = (
                            <span style={{ color: INK_3 }}>
                              {est === 0 && act === 0 ? '—' : 'Matches expected — bills as planned'}
                            </span>
                          );
                        } else if (choice === 'bill_as_adj') {
                          var desc = feeAdjustmentDescriptions[c.key] || '';
                          cellContent = (
                            <div data-testid={'receipt-fee-choice-' + c.key} style={{ color: INK_1, lineHeight: 1.4 }}>
                              <div style={{ fontWeight: 500 }}>✏ Show as adjustment</div>
                              {desc && (
                                <div style={{ color: INK_2, fontStyle: 'italic', marginTop: 2 }}>"{desc}"</div>
                              )}
                              <button data-testid={'receipt-fee-change-' + c.key}
                                onClick={function() { setFeeExpansion({ category: c.key, kind: 'adjustment', draft: desc }); }}
                                disabled={!canEdit}
                                style={{ marginTop: 4, padding: 0, border: 'none', background: 'none',
                                         color: INK_1, fontSize: 10, cursor: canEdit ? 'pointer' : 'not-allowed',
                                         textDecoration: 'underline' }}>
                                Change
                              </button>
                            </div>
                          );
                        } else if (choice === 'dont_bill') {
                          var reas = feeAbsorptionReasons[c.key] || '';
                          cellContent = (
                            <div data-testid={'receipt-fee-choice-' + c.key} style={{ color: INK_1, lineHeight: 1.4 }}>
                              <div style={{ fontWeight: 500 }}>✕ Firm absorbs</div>
                              {reas && (
                                <div style={{ color: INK_2, fontStyle: 'italic', marginTop: 2 }}>"{reas}"</div>
                              )}
                              <button data-testid={'receipt-fee-change-' + c.key}
                                onClick={function() { setFeeExpansion({ category: c.key, kind: 'absorb', draft: reas }); }}
                                disabled={!canEdit}
                                style={{ marginTop: 4, padding: 0, border: 'none', background: 'none',
                                         color: INK_1, fontSize: 10, cursor: canEdit ? 'pointer' : 'not-allowed',
                                         textDecoration: 'underline' }}>
                                Change
                              </button>
                            </div>
                          );
                        } else {
                          // Default: implicit "Bill to client" + two exception affordances.
                          cellContent = (
                            <div data-testid={'receipt-fee-choice-' + c.key} style={{ lineHeight: 1.4 }}>
                              <div style={{ color: ZONE_GREEN, fontWeight: 500 }}>✓ Bill to client</div>
                              <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                                <button data-testid={'receipt-fee-show-as-adjustment-' + c.key}
                                  onClick={function() { setFeeExpansion({ category: c.key, kind: 'adjustment', draft: '' }); }}
                                  disabled={!canEdit}
                                  style={{ padding: 0, border: 'none', background: 'none',
                                           color: INK_1, fontSize: 10, cursor: canEdit ? 'pointer' : 'not-allowed',
                                           textDecoration: 'underline' }}>
                                  Show as adjustment
                                </button>
                                <button data-testid={'receipt-fee-firm-absorbs-' + c.key}
                                  onClick={function() { setFeeExpansion({ category: c.key, kind: 'absorb', draft: '' }); }}
                                  disabled={!canEdit}
                                  style={{ padding: 0, border: 'none', background: 'none',
                                           color: INK_1, fontSize: 10, cursor: canEdit ? 'pointer' : 'not-allowed',
                                           textDecoration: 'underline' }}>
                                  Firm absorbs
                                </button>
                              </div>
                            </div>
                          );
                        }
                        var yesActive = answeredYes;
                        var noActive = answeredNo;
                        var answerBtn = function(label, active, onClick, testid) {
                          return (
                            <button data-testid={testid}
                              disabled={!canEdit}
                              onClick={onClick}
                              style={{
                                padding: '2px 9px', fontSize: 10, borderRadius: 3,
                                fontFamily: 'inherit',
                                border: active ? '1.5px solid #111111' : HAIRLINE,
                                background: 'none',
                                color: active ? INK_1 : INK_3,
                                fontWeight: active ? 600 : 400,
                                cursor: canEdit ? 'pointer' : 'not-allowed',
                              }}>
                              {label}
                            </button>
                          );
                        };
                        var rowFrag = (
                          <tr key={c.key}>
                            <td style={{ padding: '4px 4px', color: INK_1 }}>{c.label}</td>
                            <td style={{ padding: '4px 4px' }}>
                              <div style={{ display: 'flex', gap: 4 }}>
                                {answerBtn('Yes', yesActive,
                                  function() { setFeeOnReceipt(c.key, true, est); },
                                  'receipt-fee-on-receipt-yes-' + c.key)}
                                {answerBtn('No', noActive,
                                  function() { setFeeOnReceipt(c.key, false, 0); },
                                  'receipt-fee-on-receipt-no-' + c.key)}
                              </div>
                            </td>
                            <td style={{ padding: '4px 4px', textAlign: 'right' }}>
                              {answeredYes ? (
                                <div>
                                  <CurrencyInput
                                    data-testid={'receipt-fee-actual-' + c.key}
                                    value={present ? act : ''}
                                    disabled={!canEdit}
                                    onValueChange={function(n) { setFeeBreakdownCategory(c.key, n); }}
                                    style={Object.assign({}, IS, { textAlign: 'right', padding: '2px 6px', height: 24, fontSize: 11 })} />
                                  {present && est > 0 && act === est && (
                                    <div style={{ fontSize: 9, color: INK_3, marginTop: 2 }}>
                                      suggested — match the receipt
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span style={{ color: INK_3 }}>—</span>
                              )}
                            </td>
                            <td style={{ padding: '4px 4px', textAlign: 'right', color: INK_3 }}>
                              <div>{est > 0 ? fmtMoney(est) : '—'}</div>
                              {present && hasDelta && (
                                <div style={{ fontSize: 9, color: deltaColor, marginTop: 2 }}>
                                  {(delta > 0 ? '+' : '') + fmtMoney(delta) + ' vs expected'}
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '4px 4px', fontSize: 10 }}>{cellContent}</td>
                          </tr>
                        );
                        // Inline expansion row, full table width. Rendered
                        // immediately below the parent row when the user
                        // clicks "Show as adjustment" or "Firm absorbs"
                        // (or Change on a committed override).
                        var expansionFrag = expandedHere ? (
                          <tr key={c.key + '-exp'} data-testid={'receipt-fee-expansion-' + c.key}>
                            <td colSpan={5} style={{ padding: '12px 8px', background: GROUND_OFF, borderTop: HAIRLINE }}>
                              {feeExpansion.kind === 'adjustment' ? (
                                <div>
                                  <div style={{ fontSize: 11, fontWeight: 600, color: INK_1, marginBottom: 4 }}>
                                    Show as adjustment on the FIN
                                  </div>
                                  <div style={{ fontSize: 10, color: INK_2, marginBottom: 8, lineHeight: 1.4 }}>
                                    This will appear as a separate line item on the client’s final invoice, rather than being rolled into the fee subtotal. Use this when you want the client to see the specific charge.
                                  </div>
                                  <Lbl>Describe change in amount owed to client:</Lbl>
                                  <input data-testid="receipt-fee-adjustment-input"
                                    type="text"
                                    value={feeExpansion.draft}
                                    onChange={function(e) { setFeeExpansion(Object.assign({}, feeExpansion, { draft: e.target.value })); }}
                                    placeholder="e.g., Tariff actual was $385; estimate was $200..."
                                    style={Object.assign({}, IS, { fontSize: 11 })} />
                                </div>
                              ) : (
                                <div>
                                  <div style={{ fontSize: 11, fontWeight: 600, color: INK_1, marginBottom: 4 }}>
                                    Firm absorbs this fee
                                  </div>
                                  <div style={{ fontSize: 10, color: INK_2, marginBottom: 8, lineHeight: 1.4 }}>
                                    The client will not see this charge on their final invoice. The firm will pay this fee without recovering it.
                                  </div>
                                  <Lbl>Why is the firm absorbing this fee?</Lbl>
                                  <input data-testid="receipt-fee-absorption-input"
                                    type="text"
                                    value={feeExpansion.draft}
                                    onChange={function(e) { setFeeExpansion(Object.assign({}, feeExpansion, { draft: e.target.value })); }}
                                    placeholder="e.g., Vendor absorbed shipping per pre-order agreement"
                                    style={Object.assign({}, IS, { fontSize: 11 })} />
                                  <div style={{ fontSize: 10, color: INK_3, marginTop: 4, fontStyle: 'italic' }}>
                                    This reason will be saved with the receipt for future reference.
                                  </div>
                                </div>
                              )}
                              <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                                <button data-testid="receipt-fee-expansion-cancel"
                                  onClick={function() { setFeeExpansion(null); }}
                                  style={Object.assign({}, S.tbtnGhost, { fontSize: 11 })}>
                                  Cancel
                                </button>
                                <button data-testid="receipt-fee-expansion-save"
                                  disabled={!feeExpansion.draft.trim()}
                                  onClick={function() {
                                    var cat = feeExpansion.category;
                                    var text = feeExpansion.draft.trim();
                                    if (feeExpansion.kind === 'adjustment') {
                                      setFeeChoice(cat, 'bill_as_adj');
                                      setFeeAdjustmentDescriptions(function(prev) {
                                        var n = Object.assign({}, prev); n[cat] = text; return n;
                                      });
                                      // Clear any stale absorption reason for the
                                      // same category (re-decision flipping from
                                      // absorb → adjustment).
                                      setFeeAbsorptionReasons(function(prev) {
                                        var n = Object.assign({}, prev); delete n[cat]; return n;
                                      });
                                    } else {
                                      setFeeChoice(cat, 'dont_bill');
                                      setFeeAbsorptionReasons(function(prev) {
                                        var n = Object.assign({}, prev); n[cat] = text; return n;
                                      });
                                      setFeeAdjustmentDescriptions(function(prev) {
                                        var n = Object.assign({}, prev); delete n[cat]; return n;
                                      });
                                    }
                                    setFeeExpansion(null);
                                  }}
                                  style={Object.assign({}, S.tbtnSuccess, {
                                    fontSize: 11,
                                    opacity: feeExpansion.draft.trim() ? 1 : 0.45,
                                    cursor: feeExpansion.draft.trim() ? 'pointer' : 'not-allowed',
                                  })}>
                                  {feeExpansion.kind === 'adjustment' ? 'Save adjustment' : 'Confirm absorption'}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ) : null;
                        return [rowFrag, expansionFrag];
                      })}
                      <tr style={{ borderTop: HAIRLINE }}>
                        <td style={{ padding: '6px 4px 2px 4px', fontWeight: 500, color: INK_1 }}>Fees inside this receipt's total</td>
                        <td style={{ padding: '6px 4px 2px 4px' }}></td>
                        <td style={{ padding: '6px 4px 2px 4px', textAlign: 'right', color: INK_1, fontWeight: 500 }}>
                          {fmtMoney(actualSum)}
                        </td>
                        <td style={{ padding: '6px 4px 2px 4px', textAlign: 'right', color: INK_3 }}>
                          {fmtMoney(categories.reduce(function(s, c) { return s + feeSum(c.key); }, 0))}
                        </td>
                        <td style={{ padding: '6px 4px 2px 4px' }}></td>
                      </tr>
                    </tbody>
                  </table>
                  {/* Arithmetic sanity line (2026-08-23): spell out the
                      total = goods + fees relationship in one sentence so
                      "inside the total, not on top" is verifiable at a
                      glance. Amber when the fee sum exceeds the receipt
                      total — either a fee was typed that isn't on this
                      receipt, or the total is wrong. */}
                  {(function() {
                    var totalAmt = parseFloat(r.totalAmount) || 0;
                    if (actualSum <= 0 || totalAmt <= 0) return null;
                    var goods = Math.round((totalAmt - actualSum) * 100) / 100;
                    if (goods < -0.005) {
                      return (
                        <div data-testid="receipt-fee-over-total" style={{ fontSize: 11, color: ALERT_AMBER_TEXT, marginTop: 6, lineHeight: 1.4 }}>
                          Fees ({fmtMoney(actualSum)}) exceed the receipt total ({fmtMoney(totalAmt)}) — check the amounts.
                        </div>
                      );
                    }
                    return (
                      <div data-testid="receipt-fee-goods-remainder" style={{ fontSize: 11, color: INK_3, marginTop: 6 }}>
                        {fmtMoney(totalAmt)} − {fmtMoney(actualSum)} fees = {fmtMoney(goods)} goods.
                      </div>
                    );
                  })()}
                  {/* The four-choice radio panel + dev-facing help text
                      were removed 2026-06-26 in favor of the per-row
                      affordances above. "Bill to client" is now the
                      implicit default for any non-zero-delta fee; the
                      two explicit overrides (Show as adjustment / Firm
                      absorbs) are self-explanatory and don't need legend
                      copy below the table. */}
                  {/* Confirm fees (2026-07-02, Cimon's directive). The
                      primary action on this step: persists the prefilled
                      breakdown (ALL five categories — confirmed-at-
                      estimate values included) to receipts.fee_breakdown,
                      which is the confirmation marker the Approve gate
                      reads. Confirmed-at-estimate → delta 0 → no
                      items.fees change downstream; edited values flow
                      through the existing delta machinery unchanged.
                      Hairline separator + colored text, no tinted fills
                      (Bauhaus discipline). */}
                  {confirmRequired && (
                    <div data-testid="receipt-fee-confirm-block"
                         style={{ marginTop: 10, paddingTop: 10, borderTop: HAIRLINE }}>
                      {canEdit ? (
                        <>
                          <div style={{ fontSize: 11, color: INK_2, lineHeight: 1.4, marginBottom: 8 }}>
                            {copy.feeConfirm.prompt}
                          </div>
                          <button data-testid="receipt-fee-confirm-button"
                            type="button"
                            disabled={!canEdit}
                            onClick={function() {
                              // Functional onPatch: reads the CURRENT
                              // receipt (post-any-blur-patch) so a value
                              // typed in the same click event isn't
                              // overwritten by a stale-closure prefill.
                              // _onReceipt (the explicit Yes/No answers,
                              // 2026-08-23) is preserved — prefill rebuilds
                              // only the five category keys.
                              p.onPatch(function(cur) {
                                var next = prefillFeeBreakdown(cur.feeBreakdown, poItems);
                                var flags = cur.feeBreakdown && typeof cur.feeBreakdown === 'object' && cur.feeBreakdown._onReceipt;
                                if (flags) next._onReceipt = flags;
                                return { feeBreakdown: next };
                              });
                            }}
                            style={Object.assign({}, S.tbtnPrimary, {
                              opacity: canEdit ? 1 : 0.45,
                              cursor: canEdit ? 'pointer' : 'not-allowed',
                            })}>
                            {copy.feeConfirm.button}
                          </button>
                        </>
                      ) : (
                        <div data-testid="receipt-fee-confirmed-note"
                             style={{ fontSize: 11, color: ZONE_GREEN, fontWeight: 500 }}>
                          ✓ {copy.feeConfirm.confirmedNote}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Phase 3 (2026-06-27) — Reimbursable Project + Category +
                Notes belong to Step 2 (Details). Audit footer + lock
                banner stay unconditional. */}
            {wizardStep === 3 && r.kind === 'project_reimbursable' && (
              <div style={{ marginBottom: 12 }}>
                <Lbl required>Project</Lbl>
                <select value={r.projectLabel || ''} disabled={!canEdit}
                  onChange={function(e) { setProject(e.target.value); }}
                  style={Object.assign({}, IS, { background: SURFACE })}>
                  <option value="">— Pick a project —</option>
                  {(p.projects || []).map(function(pr) {
                    return <option key={pr.id} value={pr.label}>{pr.label}</option>;
                  })}
                </select>
              </div>
            )}

            {wizardStep === 3 && r.kind === 'project_reimbursable' && (
              <div style={{ marginBottom: 12 }}>
                <Lbl required>Category</Lbl>
                <select
                  value={customMode ? '__custom__' : (r.category || '')}
                  disabled={!canEdit}
                  onChange={function(e) {
                    var v = e.target.value;
                    if (v === '__custom__') {
                      setCustomMode(true);
                      if (r.category && CATEGORY_SUGGESTIONS.indexOf(r.category) !== -1) {
                        setCategory('');
                      }
                    } else {
                      setCustomMode(false);
                      setCategory(v);
                    }
                  }}
                  style={Object.assign({}, IS, { background: SURFACE })}>
                  <option value="">— Pick a category —</option>
                  {CATEGORY_SUGGESTIONS.map(function(c) {
                    return <option key={c} value={c}>{c}</option>;
                  })}
                  <option value="__custom__">Custom / not listed…</option>
                </select>
                {customMode && (
                  <input
                    value={r.category || ''}
                    disabled={!canEdit}
                    onChange={function(e) { setCategory(e.target.value); }}
                    placeholder="Type a short category (e.g. Storage, Conference)"
                    autoFocus
                    style={Object.assign({}, IS, { marginTop: 6 })} />
                )}
                <div style={{ fontSize: 10, color: INK_3, marginTop: 4 }}>
                  One- or two-word label that appears on the client invoice.
                </div>
              </div>
            )}

            {wizardStep === 3 && (
              <div style={{ marginBottom: 12 }}>
                <Lbl>Notes</Lbl>
                <textarea value={r.notes || ''} disabled={!canEdit}
                  onChange={function(e) { setNotes(e.target.value); }} rows={3}
                  style={Object.assign({}, IS, { resize: 'vertical', minHeight: 60 })} />
              </div>
            )}

            {/* Phase 3 (2026-06-27) — Confirm + approve summary (last
                step). Plain-prose summary of what the user is about to
                approve. The actual Approve button still lives in the
                toolbar above; this section just surfaces the bookkeeper's
                "you're about to record X" sanity check.
                2026-07-02 (Cimon's directive) — role-aware heading +
                trailing note. Staff can't approve, so the panel reads
                "All set" / "ready for review" instead of instructing an
                action they have no button for; the Approve-toolbar
                pointer only renders for approvers. The record summary
                itself (Type/Vendor/PO/etc.) is identical for both roles
                — it's a useful "here's what I just entered" recap either
                way. */}
            {wizardStep === wizardStepsForKind(r.kind).length && r.kind === 'vendor_final' && canEdit && (
              <div data-testid="receipt-shipped-question"
                   style={{ marginBottom: 12, padding: '12px 14px', background: SURFACE, border: HAIRLINE, borderRadius: 4 }}>
                <div style={Object.assign({}, TYPE_EYEBROW, { marginBottom: 6 })}>Has this order shipped?</div>
                <div style={{ fontSize: 10, color: INK_3, marginBottom: 8, lineHeight: 1.4 }}>
                  Final payments usually mean goods are moving. Answer if you know — items advance to Shipped with the date; skip if unsure (the Tracker&rsquo;s ship queue will ask again).
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                  {['yes', 'no'].map(function(ans) {
                    return (
                      <label key={ans} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: INK_1, cursor: 'pointer' }}>
                        <input type="radio" name="receipt-shipped" value={ans}
                          checked={shippedAnswer === ans}
                          onChange={function() { setShippedAnswer(ans); }}
                          data-testid={'receipt-shipped-' + ans}
                          style={{ cursor: 'pointer' }} />
                        {ans === 'yes' ? 'Yes — shipped' : 'Not yet'}
                      </label>
                    );
                  })}
                  {shippedAnswer === 'yes' && (
                    <input type="date" value={shipDateAns} data-testid="receipt-shipped-date"
                      onChange={function(e) { setShipDateAns(e.target.value); }}
                      style={{ padding: '3px 6px', fontSize: 10.5, border: HAIRLINE, borderRadius: 3, fontFamily: 'inherit' }} />
                  )}
                </div>
              </div>
            )}
            {wizardStep === wizardStepsForKind(r.kind).length && wizardStep > 1 && (
              <div data-testid="receipt-wizard-confirm-summary"
                   style={{ marginBottom: 12, padding: '12px 14px', background: SURFACE, border: HAIRLINE_FUCHSIA, borderRadius: 4 }}>
                <div style={Object.assign({}, TYPE_EYEBROW, { color: ZONE_FUCHSIA, marginBottom: 8 })}>
                  {p.canApprove ? 'Confirm + approve' : copy.wizardComplete.nonApproverHeading}
                </div>
                <div style={{ fontSize: 11, color: INK_1, lineHeight: 1.5 }}>
                  <div><strong style={{ fontWeight: 500 }}>Type:</strong>{' '}{(KIND_CARDS.find(function(c) { return c.value === r.kind; }) || {}).title || r.kind}</div>
                  {r.vendorName && <div><strong style={{ fontWeight: 500 }}>Vendor:</strong>{' '}{r.vendorName}</div>}
                  {r.poId && (function() {
                    var po = (p.purchaseOrders || []).find(function(x) { return x && x.id === r.poId; });
                    return po ? <div><strong style={{ fontWeight: 500 }}>PO:</strong>{' '}{po.po_ref}</div> : null;
                  })()}
                  {r.projectLabel && <div><strong style={{ fontWeight: 500 }}>Project:</strong>{' '}{r.projectLabel}</div>}
                  {r.category && <div><strong style={{ fontWeight: 500 }}>Category:</strong>{' '}{r.category}</div>}
                  {r.receiptDate && <div><strong style={{ fontWeight: 500 }}>Receipt date:</strong>{' '}{fmtDate(r.receiptDate)}</div>}
                  {r.paidDate && <div><strong style={{ fontWeight: 500 }}>Date paid:</strong>{' '}{fmtDate(r.paidDate)}</div>}
                  <div><strong style={{ fontWeight: 500 }}>Amount:</strong>{' '}{fmtMoney(parseFloat(r.totalAmount) || 0)}{(parseFloat(r.ccFee) || 0) > 0 && (
                    <span style={{ color: INK_3 }}>{' '}(+ {fmtMoney(parseFloat(r.ccFee) || 0)} CC fee = {fmtMoney((parseFloat(r.totalAmount) || 0) + (parseFloat(r.ccFee) || 0))} receipt total)</span>
                  )}</div>
                  {p.canApprove && r.kind === 'project_reimbursable' && (function() {
                    var cost = parseFloat(r.totalAmount) || 0;
                    var pct = receiptMarkupPct(r);
                    var billed = applyReimbursableMarkup(cost, pct);
                    return <div style={{ marginTop: 4 }}><strong style={{ fontWeight: 500 }}>Bill to client:</strong>{' '}{fmtMoney(billed)}{' '}<span style={{ color: INK_3, fontSize: 10 }}>(cost + {pct}%)</span></div>;
                  })()}
                  {r.kind === 'personal' && (
                    <div style={{ marginTop: 4, fontStyle: 'italic', color: INK_2 }}>Firm overhead — not billed to any client.</div>
                  )}
                  {r.kind === 'merchandise_direct' && (function() {
                    var linked = r.itemId
                      ? (p.items || []).find(function(it) { return it && it.id === r.itemId; })
                      : null;
                    return (
                      <>
                        {linked && <div><strong style={{ fontWeight: 500 }}>Item:</strong>{' '}{linked.item || linked.productName || r.itemId}{linked.itemNumber ? ' (' + linked.itemNumber + ')' : ''}</div>}
                        <div style={{ marginTop: 4, fontStyle: 'italic', color: INK_2 }}>
                          Firm record only — the client pays via the item's final invoice.
                        </div>
                      </>
                    );
                  })()}
                </div>
                <div data-testid="receipt-wizard-confirm-footnote" style={{ marginTop: 10, fontSize: 10, color: INK_3 }}>
                  {p.canApprove
                    ? 'Click Approve in the toolbar above to record this receipt.'
                    : copy.wizardComplete.nonApproverNote}
                </div>
              </div>
            )}

            <div style={{ marginTop: 16, padding: '10px 12px', background: SURFACE, border: HAIRLINE_ROW, borderRadius: 4, fontSize: 10, color: INK_3 }}>
              <div>Submitted by: {r.createdBy || '—'}</div>
              {r.approvedAt && <div>Approved by {r.approvedBy} on {fmtDate(r.approvedAt)}</div>}
              {r.rejectedAt && <div>Rejected on {fmtDate(r.rejectedAt)}{r.rejectedReason ? ' — ' + r.rejectedReason : ''}</div>}
            </div>

            {/* Status banner (2026-06-28):
                - Staff on an approved receipt: friendly note telling
                  them to flag changes to Cimon. No edit affordance —
                  the Edit button in the toolbar above is owner/admin
                  only.
                - Owner/admin in edit mode: amber warning that changes
                  update the recorded payment. (The Edit/Done toggle
                  lives in the toolbar above, alongside Refresh.)
                - Owner/admin in view mode: no banner — the toolbar's
                  Edit button is enough of a cue.

                Goal: navigation through wizard panels is read-only by
                default; flipping to edit takes a deliberate click. */}
            {isLocked && !canEditByRole && (
              <div style={{ marginTop: 12, padding: '10px 12px', background: SURFACE, border: HAIRLINE, borderRadius: 4, fontSize: 11, color: INK_2 }}>
                ✓ This receipt is approved. If something needs changing, flag it to Cimon.
              </div>
            )}
            {isLocked && canEditByRole && editMode && (
              <div style={{ marginTop: 12, padding: '10px 12px', background: SURFACE, border: HAIRLINE_AMBER, borderRadius: 4, fontSize: 11, color: ALERT_AMBER_TEXT }}>
                Editing approved receipt — changes update the recorded payment.
              </div>
            )}
            </>)}

            </div>
            {/* Phase 3 (2026-06-27, restructured 2026-06-28) — wizard
                footer with Back/Continue. Lives OUTSIDE the scrollable
                body so it's always visible at the bottom of the right
                pane (sibling to the scroll body, flexShrink:0). Pre-fix
                this was inside the scroll body with marginTop:auto,
                which caused content to be trapped above the visible
                area on long forms.
                2026-06-28: canEdit gate dropped — navigation buttons
                always render so view-mode viewers can click through
                panels read-only. The buttons change wizardStep state,
                which is navigation only, never field editing. */}
            {step === 'edit' && (function() {
              var labels = wizardStepsForKind(r.kind);
              var isLast = wizardStep >= labels.length;
              return (
                <div data-testid="receipt-wizard-footer"
                     style={{ flexShrink: 0, padding: '12px 16px', borderTop: HAIRLINE_ROW, display: 'flex', gap: 8, justifyContent: 'space-between', background: SURFACE }}>
                  <button data-testid="receipt-wizard-back"
                    type="button"
                    onClick={function() { if (wizardStep > 1) setWizardStep(wizardStep - 1); }}
                    disabled={wizardStep <= 1}
                    style={Object.assign({}, S.tbtnGhost, { opacity: wizardStep <= 1 ? 0.3 : 1, cursor: wizardStep <= 1 ? 'not-allowed' : 'pointer' })}>
                    ← Back
                  </button>
                  {!isLast && (
                    <button data-testid="receipt-wizard-continue"
                      type="button"
                      onClick={function() { setWizardStep(wizardStep + 1); }}
                      style={S.tbtnPrimary}>
                      Continue →
                    </button>
                  )}
                  {/* 2026-07-02 (Cimon's directive) — role-aware footer
                      hint. Non-approvers get the Save & close pointer
                      instead of being told to use a toolbar Approve
                      button they don't have. */}
                  {isLast && (
                    <span data-testid="receipt-wizard-footer-hint" style={{ fontSize: 10, color: INK_3, alignSelf: 'center' }}>
                      {p.canApprove
                        ? 'Use the Approve button in the toolbar above.'
                        : copy.wizardComplete.nonApproverFooterHint}
                    </span>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// ReceiptReviewView — Step 2 read-only summary (2026-06-26).
//
// Replaces the form content area on the right pane when the user has
// clicked "Continue →" from Step 1. Reads from the same in-memory state
// the form holds; the Confirm and approve button (in the toolbar) is
// what actually fires the cascade with the held choices.
//
// Per-fee summary line shape (matches the spec wireframe):
//   Bill-as-fee (default):    "$995 freight                  (client)"
//   Bill-as-adjustment:       "$45 tariff — "<desc>"          (client)"
//   Firm absorbs:             "$200 crating — Not Billed: "<reason>" (studio)"
//
// The "(client)" / "(studio)" tag is reviewer context only — it never
// flows to the FIN PDF. The FIN renderer reads from
// purchase_orders.pendingFinAdjustments where the description field
// carries the user-authored adjustment text (no tag), and from
// items.fees for the implicit bill case (no tag).
//
// Personalized greeting via displayNameFor — falls back to a generic
// "Is this correct?" question when no name resolves.
function ReceiptReviewView(p) {
  var r = p.r;
  var feeReview = p.feeReview;
  var feeChoices = p.feeChoices || {};
  var descs = p.feeAdjustmentDescriptions || {};
  var reasons = p.feeAbsorptionReasons || {};

  var firstName = displayNameFor(p.userEmail, p.firmSettings);
  var greeting = firstName
    ? 'Hi ' + firstName + ' \u2014 is this correct?'
    : 'Is this correct?';

  function feeLine(row) {
    var choice = feeChoices[row.category] || '';
    var amt = fmtMoney(row.actual);
    if (choice === 'dont_bill') {
      var reason = reasons[row.category] || '';
      return (
        <div data-testid={'receipt-review-line-' + row.category}
             data-mode="absorb"
             style={{
               display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
               padding: '6px 0 6px 10px', borderLeft: '2px solid ' + INK_3,
               color: INK_3, fontSize: 11, marginBottom: 4,
             }}>
          <div style={{ flex: 1 }}>
            <span style={{ fontWeight: 500 }}>{amt} {row.label}</span>
            {' \u2014 Not Billed'}{reason ? (': \u201c' + reason + '\u201d') : ''}
          </div>
          <span style={{ color: INK_4, marginLeft: 8, fontStyle: 'italic' }}>(studio)</span>
        </div>
      );
    }
    if (choice === 'bill_as_adj') {
      var desc = descs[row.category] || '';
      return (
        <div data-testid={'receipt-review-line-' + row.category}
             data-mode="adjustment"
             style={{
               display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
               padding: '6px 0', color: INK_1, fontSize: 11, marginBottom: 4,
             }}>
          <div style={{ flex: 1 }}>
            <span style={{ fontWeight: 500 }}>{amt} {row.label}</span>
            {desc ? (' \u2014 \u201c' + desc + '\u201d') : ''}
          </div>
          <span style={{ color: INK_3, marginLeft: 8, fontStyle: 'italic' }}>(client)</span>
        </div>
      );
    }
    // Default — implicit bill as fee.
    return (
      <div data-testid={'receipt-review-line-' + row.category}
           data-mode="bill"
           style={{
             display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
             padding: '6px 0', color: INK_1, fontSize: 11, marginBottom: 4,
           }}>
        <span style={{ fontWeight: 500 }}>{amt} {row.label}</span>
        <span style={{ color: INK_3, marginLeft: 8, fontStyle: 'italic' }}>(client)</span>
      </div>
    );
  }

  return (
    <div data-testid="receipt-review-view">
      <div style={Object.assign({}, TYPE_EYEBROW, { marginBottom: 10 })}>
        Review what will be saved
      </div>
      <div style={{ marginBottom: 12, padding: '10px 12px', background: SURFACE,
                    border: HAIRLINE, borderRadius: 4, fontSize: 11, color: INK_1 }}>
        <div style={{ marginBottom: 2 }}>
          <strong>{r.vendorName || 'Untitled vendor'}</strong>
          {feeReview.po ? ' \u00b7 ' + feeReview.po.po_ref : ''}
        </div>
        <div style={{ color: INK_2 }}>
          {fmtMoney(r.totalAmount)} {r.receiptDate ? '\u00b7 ' + r.receiptDate : ''}
        </div>
      </div>

      <div style={{ marginBottom: 16, padding: '4px 0', border: HAIRLINE,
                    borderRadius: 4, paddingLeft: 12, paddingRight: 12, paddingTop: 6, paddingBottom: 6 }}>
        {feeReview.rows.map(function(row) {
          return <div key={row.category}>{feeLine(row)}</div>;
        })}
      </div>

      <div style={{ marginTop: 16, padding: '12px 14px', background: SURFACE,
                    border: HAIRLINE, borderRadius: 4 }}>
        <div data-testid="receipt-review-greeting"
             style={{ fontSize: 13, color: INK_1, fontWeight: 500 }}>
          {greeting}
        </div>
      </div>
    </div>
  );
}
